/**
 * SLF™ Accounting — operator UI strings (en / zh / th).
 *
 * The statutory books module was written with its labels inline, mostly English with hand-built
 * bilingual pairs ("งบทดลอง · Trial balance"). Those are split here: the Thai half of such a pair is
 * the STATUTORY NAME of a book, a statement, a register or an RD form, and it stays in the TSX as a
 * literal because it is what the server prints on the document face. An accountant working in the
 * Chinese UI still has to see which Thai document they are about to produce. Only the gloss beside
 * it, and the chrome around it, live in this file.
 *
 * ⛔ Three rules this file is held to:
 *
 * 1. NO LAW IS INVENTED, in any language. Where a string states a legal rule, every number, form
 *    code, section reference and year inside it is reproduced exactly — ภ.ง.ด.50 stays ภ.ง.ด.50,
 *    พ.ศ. 2566 stays 2566 and does not become 2023, "five-year" stays five. The sentence around
 *    them is translated; the facts are not re-expressed.
 *
 * 2. AN EMPTY VALUE MEANS "fall back to English", NEVER "render nothing". src/i18n/index.ts sets
 *    returnEmptyString: false for exactly this reason (and the comment there records that there
 *    were zero existing empty values when it was flipped). So a gloss we have no good Chinese or
 *    Thai word for degrades to the English — it never leaves a hole where a label should be.
 *
 * 3. SERVER STRINGS ARE NOT IN HERE. The warnings and cover issues the bound-statements endpoint
 *    returns, account names from the chart, a client's name, a bank volume's label: all data. They
 *    are what will actually be printed, and translating them on the screen would show the operator
 *    one wording and print another.
 *
 * Chinese is the vocabulary a Thai firm's Chinese-speaking accounting staff use (会计期间, 过账,
 * 日记账, 试算表, 会计科目表), not a literal gloss of the English. Thai is ordinary operating Thai,
 * not invented officialese.
 */

export const acctEn: Record<string, string> = {
  // ── Accounting period: opening, closing ──────────────────────────────────────────────────────
  pa_sb_period_label: 'Accounting period',
  pa_sb_open_books_gloss: 'Open the books',
  pa_sb_open_books_why: 'The Accounting Act requires the accounts to be closed every twelve months, so a period has to exist before anything can be posted. Leave the end date blank for the ordinary twelve months; a FIRST period may be shorter, and any later period that is not twelve months needs the Chief Accounts Inspector’s written permission recorded first. The firm’s chart of accounts is copied in on the way.',
  pa_sb_from: 'From',
  pa_sb_to: 'To',
  pa_sb_optional: 'optional',
  pa_sb_open_period: 'Open period',
  pa_sb_open_next: 'Open next period',
  pa_sb_status_closed: 'closed',
  // ⛔ _one / _other, NOT _plural: this project is on i18next 23 with no compatibilityJSON, so
  // JSON v4 pluralisation applies and a _plural suffix is silently ignored (checked against the
  // real library, not assumed). zh and th have one plural form, so they carry only _other.
  pa_sb_n_entries_one: '{{count}} entry',
  pa_sb_n_entries_other: '{{count}} entries',
  pa_sb_nothing_posted: 'Nothing has been posted to this period yet — generate the drafts above, review them, and post.',

  pa_sb_close_gloss: 'Close period…',
  pa_sb_close_confirm: 'Closing this period cannot be undone. The result is transferred to retained earnings, the balances carried forward are fixed, and the five-year retention clock starts on the period’s vouchers. Continue?',
  pa_sb_close_sum: 'Income {{income}} − expenses {{expense}} =',
  pa_sb_profit: 'profit',
  pa_sb_loss: 'loss',
  pa_sb_close_drafts_one: '{{count}} draft entry is still waiting — clear it first.',
  pa_sb_close_drafts_other: '{{count}} draft entries are still waiting — clear them first.',
  pa_sb_close_outofrange_one: '{{count}} posted entry falls outside the period.',
  pa_sb_close_outofrange_other: '{{count}} posted entries fall outside the period.',
  pa_sb_close_unbalanced: 'The period does not balance.',
  pa_sb_close_payroll: 'Payroll {{docNos}} is approved but not posted, and its period ends inside this one. Post it from Payroll › Payroll list before closing — nothing can be written into a closed period.',
  pa_sb_close_it: 'Close it',
  pa_sb_closed_detail: '{{entries}} entries · {{result}} {{amount}} · {{carried}} balances carried forward · {{vouchers}} vouchers stamped for five-year retention.',

  // ── Section headings. Each sits after a statutory Thai name, as its gloss. ───────────────────
  pa_sb_h_journals: 'Daily journals',
  pa_sb_h_ledger: 'Ledger & trial balance',
  pa_sb_h_statements: 'Financial statements',
  pa_sb_h_extras: 'Cash flow, notes, stock and VAT',

  // ── The cards. The Thai name is in the TSX; these are the lines under it. ────────────────────
  pa_sb_c_gl: 'General ledger',
  pa_sb_c_tb: 'Trial balance',
  pa_sb_c_ar: 'Debtors — subsidiary ledger',
  pa_sb_c_ap: 'Creditors — subsidiary ledger',
  pa_sb_c_stock: 'Stock account — goods clients only',
  pa_sb_c_goods: 'Goods and raw materials report',
  pa_sb_c_vatout: 'Sales tax report — this month',
  pa_sb_c_vatin: 'Purchase tax report — this month',

  // ── The headline figures above the statements ────────────────────────────────────────────────
  pa_sb_pl_line: 'Income {{income}} − expenses {{expense}}',
  pa_sb_pl_gross_line: 'Sales {{sales}} − cost of sales {{cost}}',
  pa_sb_pl_gross_unplaced: '({{n}} account(s) not placed — {{amount}} not counted)',
  pa_sb_assets_line: 'Assets {{amount}}',
  pa_sb_unbalanced_why: 'assets do not equal liabilities plus equity',

  // ── The bound set ────────────────────────────────────────────────────────────────────────────
  pa_fs_bound_what: 'Financial statements — bound set: cover, contents with page numbers, one run of pages',
  pa_fs_bound_build: 'Build the set',
  pa_fs_bound_building: 'Binding…',
  pa_fs_cover_issues: 'printed on the cover',
  pa_fs_pick_least: 'Pick at least one statement.',
  pa_fs_sfp: 'Statement of financial position',
  pa_fs_pl: 'Statement of profit or loss',
  pa_fs_sce: 'Statement of changes in equity',
  pa_fs_cf: 'Statement of cash flows',
  pa_fs_notes: 'Notes to the financial statements',
  // ⛔ ภ.ง.ด.50 is the form code. It is not translated, transliterated or expanded in any locale.
  pa_fs_wa: 'Working account — filed with ภ.ง.ด.50',

  // ── The footnote. States what these documents are and are NOT. ───────────────────────────────
  pa_sb_foot_books: 'Books print in Thai with a cover showing {{client}}, the kind of book and its volume, and consecutive page numbers — the form the DBD’s 2544 notification prescribes.',
  pa_sb_foot_statements: 'The statements are built from those books on the accrual basis. They are NOT yet arranged into the abbreviated line items (รายการย่อ) the DBD’s 2566 notification prescribes, so they are not the form filed with the DBD — that mapping is waiting on the notification’s official text.',

  // ── AccountingBooks — the client picker ───────────────────────────────────────
  pa_books_all_clients: 'All clients',
  pa_books_title: 'Statutory books',
  pa_books_pinned_missing_title: 'This login\'s client is not available',
  pa_books_pinned_missing_body: 'It is linked to one client, and that client is not in the list this login may read. Ask the firm to check the link in Admin → Users.',
  pa_books_intro: 'Chart of accounts, accounting periods, the five daily journals, the general ledger, closing the year and the financial statements. Every accounting client is listed — including those with no invoices yet, because a newly incorporated company is exactly the one whose books need opening.',
  pa_books_search_ph: 'Search clients…',
  pa_books_empty: 'No accounting clients match.',
  pa_books_drafts_pill: '{{n}} in draft',
  pa_books_no_period: 'No accounting period yet',
  pa_books_pill_closed: 'Closed',
  pa_books_pill_open: 'Open',
  pa_books_card_counts: '{{accounts}} accounts · {{entries}} posted entries',
  pa_books_card_vat: 'VAT',

  // ── Bookkeeping — the invoice-line-item books ─────────────────────────────────
  pa_bk_mon_jan: 'Jan',
  pa_bk_mon_feb: 'Feb',
  pa_bk_mon_mar: 'Mar',
  pa_bk_mon_apr: 'Apr',
  pa_bk_mon_may: 'May',
  pa_bk_mon_jun: 'Jun',
  pa_bk_mon_jul: 'Jul',
  pa_bk_mon_aug: 'Aug',
  pa_bk_mon_sep: 'Sep',
  pa_bk_mon_oct: 'Oct',
  pa_bk_mon_nov: 'Nov',
  pa_bk_mon_dec: 'Dec',
  pa_bk_individual: 'Individual',
  pa_bk_item_remove: 'Delete',
  pa_bk_zero_rate: '0%',
  pa_bk_loading: 'Loading…',
  pa_bk_books_link: 'Statutory books',
  pa_bk_books_link_title: 'Chart of accounts, daily journals, general ledger, closing and financial statements',
  pa_bk_err_items_load: 'Could not load the items',
  pa_bk_err_not_saved: 'Not saved',
  pa_bk_err_add: 'Could not add',
  pa_bk_err_delete: 'Could not delete',
  pa_bk_err_clear: 'Could not clear',
  pa_bk_err_regen: 'Regenerate failed',
  pa_bk_err_month_load: 'Could not load this month',
  pa_bk_err_photo: 'Could not load the photo',

  // ── EntityInfo — juristic form and dividends ──────────────────────────────────
  pa_entity_form_not_recorded: 'Juristic form not recorded',
  pa_entity_unknown_why: 'Whether this client must keep accounts under s.8, which DBD form its statements take, and whether an audit may be waived all depend on this.',
  pa_entity_must_keep: 'Must keep accounts (ม.8)',
  pa_entity_not_required: 'Not a person required to keep accounts',
  pa_entity_dbd_form: 'DBD แบบ {{n}}',
  pa_entity_reg_capital_val: 'Registered capital {{amount}}',
  pa_entity_registered_on_val: 'Registered {{date}}',
  pa_entity_dividend: 'Dividend',
  pa_entity_f_form: 'Juristic form',
  pa_entity_f_form_select: '— Select —',
  pa_entity_form_dbd_suffix: '(แบบ {{n}})',
  pa_entity_f_capital: 'Registered capital ($t(cur_unit))',
  pa_entity_f_registered_on: 'Registration date',
  pa_entity_div_law: 'ประมวลกฎหมายแพ่งและพาณิชย์ ม.1201–1202 — a dividend is paid only out of profits, never while an accumulated loss stands, and at least 5% of it goes to the legal reserve until that reserve reaches a tenth of registered capital. It must be paid within one month of the resolution.',
  pa_entity_div_amount: 'Amount ($t(cur_unit))',
  pa_entity_div_resolution_date: 'Resolution date',
  pa_entity_div_payable_account: 'Dividend payable account',
  pa_entity_div_retained: 'Retained earnings {{amount}}',
  pa_entity_div_reserve: 'Legal reserve {{amount}}',
  pa_entity_div_ceiling: 'ceiling {{amount}}',
  pa_entity_div_appropriation: 'Appropriation to legal reserve {{amount}}',
  pa_entity_div_create: 'Create draft',
  pa_entity_div_created: '{{doc}} created as a draft.',
  pa_entity_div_created_appr: '{{doc}} created as a draft, with {{amount}} appropriated to the legal reserve.',

  // ── DraftEntries — generate, review, post ─────────────────────────────────────
  pa_jnl_cash: 'เงินสด',
  pa_jnl_bank: 'ธนาคาร',
  pa_jnl_purchase: 'รายวันซื้อ',
  pa_jnl_sale: 'รายวันขาย',
  pa_jnl_general: 'รายวันทั่วไป',
  pa_draft_title: 'Draft entries',
  pa_draft_sub_pending: 'Nothing here is in the books until you post it.',
  pa_draft_sub_empty: 'Derived from the VAT sub-ledger — invoices and bills already recorded.',
  pa_draft_generate: 'Generate from sub-ledger',
  pa_draft_stale_btn: 'Rebuild {{n}} stale',
  pa_draft_stale_btn_title: 'Rebuild the drafts whose document has changed since — only the ones nobody has worked on; the rest are listed for you to handle.',
  pa_draft_stale_rebuilt: '{{n}} rebuilt from their documents',
  pa_draft_stale_kept: '{{n}} left for you (somebody has worked on them)',
  pa_draft_stale_withdrawn: '{{n}} cleared — their document had been withdrawn',
  pa_draft_stale_r_line: 'This draft was built from a document that has changed since. Discard it and generate again, so the books state what the document says now.',
  pa_draft_stale_r_items: 'This draft was built from a document whose items have changed since. Discard it and generate again, so the books state what the document says now.',
  pa_draft_stale_r_both: 'This draft was built from a document that has changed since — the document and its items both. Discard it and generate again.',
  pa_draft_stale_r_gone: 'The document this draft was built from has been withdrawn — re-booked, voided or deleted. Discard this draft: posting it would put a document in the books that no longer exists.',
  pa_draft_stale_r_vouchers: 'A hand voucher of this line\'s month that moves the same money was posted after this draft was generated. Discard it and generate again — you will be asked to confirm that voucher.',
  pa_draft_stale_r_prepaid: 'The customer\'s withholding on this draft\'s receipt is now booked differently by the receipt\'s other statement lines — by another entry, or by none. Discard this draft and generate again, so the withholding is booked exactly once.',
  pa_draft_stale_r_prepaid_wait: 'This draft leaves the customer\'s withholding on its receipt to another entry of the receipt that is still a draft (hover for which) — post that one first, then this one.',
  pa_draft_stale_r_vouchers_named: 'Hand vouchers of this line\'s month that move the same money ({{docNos}}) were posted after this draft was generated, and nobody has confirmed them. Discard it and generate again — you will be asked to confirm them.',   // batch 11 W4 P9 (W3 lows)
  pa_draft_stale_r_prepaid_twice: 'The customer\'s withholding on {{receipt}} is booked already by {{by}} — discard this draft and generate again, so the withholding is not booked twice.',
  pa_draft_stale_r_prepaid_nowhere: 'No entry books the customer\'s withholding on {{receipt}} — this draft does not, and no other entry of the receipt does. Discard it and generate again, so the withholding is booked once.',
  pa_draft_stale_r_prepaid_part: 'The customer\'s withholding on {{receipt}} is booked only in part — {{part}} by {{by}} — and this draft books none of it. Discard it and generate again: you will be told how the rest is booked.',
  pa_draft_stale_r_prepaid_unclear: 'The customer\'s withholding on {{receipt}} may be booked already by {{by}}, whose line is matched to an invoice with more than one receipt with withholding. Discard this draft, match that line to its receipt instead, and generate again.',
  pa_draft_stale_r_prepaid_leaving: 'This draft leaves the customer\'s withholding on {{receipt}} to {{by}}, whose reversal {{rv}} is raised already — once {{rv}} posts, nothing books it. Post {{rv}}, then discard this draft and generate again; or discard {{rv}}.',
  pa_draft_stale_r_prepaid_wait_named: 'This draft leaves the customer\'s withholding on {{receipt}} to {{by}}, which is still a draft — post that one first, then this one.',
  pa_draft_stale_not_rebuilt: '{{n}} could not be rebuilt and were kept as they were: {{list}}',
  pa_draft_stale_other: '{{n}} further draft(s) created for documents that had none',
  pa_draft_hide: 'Hide',
  pa_draft_review: 'Review',
  pa_draft_post_all: 'Post all {{n}}',
  pa_draft_post: 'Post',
  pa_draft_discard_title: 'Discard this draft',
  pa_draft_discard_confirm: 'Discard the draft for {{docNo}}? The sub-ledger row stays — you can generate it again.',
  pa_draft_discard_confirm_reversal: 'Discard the reversal {{docNo}}? {{reverses}} stays in the books until it is reversed again.',
  pa_draft_reversal_note: 'Reverses {{reverses}} line for line: its accounts and legs are {{reverses}}’s with the sides swapped, so they are not changed or split here.',
  pa_draft_reversal_hover: '(hover for how to book it differently)',
  // (Batch 11 W4 P9, LB-13) The reversal note's hover, by the route of its way out (GET /journal/drafts reverses.lockedCode);
  // the server's English sentence is the fallback. (LB-18) A manual entry made for a source by hand says which, on its card.
  pa_draft_reversal_way_own: 'Post {{rv}} as it stands and record the right figures in an entry of their own.',
  pa_draft_reversal_way_putBack: 'Posting {{rv}} puts {{orig}} back in the books as it was; to book it differently, discard {{rv}} and record the right figures in an entry of their own.',
  pa_draft_reversal_way_asset: 'Post {{rv}} as it stands — the asset register is put back when it posts. Run it again and post the new draft as it stands (its accounts follow the asset register — correct the asset there first to book it differently), never in an entry of your own.',
  pa_draft_reversal_way_held: 'The source of {{orig}} has already been generated again as {{held}}. Post {{rv}} as it stands, then book it on {{held}}: on a document\'s draft choose the account or split a line that is not the party, VAT or cash line; a settlement\'s draft is posted as it stands (its accounts follow the match and the posting accounts). Never in an entry of your own.',
  pa_draft_reversal_way_heldPosted: 'The source of {{orig}} is in the books again as {{held}}. Post {{rv}} as it stands; to book it differently, reverse {{held}}, post that reversal, generate the entry again and book it on the new draft (on a document\'s draft choose the account of a line that is not the party, VAT or cash line; a settlement follows its match and the posting accounts).',
  pa_draft_reversal_way_heldBlocked: 'The source of {{orig}} was generated again as {{held}}, which can no longer be posted — discard {{held}} in Journal first, then post {{rv}} as it stands and book the source as it now stands.',
  pa_draft_reversal_way_gone: 'Post {{rv}} as it stands: the source of {{orig}} no longer stands (voided, deleted, reversed or held by payroll), so nothing books it again.',
  pa_draft_reversal_way_now: 'Post {{rv}} as it stands — the source of {{orig}} can then be generated again. Generate it and book it on the new draft: on a document\'s draft choose the account or split a line that is not the party, VAT or cash line; a settlement\'s draft is posted as it stands (its accounts follow the match and the posting accounts). Never in an entry of your own.',
  pa_draft_reversal_way_fix: 'Post {{rv}} as it stands, put right what stops Generate taking the source of {{orig}}, generate it again and book it on the new draft (on a document\'s draft choose the account or split a line that is not the party, VAT or cash line; a settlement\'s draft is posted as it stands).',
  pa_draft_reversal_way_later: 'Post {{rv}} as it stands; the source of {{orig}} is dated after the open period — open each period up to the one that covers it, generate it there and book it on the new draft (on a document\'s draft choose the account of a line that is not the party, VAT or cash line; a settlement\'s draft is posted as it stands).',
  pa_draft_reversal_way_redate: 'Post {{rv}} as it stands, correct the date of the source of {{orig}} in the Tax module and generate it again — or, if the right date lies in a closed period, record it by hand as a manual entry made for that source.',
  pa_draft_reversal_way_byHand: 'Post {{rv}} as it stands and record the right figures by hand as a manual entry made for the source of {{orig}} (Record by hand in Manual entries › Reverse): that entry holds the source, so Generate does not book it again.',
  pa_draft_made_for: 'Made by hand for {{what}}.',
  // (Batch 12 tail T4, COA-D11) Draft entries edited by permission (Bookkeeping/DraftEditDialog.tsx).
  pa_draft_edit: 'Edit',
  pa_draft_edit_title: 'Edit draft entry',
  pa_edit_date: 'Date',
  pa_edit_book: 'Book',
  pa_edit_bank_acct: 'Bank account',
  pa_edit_docno: 'Document number',
  pa_edit_docno_jv: 'Journal-voucher number (kept)',
  pa_edit_narration: 'Description',
  pa_edit_calcnote: 'How the amount was arrived at',
  pa_edit_reverse_on: 'Reverse on',
  pa_edit_account: 'Account',
  pa_edit_dr: 'Debit',
  pa_edit_cr: 'Credit',
  pa_edit_partner: 'Party',
  pa_edit_ref: 'Reference',
  pa_edit_memo: 'Line description',
  pa_edit_add_line: 'Add line',
  pa_edit_remove_line: 'Remove this line',
  pa_edit_balanced: 'Debits equal credits',
  pa_edit_unbalanced: 'Out of balance by {{diff}}',
  pa_edit_save: 'Save changes',
  pa_edit_saved: '{{docNo}} saved — it is still a draft.',
  pa_edit_why: 'Why (optional, kept with this edit)',
  pa_edit_jv_month: '{{docNo}} is a journal-voucher number of {{month}}: its date stays in that month. To book it in another month, discard it and enter it there.',
  pa_edit_jv_typed: 'Journal-voucher numbers are given by the system — type the outside document\'s own number, or leave it empty.',
  pa_edit_review_hint: 'Generated from a document: only the account of a line that is not the customer\'s or supplier\'s line, a VAT line or a cash line is chosen here, or split. For anything else correct the document, then refresh or regenerate.',
  pa_edit_changed_elsewhere: 'This draft was changed somewhere else after you opened it. It has been read again — make your change once more.',
  pa_edit_need_narration: 'Every entry needs a description.',
  pa_edit_need_calc: 'With no outside document number this is an internal voucher: write down how the amount was arrived at.',
  pa_edit_need_bank: 'A bank-book entry names its bank account.',
  pa_edit_need_lines: 'At least two lines, each with an account and an amount on one side.',
  pa_edit_date_out: 'The date stays in this draft\'s period, {{from}} – {{to}}.',
  pa_edit_reverse_inside: 'A reversal date falls after this period ends ({{to}}).',
  pa_edit_account_switched_off: 'Its account {{code}} has been switched off — choose another.',
  pa_edit_account_not_postable: 'Nothing is posted to its account {{code}} — choose another.',
  pa_edit_account_missing: 'Its account is no longer in this chart — choose another.',
  pa_edit_refused_again: 'Change something first: the same entry would be refused again.',
  pa_draft_mirror_broken: '{{docNo}} no longer reverses {{reverses}} line for line — discard {{docNo}} and reverse {{reverses}} again.',
  pa_draft_hand_source_gone: '{{docNo}} was made by hand for {{what}}, which no longer stands (withdrawn, deleted or reversed since) — discard {{docNo}}: posting it would book something that is no longer there.',
  pa_draft_hand_source_held: '{{docNo}} was made by hand for {{what}}, which another entry books now — discard {{docNo}}: posting it would book it twice.',
  pa_draft_recorded_by_hand: 'Recorded by hand already: {{what}} is held by {{by}}, a manual entry made for it — discard this draft (hover for the other way), or the two would book it twice.',
  pa_draft_hand_prepaid_taken: '{{docNo}} was made by hand for {{what}} and books more of the customer\'s withholding on its receipt than is left for it — what was withheld, less what the receipt\'s other entries book (hover for which and how much). Discard {{docNo}} and record the line again with only what is left on the WHT-prepaid account (nothing, when the others book all of it), or take another entry out first: posting it would book the withholding beyond what was withheld.',
  pa_draft_hand_changed: 'Source changed: {{what}} no longer says what it said when {{docNo}} was made for it by hand, and an entry typed by hand does not follow it. Check the figures of {{docNo}} before posting it — if they no longer hold, discard {{docNo}} and record it again (Record by hand where Adjustments and corrections › Reverse offers it, or Generate from sub-ledger where it says so).',
  pa_draft_post_all_hand_left: 'Left out of Post all: {{n}} recorded by hand whose source has changed since — check the figures of each and post it from its own card.',
  pa_draft_post_many_hand_changed: '{{docNo}} was not posted: its source has changed since it was recorded by hand. Check its figures and post it from its own card, or discard it and record it again.',
  pa_draft_gen_created: 'Drafts created: {{n}}',
  pa_draft_gen_skipped: 'Already had a draft: {{n}}',
  // (W1 ship) A line a manual entry made for it by hand holds: Generate leaves it to that entry, and says which.
  pa_draft_gen_by_hand: 'Recorded by hand, left to their manual entries: {{n}} ({{list}})',
  // Counts sale-side drafts too (a mark-up line, a pass-through, an unnamed charge — income or
  // payable accounts), so it does not say "expense" (operator lens, 2026-10-20 third round).
  pa_draft_gen_needs_account: 'Need an account chosen: {{n}}',
  pa_draft_gen_blocked: 'Lines refused: {{n}} — {{list}}',
  pa_draft_gen_blocked_more: ' … and {{n}} more',
  pa_draft_no_doc_no: '(no number)',
  pa_draft_posted_single: 'Posted.',
  pa_draft_posted_n: 'Posted: {{n}}',

  // ── SplitLeg — dividing one leg across accounts by hand, and remembering an account ───────────
  // The screen for the documents routing cannot answer: half a bill that is two things. ⛔ Nothing
  // here states a rule of law or a tax treatment — these are the words around a person's own
  // decision, and the only figures in them are the ones the screen computed from the entry.
  pa_split_btn: 'Split',
  pa_split_btn_title: 'Divide this leg across several accounts',
  pa_split_head: 'Divide {{amount}} across accounts',
  pa_split_now_on: 'now on {{account}}',
  pa_split_account: 'Account',
  pa_split_amount: 'Amount',
  pa_split_choose: '— choose an account —',
  pa_split_add_row: 'Add a row',
  pa_split_remove_row: 'Remove this row',
  pa_split_even: 'Divide evenly',
  pa_split_carries_odd: 'carries the odd {{amount}}',
  pa_split_remaining: 'Still to allocate {{amount}}',
  pa_split_over: 'Over the leg by {{amount}}',
  pa_split_balanced: 'The rows come to the leg.',
  pa_split_pin_why: 'The rows have to come to {{amount}}, this leg’s own figure, or the entry stops balancing — the server checks it again before anything is stored.',
  pa_split_draft_only: 'This changes the draft only; nothing is posted until you post it.',
  pa_split_save: 'Save the split',
  pa_split_cancel: 'Cancel',
  pa_split_bad_amount: 'Row {{n}} is not an amount.',
  pa_split_dup: 'Two rows name the same account — combine them into one row.',
  pa_split_need_account: 'Every row needs an account.',
  pa_split_saved: 'Split saved. Nothing has been posted.',
  pa_split_group_one: 'Divided into {{count}} row',
  pa_split_group_other: 'Divided into {{count}} rows',
  pa_split_group_of: '{{amount}} that was on {{account}}',
  pa_split_by: 'by {{name}}',
  pa_split_undo: 'Undo',
  pa_split_undo_confirm: 'Undo this split? The rows go back to one leg on {{account}}.',
  pa_split_undone: 'Split undone — the leg is back on one account.',
  pa_rem_link: 'Remember',
  pa_rem_head: 'Remember {{account}} for next time',
  pa_rem_party: 'Document from {{name}}',
  pa_rem_which: 'For which charge on this document?',
  pa_rem_other: 'Something else…',
  pa_rem_type: 'The charge as the document spells it',
  pa_rem_none: 'This document does not name its charges, so type the charge as it is spelled on the paper.',
  pa_rem_scope_client_only: 'Remembered for this client only',
  pa_rem_save: 'Remember',
  pa_rem_cancel: 'Cancel',
  pa_rem_saved: 'Remembered: {{label}} → {{account}}. From the next document on it arrives there by itself.',
  pa_rem_need_label: 'Say which charge this account is for.',
  pa_rem_why: 'Splitting this document changed this document. Remembering is separate and deliberate — nothing above becomes a standing instruction unless you say so here.',


  pa_chart_title: 'Chart of accounts',
  pa_chart_download: 'Download PDF',
  pa_chart_download_failed: 'The PDF could not be built.',
  pa_chart_counts: '{{n}} accounts · {{used}} in use',
  pa_chart_empty: 'Empty — copy the firm template to start.',
  pa_chart_copy_template: 'Copy firm template',
  pa_chart_sync_template: 'Sync from template',
  pa_chart_sync_title: 'Preview the template accounts this chart does not have; an administrator adds the ones ticked',
  pa_chart_add_account: 'Add account',
  pa_chart_open: 'Open',
  pa_chart_hide: 'Hide',
  pa_chart_ph_code: 'Code',
  pa_chart_ph_name_th: 'Account name (Thai)',
  pa_chart_ph_name_en: 'English name',
  pa_chart_save: 'Save',
  pa_chart_cancel: 'Cancel',
  pa_chart_switch_off: 'Off',
  pa_chart_switch_on: 'On',
  pa_chart_name_th_manual: 'The Thai name is the account-code translation manual required by ม.21(1) — required',
  pa_chart_template_complete: 'The chart already has every account in the firm template.',
  pa_chart_remove_confirm: 'Remove account {{code}} {{name}}?',
  pa_chart_class_asset: 'Assets',
  pa_chart_class_liability: 'Liabilities',
  pa_chart_class_equity: 'Equity',
  pa_chart_class_income: 'Income',
  pa_chart_class_expense: 'Expenses',
  pa_chart_cf_prefixed: 'งบกระแสเงินสด: {{section}}',
  pa_chart_cf_none: '— not set —',
  pa_chart_cf_operating: 'Operating',
  pa_chart_cf_investing: 'Investing',
  pa_chart_cf_financing: 'Financing',
  pa_chart_cf_cash: 'Cash',
  pa_chart_cf_noncash: 'Non-cash',
  pa_chart_cf_missing_warn: '⚠️ An asset, liability or equity account with no cash-flow section will leave the cash flow statement out of balance — by exactly the amount that moved through this account.',
  pa_chart_cf_missing_title: 'The cash flow statement will not balance until this account is given a section',
  pa_chart_cf_missing_pill: 'No งบกระแสเงินสด section',
  // Where an income or expense account's money sits on บัญชีทำการ. The pill appears only on an
  // account that carries money and has no placement — the same test the statement applies before it
  // lists one — so it is never a row of warnings nobody can act on.
  pa_chart_fs_prefixed: 'บัญชีทำการ: {{section}}',
  pa_chart_fs_none: '— not set —',
  pa_chart_fs_trading: 'inside — sales / cost of sales',
  pa_chart_fs_other: 'not on it — below gross profit',
  pa_chart_fs_missing_title: 'This account carries money and no placement, so บัญชีทำการ lists it instead of totalling it',
  pa_chart_fs_missing_pill: 'No บัญชีทำการ section',
  // ── The write-down placement, asked once per client (owner 2026-10-14) ────────────────────────
  // ⛔ This is a PRESENTATION choice and the wording says nothing else. It names the two placements,
  // says what each does to the gross profit this system computes, and stops: no section number, no
  // rate, no framework named, no recommendation, and neither button highlighted. If it could not be
  // phrased without asserting a rule, the two placements alone would have been better than an
  // invented one. server/src/scripts/testWritedownPlacement.ts §11 holds that line in all three
  // languages.
  pa_chart_wd_title: 'Inventory written down — where does it appear?',
  pa_chart_wd_account: '{{code}} {{name}} · {{amount}}',
  pa_chart_wd_q: 'This client has written inventory down. Choose where that amount appears on บัญชีทำการ.',
  pa_chart_wd_trading: 'Inside cost of sales',
  pa_chart_wd_trading_hint: 'บัญชีทำการ totals it, so the gross profit there is lower by this amount.',
  pa_chart_wd_other: 'Below gross profit',
  pa_chart_wd_other_hint: 'บัญชีทำการ leaves it out, so the gross profit there is higher by this amount.',
  pa_chart_wd_unanswered: 'Until somebody answers, บัญชีทำการ lists this account with its amount and warns, and neither total includes it.',
  pa_chart_wd_note: 'Neither choice changes a figure in the ledger — only where the amount is shown. It is a presentation choice under this client’s own reporting framework and the system does not make it. Recorded once for this client, and not asked again.',
  pa_chart_wd_failed: 'Could not record that. Nothing was changed.',

  // ── CompliancePanel — Accounting Act records ──────────────────────────────────
  pa_compliance_title: 'Audit and DBD filing',
  pa_compliance_file_by: 'File by {{date}}',
  pa_compliance_due_unknown: 'The filing deadline cannot be worked out yet',
  pa_compliance_record_meeting_audit: 'Record meeting / audit',
  pa_compliance_record_filing: 'Record filing',
  pa_compliance_audit_unknown: '⚠ Whether an audit is required is still UNKNOWN',
  pa_compliance_audit_required: 'Must be audited by a Certified Public Accountant (CPA)',
  pa_compliance_audit_exempt: 'Exempt from audit',
  pa_compliance_unbacked: '{{n}} entries with no document, no number and no stated basis. They cannot be posted (ข้อ 5).',
  pa_compliance_filed_msg: '{{kind}} recorded as filed on {{date}}.',
  pa_compliance_agm_due: 'General meeting by {{date}}',
  pa_compliance_agm_held: 'met on {{date}}',
  pa_compliance_agm_not_held: 'not met yet',
  pa_compliance_statements_due: 'statements to shareholders by {{date}}',
  pa_compliance_bojo5_due: 'File บอจ.5 by {{date}}',
  pa_compliance_filed_label: 'Filed:',
  pa_compliance_bookkeeper_label: 'Bookkeeper:',
  pa_compliance_tfac: '(TFAC {{no}})',
  pa_compliance_tfac_lapsed: 'membership lapsed',
  pa_compliance_no_bookkeeper: 'None appointed (Accounting Act s.19)',
  pa_compliance_eacct_pending: 'e-Accountant notification not yet made for {{name}} (within 30 days of {{date}})',
  pa_compliance_notified_today: 'Notified today',
  pa_compliance_f_agm_date: 'General meeting date',
  pa_compliance_f_statements_sent: 'Statements sent to shareholders',
  pa_compliance_f_audit_completed: 'Audit completed',
  pa_compliance_f_opinion: 'Opinion',
  pa_compliance_opinion_unqualified: 'Unqualified',
  pa_compliance_opinion_qualified: 'Qualified',
  pa_compliance_opinion_adverse: 'Adverse',
  pa_compliance_opinion_disclaimer: 'Disclaimer',
  pa_compliance_f_auditor: 'Auditor',
  pa_compliance_f_auditor_license: 'Registration no.',
  pa_compliance_f_auditor_kind: 'Type',
  pa_compliance_auditor_cpa: 'Certified Public Accountant (CPA)',
  pa_compliance_auditor_ta: 'Tax Auditor (TA)',
  pa_compliance_f_audit_fee: 'Audit fee',
  pa_compliance_save: 'Save',
  pa_compliance_f_kind: 'What is being filed',
  pa_compliance_kind_fs: 'งบการเงิน',
  pa_compliance_kind_fs_gloss: 'Financial statements',
  pa_compliance_kind_bojo5: 'บอจ.5',
  pa_compliance_kind_bojo5_gloss: 'Shareholder list',
  pa_compliance_f_filed_on: 'Date filed',
  pa_compliance_f_receipt_no: 'เลขที่ใบรับ · Receipt no.',
  pa_compliance_record: 'Record',

  // ── FixedAssets — the asset register ──────────────────────────────────────────
  pa_assets_register_name: 'ทะเบียนทรัพย์สิน',
  pa_assets_title: 'Fixed assets',
  pa_assets_summary: '{{n}} asset(s) · {{nbv}} net book value',
  pa_assets_empty: 'The register is empty.',
  pa_assets_due: '{{amount}} depreciation to charge this period',
  pa_assets_open_register: 'Register',
  pa_assets_charge_dep: 'Charge depreciation {{amount}}',
  pa_assets_dep_confirm: 'Charge depreciation of {{amount}} for this period?\n\nIt is written as a DRAFT — review it under Journal entries and post it there before it reaches the ledger.',
  pa_assets_add: 'Fixed asset',
  pa_assets_open: 'Open',
  pa_assets_hide: 'Hide',
  pa_assets_registered_msg: '{{no}} registered at {{rate}}% a year.',
  pa_assets_dep_draft: '{{n}} asset(s), {{amount}} — created as a draft for review.',
  pa_assets_dep_none: 'Every asset has already been charged for this period.',
  pa_assets_f_class: 'Class',
  pa_assets_no_dep: 'Not depreciated',
  pa_assets_f_acquired: 'Acquired on',
  pa_assets_f_cost: 'Cost',
  pa_assets_f_salvage: 'Salvage value',
  pa_assets_import_btn: 'Import a register',
  pa_assets_import_note: 'The asset register as a CSV or .xlsx file, one row per asset. The first row names the columns, in English, Thai or Chinese, in any order. Required: a name (“Name”, and “Thai name” if you keep one), the purchase date (“Purchase date”, “Acquired” or “Date”) and the cost (“Cost”). Optional: “Category” (a row without one takes the category chosen here), “Asset class”, “Rate”, “Residual”, “Asset account”, “Accumulated account” and “Expense account” (by code), “Accumulated brought forward” with “Brought forward to”, “Note”, “Supplier”, “Invoice no.”, “Capitalised cost”, “Available date”, “Depreciation start”, “Location”, “Status” (“in use”, “not yet available” or “idle”) and “Useful life (years)”. A column with any other heading is ignored, and checking the file names it. Every row is checked exactly as the form checks one: it needs a category, a depreciable class must carry a rate at or under the decree\'s ceiling, the residual must sit below the cost, and the three accounts must be in this client\'s chart.',
  pa_assets_import_preview: 'Check the file',
  pa_assets_import_commit: 'Register them',
  pa_assets_import_summary: '{{ok}} asset(s) would be registered · {{refused}} refused',
  pa_assets_import_will_add: 'will be registered',
  pa_assets_import_ignored: '{{n}} column(s) will be ignored — nothing in them is registered. If one of them matters, rename its heading to one the note above names (or remove the column) and check the file again.',
  pa_assets_import_done: '{{n}} asset(s) registered.',
  pa_assets_cadence_annual: 'Charged once a year',
  pa_assets_cadence_monthly: 'Charged monthly',
  pa_assets_month: 'Which month to charge',
  pa_assets_pick_month: 'Pick the month to charge.',
  pa_assets_f_opening: 'Accumulated depreciation brought forward',
  pa_assets_f_opening_as_of: '…which runs to',
  pa_assets_opening_hint: 'For an asset that was already in use before its books were kept here. Leave both blank for an asset bought while on this system. The next charge starts the day after the date given, so the stretch already depreciated elsewhere is never charged twice.',
  pa_assets_f_rate: 'Rate %',
  pa_assets_f_name_th: 'Item (Thai) — required',
  pa_assets_f_name_en: 'Description (English)',
  pa_assets_f_acct_asset: 'Asset account',
  pa_assets_f_acct_accum: 'Accumulated depreciation',
  pa_assets_f_acct_expense: 'Depreciation expense',
  pa_assets_max_rate_source: 'Maximum rate per {{source}}',

  // ── Asset categories (batch 5 round 8, H2a) ───────────────────────────────────
  // ⛔ Two layers, and the words keep them apart: a CATEGORY is the client's own accounting grouping;
  // the decree CLASS it points at is the Royal Decree's tax class, and the only thing it decides is
  // the rate ceiling. The customer's own category names are never translated.
  pa_cat_title: 'Asset categories',
  pa_cat_count: 'Categories: {{n}}',
  pa_cat_none: 'No categories yet — an asset can still be registered without one.',
  pa_cat_add: 'Category',
  pa_cat_th_name: 'Category',
  pa_cat_th_class: 'Decree class · rate',
  pa_cat_th_accounts: 'Accounts',
  pa_cat_th_assets: 'Assets',
  pa_cat_edit: 'Edit',
  pa_cat_on: 'Switch on',
  pa_cat_off: 'Switch off',
  pa_cat_delete: 'Remove',
  pa_cat_delete_confirm: 'Remove the category "{{name}}"?\n\nIt is only removed while no asset has ever been registered under it — disposed ones count. Switch it off instead to stop it being offered.',
  pa_cat_inactive: 'switched off',
  pa_cat_saved: 'Category saved.',
  pa_cat_f_name_th: 'Category name (Thai) — required',
  pa_cat_f_name_en: 'Category name (English)',
  pa_cat_f_class: 'Decree class (rate ceiling)',
  pa_cat_f_nature: 'Charge',
  pa_cat_f_rate: 'Rate %',
  pa_cat_nature_depreciation: 'Depreciation',
  pa_cat_nature_amortisation: 'Amortisation',
  pa_cat_nature_none: 'Never charged (land)',
  pa_cat_never_note: 'Land is held and never charged, so this category has no accumulated depreciation account, no expense account and no rate. It can only point at a class the decree does not depreciate.',
  pa_cat_note: 'A category is this client\'s own accounting grouping; the decree class it points at is only where the maximum rate comes from. Registering an asset copies the category\'s three accounts and its rate onto that asset, so changing a category later does not move an asset already registered.',
  pa_assets_f_category: 'Category',
  pa_assets_f_category_none: 'No category',
  pa_assets_f_category_pick: 'Choose a category…',
  pa_assets_import_category: 'Category for rows without one',
  pa_assets_cancel: 'Cancel',
  pa_assets_submit: 'Register',
  pa_assets_th_no: 'No.',
  pa_assets_th_item: 'Item',
  pa_assets_th_acquired: 'Acquired',
  pa_assets_th_cost: 'Cost',
  pa_assets_th_accum: 'Accum. depreciation',
  pa_assets_th_nbv: 'Net book value',
  pa_assets_th_year: 'This year',
  pa_assets_disposed_on: 'Disposed {{date}}',
  pa_assets_days: '{{n}}d',

  // ── ManualEntry — the general journal ─────────────────────────────────────────
  pa_manual_book_general_gloss: 'General',
  pa_manual_book_cash_gloss: 'Cash',
  pa_manual_book_bank_gloss: 'Bank',
  pa_manual_book_purchase_gloss: 'Purchase',
  pa_manual_book_sale_gloss: 'Sale',
  pa_manual_title: 'Adjustments and corrections',
  pa_manual_subtitle: 'Accruals, prepayments, depreciation and provisions — and the reversal that is the only way to correct a posted entry.',
  pa_manual_new_btn: 'Record an entry',
  pa_manual_reverse_btn: 'Reverse',
  pa_manual_sweep_title: 'Bring in accruals from earlier periods that reverse into this one',
  pa_manual_accruals_btn: 'Accruals',
  pa_manual_import_btn: 'Import a file',
  pa_manual_import_note: 'A CSV of your own vouchers: one row per line of an entry, rows sharing a document number forming one voucher. Columns read by name — date, document no., description, account code, debit, credit (Thai and Chinese headers are understood too). Every voucher is checked exactly as a typed one is: it must balance, every account must be in this client\'s chart, the date must fall inside the open period, and a voucher with no document number needs its calculation basis.',
  pa_manual_import_preview: 'Check the file',
  pa_manual_import_commit: 'Enter them',
  pa_manual_import_blocked: 'Some vouchers in this file cannot be entered — fix them in the file and check it again. Nothing is imported while any is refused.',
  pa_manual_import_summary: '{{ok}} voucher(s) would be entered · {{refused}} refused',
  pa_manual_import_will_enter: 'will be entered',
  pa_manual_import_done: '{{n}} voucher(s) entered as drafts — review and post them.',
  pa_manual_copy_btn: 'Repeat',
  pa_manual_copy_title: 'Enter this same voucher again on another date — the monthly bank charge, a standing accrual. It takes its own new voucher number.',
  pa_manual_copy_ref_hand: '{{docNo}} was recorded by hand for a source, or takes such an entry back, so it is not repeated: a repeat would book the same figures again, holding nothing. To record a source by hand, use Record by hand where this list offers it.',
  pa_manual_copy_ref_settlement: '{{docNo}} is a settlement generated from a statement line or a petty cash row (or takes one back), so it is not repeated: a repeat would book the money a second time, holding no source. Generate settles the line again once it is free; Record by hand books one Generate cannot.',   // batch 11 W4 P9 (W3.2 low)
  pa_manual_copy_prompt: 'Repeat {{docNo}} on which date? (YYYY-MM-DD)',
  pa_manual_copy_done: 'Entered as {{docNo}} — review and post it.',
  pa_manual_late_entries: 'Entries posted more than 15 days after their date: {{n}} — the latest by {{days}} days ({{docNo}}).',
  pa_manual_late_rule: 'The DBD requires the daily journals to be written up within 15 days of the transaction.',
  pa_manual_closing_overdue: 'This period ended {{date}}, {{days}} days ago, and is still open. The accounts are to be closed within 60 days.',
  pa_manual_saved: 'Draft {{docNo}} created — review it below and post it.',
  pa_manual_reverse_confirm: 'Reverse {{docNo}}? The original stays in its book with its page number; the correction is created as a draft beside it.',
  pa_manual_reversed_msg: '{{docNo}} created as a draft, dated {{date}}.',
  pa_manual_sweep_done: '{{n}} accrual reversal(s) created as drafts.',
  pa_manual_sweep_none: 'No accrual from an earlier period reverses into this one.',
  pa_manual_f_date: 'Date',
  pa_manual_f_book: 'Journal',
  pa_manual_f_bank_acct: 'Bank account number',
  pa_manual_f_bank_acct_hint: 'one book per account',
  pa_manual_f_bank_acct_ph: 'e.g. XXX-X-XXXXX-X',
  pa_manual_bank_acct_new: 'Another account…',
  pa_manual_bank_acct_used: '{{n}} so far',
  pa_manual_bank_acct_none: 'No bank book has been kept for this client yet — type the account number and it will be offered next time.',
  pa_manual_f_docno: 'Document number',
  pa_manual_f_docno_hint: '(blank → JV number)',
  pa_manual_f_docno_ph: 'JV-… allocated',
  pa_manual_f_reverse_on: 'Reverse on',
  pa_manual_f_reverse_on_hint: '(accrual)',
  pa_manual_narration_ph: 'Description — required',
  pa_manual_calcnote_ph: 'How the amount was arrived at — required when there is no supporting document (ข้อ 4)',
  pa_manual_th_account: 'Account',
  pa_manual_th_dr: 'Debit',
  pa_manual_th_cr: 'Credit',
  pa_manual_pick_account: '— Select an account —',
  pa_manual_add_line: 'Line',
  pa_manual_no_amounts: 'No amounts yet',
  pa_manual_balances: 'Balances',
  pa_manual_out_by: 'Out by {{amount}}',
  pa_manual_save_draft: 'Save as draft',
  pa_manual_reverse_note: 'A posted entry is never unposted. It keeps its place and its page number, and the correction is a new entry beside it — which is what leaves an audit trail.',
  pa_manual_search_ph: 'Search posted entries…',
  pa_manual_filter_by_hand: 'Can be recorded by hand',   // batch 11 W4 P9 (LB-29): every such row, 100 a page
  pa_manual_none_posted: 'Nothing posted matches.',
  pa_manual_reversed_by: 'Reversed {{docNo}}',
  pa_manual_rev_ref_asset: 'Not reversible here: an asset rule holds {{docNo}} (hover for why).',
  pa_manual_rev_ref_asset_reversal: '{{docNo}} put the asset register back — run the depreciation or disposal again instead.',
  pa_manual_rev_ref_reversal_of_released: 'Not reversible here: {{docNo}} took back an entry whose source was released (hover for why).',
  pa_manual_rev_ref_no_open_period: 'No open accounting period to put a reversal of {{docNo}} in.',
  pa_manual_rev_ref_no_lines: '{{docNo}} has no lines to reverse.',
  pa_manual_by_hand_btn: 'Record by hand',
  pa_manual_by_hand_title: 'Record the correction as a manual entry made for this source: the entry holds it, so Generate does not book it again while the entry stands.',
  pa_manual_for_tax_line: 'the source document {{label}}',
  pa_manual_for_bank_line: 'the statement line {{label}}',
  pa_manual_prepaid_taken: 'The customer\'s withholding on {{receipt}} is booked already by {{by}} — {{way}}',
  pa_manual_prepaid_by_settlement: 'the settlement of the statement line {{line}}',
  pa_manual_prepaid_by_hand: '{{docNo}}, made by hand for the statement line {{line}}',
  pa_manual_prepaid_way_discard: 'leave account {{codes}} out of this entry, or discard that draft first.',
  pa_manual_prepaid_way_reverse: 'leave account {{codes}} out of this entry, or reverse that entry and post the reversal first.',
  pa_manual_prepaid_way_post_reversal: 'leave account {{codes}} out of this entry, or post {{rv}}, that entry\'s reversal, first.',
  pa_manual_prepaid_way_closed: 'that entry is posted in a closed accounting period and stays in the books — leave account {{codes}} out of this entry.',
  pa_manual_prepaid_unclear_self: 'This statement line is matched to invoice {{invoice}}, which has more than one receipt with withholding ({{receipts}}) — whose withholding this entry would book cannot be told. Match the line to its receipt instead of the invoice, or leave account {{codes}} out of this entry.',
  pa_manual_prepaid_unclear_other: '{{docNo}}, made by hand for the statement line {{line}} matched to invoice {{invoice}}, may book the customer\'s withholding on {{receipt}} already — that invoice has more than one receipt with withholding, so whose it books cannot be told. Match that line to its receipt instead of the invoice first, or leave account {{codes}} out of this entry.',
  pa_manual_prepaid_over: 'Of the customer\'s withholding on {{receipt}} ({{withheld}}), {{booked}} is booked already by {{by}} — this entry\'s {{mine}} on account {{codes}} would book more than was withheld: debit at most {{room}} to it.',
  pa_manual_prepaid_over_whole: 'The customer withheld {{withheld}} on {{receipt}} — this entry\'s {{mine}} on account {{codes}} is more than was withheld: debit at most {{room}} to it.',   // the W4 gate review: the receipt's own figure bounds one entry alone too
  pa_manual_prepaid_room_hint: 'This line may carry the other {{room}} of the customer\'s withholding on {{receipt}} on the WHT-prepaid account, if the customer withheld it on this payment.',   // batch 11 W4 P9 (W3.2 residual)
  pa_manual_prepaid_by_more: '{{by}} and {{n}} more entries of the receipt',
  pa_manual_prepaid_way_several: 'leave account {{codes}} out of this entry, or take one of those entries out of the books first.',
  pa_manual_for_cash_line: 'the petty cash row {{label}}',
  pa_manual_for_banner: 'Made by hand for {{what}}: this entry holds it, so Generate does not book it again while the entry stands.',
  pa_manual_row_closed_period: 'closed period {{from}} – {{to}}',
  pa_manual_payroll_owned: 'Payroll {{docNo}} — reversed from its run in Payroll › Payroll list, not here',
  pa_manual_payroll_pay_owned: 'Payment {{payNo}} of payroll {{docNo}} — reversed in that run’s window in Payroll › Payroll list, not here',
  pa_manual_payroll_remit_owned: 'Remittance of {{month}} — reversed in Payroll › Payroll list › Remittances, not here',

  // ── Headless — the records with no other home ─────────────────────────────────
  pa_headless_wa_rule_a: 'Revenue Code s.68 bis — for tax purposes every company prepares a balance sheet, a ',
  pa_headless_wa_rule_b: 'working account',
  pa_headless_wa_rule_c: ' and a profit-and-loss account. ⚠️ The DBD\'s official layout is not held here, so this prints in this system\'s own form.',
  pa_headless_wa_income: 'Income',
  pa_headless_wa_cost: 'Cost of sales',
  pa_headless_wa_gross: 'Gross profit',
  pa_headless_wa_expenses: 'Expenses',
  pa_headless_wa_net: 'Net profit / (loss)',
  pa_headless_wa_unplaced: '{{n}} account(s) are not placed on the working account — {{amount}} is left out of the gross profit above.',
  pa_headless_wa_doc_name: 'บัญชีทำการ',
  pa_headless_wa_doc: 'Working account',
  pa_headless_fw_rule: 'Forms 1, 2, 4 and 5 are built on TFRS for NPAEs; Form 3 on TFRS for PAEs. An NPAE may elect TFRS for PAEs if it applies them in full, and must disclose the change of format in the notes.',
  pa_headless_fw_period: 'Recorded against this period',
  pa_headless_fw_period_tail: ' — a closed period is never restated.',
  pa_headless_fw_none: '— not recorded —',
  pa_headless_fw_npae: 'TFRS สำหรับ NPAEs',
  pa_headless_fw_pae: 'TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะ',
  pa_headless_fw_dbd_form: 'DBD แบบ {{form}}',
  pa_headless_fw_locked: 'Closed — locked',
  pa_headless_sh_rule: 'CCC — a company keeps a share register from incorporation and files บอจ.5 as at the meeting date within 14 days. ⚠️ The Registrar\'s blank บอจ.5 is not held here, so the list can be read and checked but not printed onto the official form.',
  pa_headless_sh_as_at: 'As at',
  pa_headless_sh_holders: '{{n}} shareholders',
  pa_headless_sh_shares: '{{n}} shares',
  pa_headless_sh_form_name: 'บอจ.5',
  pa_headless_sh_form_not_held: 'form not held',
  pa_headless_sh_name_ph: 'Shareholder name',
  pa_headless_sh_individual: 'Individual',
  pa_headless_sh_juristic: 'Juristic person',
  pa_headless_sh_idno: 'ID number',
  pa_headless_sh_nationality: 'Nationality',
  pa_headless_sh_shares_ph: 'Number of shares',
  pa_headless_sh_paid_per_share: 'Paid per share',
  pa_headless_sh_held_from: 'Held from',
  pa_headless_sh_add: 'Add',
  pa_headless_sh_th_name: 'Name',
  pa_headless_sh_th_kind: 'Kind',
  pa_headless_sh_th_shares: 'Shares',
  pa_headless_sh_end_prompt: 'Holding ended on (YYYY-MM-DD)?',
  pa_headless_sh_end: 'End…',
  pa_headless_sh_empty: 'No holdings recorded at this date.',
  pa_headless_pkg_rule: 'Financial statements and the auditor\'s report are filed through DBD e-Filing: the statements in XBRL, the auditor\'s report as a black-and-white scan, and the shareholder list.',
  pa_headless_pkg_unit: 'Unit',
  pa_headless_pkg_file: 'file {{id}}',
  pa_headless_pkg_declared: 'declared',
  pa_headless_pkg_not_attached: 'not attached',
  pa_headless_pkg_not_producible: 'not producible here',
  pa_headless_pkg_file_prompt: 'File ID for {{slot}}?',
  pa_headless_pkg_attach: 'Attach…',
  pa_headless_pkg_declare_bw: 'Declare the auditor\'s report black-and-white',
  pa_headless_cit_rule: 'Revenue Code s.65 ter — which accounts are deductible for corporate income tax and which are not. ⚠️ Only subsections (1)–(6) are held here; the rest of s.65 ter has not been obtained.',
  pa_headless_cit_th_account: 'Account',
  pa_headless_cit_th_movement: 'Movement',
  pa_headless_cit_th_treatment: 'CIT treatment',
  pa_headless_cit_th_note: 'Note',
  pa_headless_cit_undecided: '— not decided —',
  pa_headless_cit_yes: 'Deductible',
  pa_headless_cit_no: 'Not deductible',
  pa_headless_cit_limited: 'Deductible in part',
  pa_headless_cit_empty: 'Nothing to review for this period.',
  pa_headless_fx_rule_a: 'Revenue Code s.86/4 — a tax invoice is in Thai baht unless the Revenue Department approves another currency. ',
  pa_headless_fx_rule_b: 'This gate is now enforced when a tax invoice is issued or edited',
  pa_headless_fx_rule_c: ': until an approval is recorded here, a non-THB tax invoice is refused.',
  pa_headless_fx_approval_no: 'RD approval no.',
  pa_headless_fx_approved_on: 'Approved on',
  pa_headless_fx_effective_from: 'Effective from',
  pa_headless_fx_effective_to: 'To',
  pa_headless_fx_recorded: 'Approval recorded for {{currency}}.',
  pa_headless_fx_record: 'Record approval',
  pa_headless_fx_check: 'Check {{currency}} today',
  pa_headless_fx_allowed: 'Allowed.',
  pa_headless_fx_not_allowed: 'Not allowed.',
  pa_headless_ret_label: 'Retention period',
  pa_headless_ret_5y: '5 years (the minimum under มาตรา 14)',
  pa_headless_ret_6y: '6 years',
  pa_headless_ret_7y: '7 years (the most the DBD can extend to)',
  pa_headless_ret_th_when: 'When',
  pa_headless_ret_th_what: 'What',
  pa_headless_ret_th_mode: 'Mode',
  pa_headless_ret_th_reason: 'Reason',
  pa_headless_ret_th_by: 'By',
  pa_headless_ret_none: 'Nothing has been destroyed.',
  pa_headless_agm_registered: 'Registered',
  pa_headless_agm_first_due: 'First meeting due by',
  pa_headless_agm_last_held: 'Last meeting held',
  pa_headless_agm_next_due: 'Next due by',
  pa_headless_agm_overdue: 'Overdue',
  pa_headless_wht_settled: '{{n}} settled',
  pa_headless_wht_payer_alone: 'Payer liable alone',
  pa_headless_wht_joint: 'Jointly liable',
  pa_headless_wht_at_risk: 'Tax still at risk: {{amount}} $t(cur_unit).',
  pa_headless_wht_th_paid: 'Paid',
  pa_headless_wht_th_payee: 'Payee',
  pa_headless_wht_th_form: 'Form',
  pa_headless_wht_th_gross: 'Gross',
  pa_headless_wht_th_withheld: 'Withheld',
  pa_headless_wht_th_who: 'Who owes it',
  pa_headless_wht_th_why: 'Why',
  pa_headless_wht_all_clear: 'Every line was withheld and remitted.',
  pa_headless_sso_rule_a: 'A newly registered company is an employer by operation of law and registers its employees for social security. Per-employee SSO ids were already held; the ',
  pa_headless_sso_rule_b: 'employer\'s own',
  pa_headless_sso_rule_c: ' account was not.',
  pa_headless_sso_account_no: 'Employer account number',
  pa_headless_sso_branch: 'Branch',
  pa_headless_sso_registered_on: 'Registered on',
  pa_headless_sso_form_101: 'สปส.1-01',
  pa_headless_sso_office: 'Area office',
  pa_headless_sso_saved: 'Saved.',
  pa_headless_sso_save: 'Save',
  pa_headless_sso_employees: '{{n}} employees',
  pa_headless_sso_no_number: '{{n}} with no social-security number',
  pa_headless_rev_rule_a: 'Revenue Code s.65 bis (3)-(4) — an upward revaluation gain is excluded from taxable profit',
  pa_headless_rev_rule_b: ' and',
  pa_headless_rev_rule_c: ' from the depreciation base, so the book basis and the tax basis diverge permanently.',
  pa_headless_rev_th_asset: 'Asset',
  pa_headless_rev_th_cost: 'Cost',
  pa_headless_rev_th_book: 'Book basis',
  pa_headless_rev_th_tax: 'Tax basis',
  pa_headless_rev_th_reval: 'Revaluation',
  pa_headless_rev_amount_prompt: 'New carrying amount for {{name}} ($t(cur_word))?',
  pa_headless_rev_date_prompt: 'Revalued on (YYYY-MM-DD)?',
  pa_headless_rev_basis_prompt: 'Who valued it, and under what report? (required)',
  pa_headless_rev_revalue: 'Revalue…',
  pa_headless_rev_empty: 'No assets on the register.',
  pa_headless_module_title: 'The rest',
  pa_headless_sec_wa_name: 'บัญชีทำการ',
  pa_headless_sec_wa: 'Working account (s.68 bis)',
  pa_headless_sec_wa_sub: 'Built, and had no button until now.',
  pa_headless_sec_fw_name: 'แม่บทการรายงานทางการเงิน',
  pa_headless_sec_fw: 'Reporting framework',
  pa_headless_sec_fw_sub: 'Now recorded against the period — a closed one is never restated.',
  pa_headless_sec_sh_name: 'ทะเบียนผู้ถือหุ้น',
  pa_headless_sec_sh: 'Share register / บอจ.5',
  pa_headless_sec_sh_sub: 'Holdings as at a date, with the transfer chain.',
  pa_headless_sec_pkg: 'DBD e-Filing package',
  pa_headless_sec_pkg_sub: 'Three slots, the unit declaration, and what cannot be produced here.',
  pa_headless_sec_cit: 'CIT treatment by account (s.65 ter)',
  pa_headless_sec_cit_sub: 'Subsections (1)–(6) only — the rest has not been obtained.',
  pa_headless_sec_fx_name: 'ใบกำกับภาษีสกุลเงินต่างประเทศ',
  pa_headless_sec_fx: 'Foreign-currency approval (s.86/4)',
  pa_headless_sec_fx_sub: '⚠️ Now enforced when a tax invoice is issued or edited.',
  pa_headless_sec_ret: 'Retention and destruction register (s.14)',
  pa_headless_sec_ret_sub: 'The machinery existed with no route at all.',
  pa_headless_sec_agm: 'General meetings (CCC s.1197)',
  pa_headless_sec_agm_sub: 'The first within 6 months of registration, then every 12.',
  pa_headless_sec_wht: 'Who owes the withholding (s.54)',
  pa_headless_sec_wht_sub: 'Withheld-not-remitted is the payer alone; never withheld is joint.',
  pa_headless_sec_sso: 'Employer social-security registration',
  pa_headless_sec_sso_sub: 'The employer\'s own account, not the employees\'.',
  pa_headless_sec_rev: 'Asset revaluation, book vs tax basis (s.65 bis)',
  pa_headless_sec_rev_sub: 'The gain is out of taxable profit and out of the depreciation base.',

  // ── Registers — the statutory registers ───────────────────────────────────────
  pa_reg_title: 'Statutory registers',
  pa_reg_record: 'Record',
  pa_reg_save: 'Save',
  pa_reg_saved: 'Saved.',
  pa_reg_empty: 'Nothing on the register.',
  pa_reg_overdue: 'overdue',
  pa_reg_days_late: '{{n}}d late',
  pa_reg_doc_directors_report: 'รายงานของกรรมการ',
  pa_reg_doc_firm_register: 'ทะเบียนสำนักงานบัญชี',
  pa_reg_doc_liquidation_report: 'รายงานการชำระบัญชี',
  pa_reg_doc_sales_tax_invoice: 'ใบกำกับภาษีขาย',
  pa_reg_incident_kind_lost: 'Lost',
  pa_reg_incident_kind_damaged: 'Damaged',
  pa_reg_incident_discovered: 'discovered',
  pa_reg_incident_happened: 'happened',
  pa_reg_incident_scope_ph: 'which books / documents',
  pa_reg_incident_cause_ph: 'cause (s.16: blank = presumed intentional)',
  pa_reg_incident_presumed_alert: 's.16: a loss with no stated cause is PRESUMED INTENTIONAL. Record what happened as soon as it is known.',
  pa_reg_incident_th_discovered: 'Discovered',
  pa_reg_incident_th_what: 'What',
  pa_reg_incident_th_scope: 'Scope',
  pa_reg_incident_th_cause: 'Cause',
  pa_reg_incident_th_notify_by: 'Notify by',
  pa_reg_incident_th_notified: 'Notified',
  pa_reg_incident_recovered: 'recovered',
  pa_reg_incident_cause_blank: 'not stated — s.16 presumes intentional',
  pa_reg_incident_notified_prompt: 'Notified the DBD on (YYYY-MM-DD)?',
  pa_reg_incident_epermit_prompt: 'e-Permit reference?',
  pa_reg_incident_notified_btn: 'Notified…',
  pa_reg_incident_unexplained: 'Incidents with no stated cause: {{n}}. s.16 presumes an unexplained loss intentional — record what happened.',
  pa_reg_cess_ceased: 'ceased',
  pa_reg_cess_reason_ph: 'reason',
  pa_reg_cess_record: 'Record cessation',
  pa_reg_cess_ceased_on: 'Ceased {{date}}',
  pa_reg_cess_deliver_by: 'Deliver by',
  pa_reg_cess_extended_to: 'extended to {{date}}',
  pa_reg_cess_max_extension: '(extendable to {{date}} at the latest)',
  pa_reg_cess_delivered: 'Delivered {{date}}',
  pa_reg_cess_not_delivered: 'Not delivered',
  pa_reg_cess_overdue_days: 'overdue by {{n}} days',
  pa_reg_cess_extend_prompt: 'Extended to (YYYY-MM-DD, at the latest {{date}})?',
  pa_reg_cess_extend_ref_prompt: 'Extension reference?',
  pa_reg_cess_extend_btn: 'Extension granted…',
  pa_reg_cess_delivered_prompt: 'Delivered to the DBD on (YYYY-MM-DD)?',
  pa_reg_cess_receipt_prompt: 'DBD\'s acknowledgement reference?',
  pa_reg_cess_delivered_btn: 'Delivered…',
  pa_reg_insp_demanded: 'demanded',
  pa_reg_insp_authority_ph: 'authority',
  pa_reg_insp_inspector_ph: 'inspector',
  pa_reg_insp_due: 'due',
  pa_reg_insp_record: 'Record demand',
  pa_reg_insp_asked_label: 'Asked to produce',
  pa_reg_insp_preview: 'Preview the manifest',
  pa_reg_insp_build: 'Build the pack',
  pa_reg_insp_th_demanded: 'Demanded',
  pa_reg_insp_th_by: 'By',
  pa_reg_insp_th_asked: 'Asked for',
  pa_reg_insp_th_due: 'Due',
  pa_reg_insp_th_answered: 'Answered',
  pa_reg_insp_answered_prompt: 'Answered on (YYYY-MM-DD)?',
  pa_reg_insp_answered_btn: 'Answered…',
  pa_reg_chg_resolved: 'resolved',
  pa_reg_chg_from_ph: 'from',
  pa_reg_chg_to_ph: 'to',
  pa_reg_chg_liquidation_due: 'the next liquidation report is due {{due}} (three months from {{since}}).',
  pa_reg_chg_th_resolved: 'Resolved',
  pa_reg_chg_th_what: 'What',
  pa_reg_chg_th_fromto: 'From → to',
  pa_reg_chg_th_register_by: 'Register by',
  pa_reg_chg_th_registered: 'Registered',
  pa_reg_chg_registered_prompt: 'Registered with the DBD on (YYYY-MM-DD)?',
  pa_reg_chg_receipt_prompt: 'Receipt number?',
  pa_reg_chg_registered_btn: 'Registered…',
  pa_reg_dr_meeting: 'meeting',
  pa_reg_dr_presented: 'presented',
  pa_reg_dr_body_th_label: 'Directors\' report (Thai)',
  pa_reg_dr_body_en_label: 'Directors\' report (English)',
  pa_reg_vat_rev12: 'Revenue, 12 months {{from}} – {{to}}',
  pa_reg_vat_source: 'from {{source}}',
  pa_reg_vat_source_ledger: 'the books',
  pa_reg_vat_exceeded_head: 'Over 1.8 ล้านบาท.',
  pa_reg_vat_exceeded_body: 'The rolling twelve months are over the registration threshold.',
  pa_reg_vat_notice_ack_by: 'Confirmed by',
  pa_reg_vat_notice_ack_tail: ', so the daily notice has stopped.',
  pa_reg_vat_notice_sending: 'Daily notice — sent to Telegram and {{mailbox}}, {{n}} times.',
  pa_reg_vat_notice_sending_last: 'Daily notice — sent to Telegram and {{mailbox}}, {{n}} times, last on {{date}}.',
  pa_reg_vat_notice_stops: 'It stops when somebody clicks the link in the email.',
  pa_reg_vat_notice_last_send: 'Last send:',
  pa_reg_vat_notice_resume: 'Start sending again',
  pa_reg_stock_cost_formula: 'cost formula',
  pa_reg_stock_method_none: '— not chosen —',
  pa_reg_stock_th_item: 'Item',
  pa_reg_stock_th_qty: 'Qty',
  pa_reg_stock_th_cost: 'Cost',
  pa_reg_stock_th_market: 'Market',
  pa_reg_stock_th_carried: 'Carried',
  pa_reg_stock_th_writedown: 'Write-down',
  pa_reg_stock_total: 'Total',
  pa_reg_stock_empty: 'Nothing on hand to value.',
  pa_reg_stock_struck_msg: 'Struck. Write-down {{amount}} on draft entry {{doc}} — review it with the other drafts.',
  pa_reg_stock_struck_none: 'Struck. Nothing to write down.',
  pa_reg_stock_strike_btn: 'Strike the valuation',
  pa_reg_stock_struck_line: 'Struck: {{n}} items, on {{date}} by {{name}}.',
  pa_reg_firm_rule: 'Accounting Professions Act s.11 — a juristic person providing accounting services registers with the TFAC and posts a guarantee for third-party liability; one offering audit services must be bound by a CPA, and is jointly liable with its auditor.',
  pa_reg_firm_regno_ph: 'TFAC registration no.',
  pa_reg_firm_registered: 'registered',
  pa_reg_firm_expires: 'expires',
  pa_reg_firm_guarantee_ph: 'guarantee posted (baht)',
  pa_reg_firm_guarantee_form_ph: 'form (bank guarantee…)',
  pa_reg_firm_offers_audit: 'offers audit services',
  pa_reg_firm_cpa_name_ph: 'CPA signatory',
  pa_reg_firm_cpa_no_ph: 'CPA licence no.',
  pa_reg_firm_expired: 'Expired — this registration expired on {{date}}.',
  pa_reg_firm_expiring: 'Expires {{date}} — within 60 days.',
  pa_reg_sec_incidents_title: 'Lost or damaged books or documents (ss.15-16)',
  pa_reg_sec_incidents_sub: 'Notified within 15 days of discovery. An unexplained loss is presumed intentional.',
  pa_reg_sec_cess_title: 'Ceasing business (s.17)',
  pa_reg_sec_cess_sub: 'The books go to the DBD within 90 days, extendable to 180.',
  pa_reg_sec_insp_title: 'Inspector\'s demand (ss.22, 24, 25)',
  pa_reg_sec_insp_sub: 'Record the demand, and build the books, vouchers and code manual as one pack.',
  pa_reg_sec_chg_title: 'Registrable changes (14 days)',
  pa_reg_sec_chg_sub: 'Capital, articles, directors; and a liquidation\'s three-month reporting cycle.',
  pa_reg_sec_dr_title_gloss: 'Directors\' report (CCC s.1198)',
  pa_reg_sec_dr_sub: 'Presented to the meeting with the balance sheet. No form is prescribed.',
  pa_reg_sec_stock_title: 'Closing stock, lower of cost or market (s.65 bis)',
  pa_reg_sec_stock_sub: 'FIFO or weighted average, locked once used. (Value in − value out is not a cost formula.)',
  pa_reg_sec_vat_title: 'VAT registration threshold 1.8 ล้าน',
  pa_reg_sec_vat_sub: 'Rolling twelve months of revenue against 1,800,000 baht.',
  pa_reg_sec_firm_title_gloss: 'The firm\'s own TFAC registration (APA s.11)',
  pa_reg_sec_firm_sub: 'About {{brand}}, not a client: registration, guarantee, and the CPA who binds it.',

  // ── TaxLedgers — the six tax ledgers ──────────────────────────────────────────
  pa_tax_channel_label: 'Files',
  pa_tax_channel_online: 'Internet (ยื่นออนไลน์)',
  pa_tax_channel_paper: 'Paper (ยื่นกระดาษ)',
  pa_tax_channel_deadlines_paper: 'Monthly returns are due the 7th (VAT the 15th); PND.50 is 150 days, PND.51 two months — no internet extension.',
  pa_tax_channel_deadlines_online: 'Monthly returns are due the 15th (VAT the 23rd); PND.50 is 158 days, PND.51 two months and eight days.',
  pa_tax_fx_rule_1: 's.65 bis (5) — year-end balances convert at the Bank of Thailand rate, on one method applied consistently. There is no rate source in this system: rates are imported, and a balance with no rate on or before the year end is ',
  pa_tax_fx_rule_not: 'not',
  pa_tax_fx_rule_2: ' converted at anything.',
  pa_tax_fx_method_label: 'Method',
  pa_tax_fx_other_method_prompt: 'What is the approved method, and who approved it?',
  pa_tax_fx_method_none: '— not chosen —',
  pa_tax_fx_no_method_warn: 'No method is locked. s.65 bis (5) requires one method applied consistently, so nothing will be revalued until it is chosen — and it cannot be changed once a period has been revalued on it.',
  pa_tax_fx_rate_label: 'rate',
  pa_tax_fx_kind_avg: 'BOT average',
  pa_tax_fx_kind_buy: 'BOT buying',
  pa_tax_fx_kind_sell: 'BOT selling',
  pa_tax_fx_source_ph: 'where it came from',
  pa_tax_fx_rate_stored: 'Rate stored.',
  pa_tax_fx_nothing_imported: 'Nothing imported.',
  pa_tax_fx_add_rate: 'Add rate',
  pa_tax_fx_account_none: '— account —',
  pa_tax_fx_amount_fx_ph: 'amount in currency',
  pa_tax_fx_booked_ph: '$t(cur_word) in the books',
  pa_tax_fx_add_balance: 'Add balance',
  pa_tax_fx_th_account: 'Account',
  pa_tax_fx_th_cur: 'Cur',
  pa_tax_fx_th_amount: 'Amount',
  pa_tax_fx_th_booked: 'In the books',
  pa_tax_fx_th_rate: 'Rate',
  pa_tax_fx_th_at_rate: 'At the rate',
  pa_tax_fx_th_diff: 'Gain / (loss)',
  pa_tax_fx_empty: 'No foreign-currency balances recorded for this period.',
  pa_tax_fx_revalued_msg: 'Draft entry {{doc}} — net {{amount}} · balances revalued: {{n}}. Review it with the other drafts before posting.',
  pa_tax_fx_revalue_btn: 'Revalue {{amount}}',
  pa_tax_rem_warn_1: 'This system holds ',
  pa_tax_rem_warn_norate: 'no rate',
  pa_tax_rem_warn_2: ' for either form, and no statutory basis for them. The domestic ท.ป.4/2528 rates are a different provision and must not be used here — every line carries the rate the adviser gives it, and the line records who entered it.',
  pa_tax_rem_lines: '{{n}} lines',
  pa_tax_rem_base: 'base {{amount}}',
  pa_tax_rem_tax: 'tax',
  pa_tax_rem_due: 'due {{date}}',
  pa_tax_rem_filed: 'filed',
  pa_tax_rem_payee_ph: 'payee',
  pa_tax_rem_country_ph: 'country',
  pa_tax_rem_nature_ph: 'what for',
  pa_tax_rem_base_ph: 'baht base',
  pa_tax_rem_rate_ph: 'rate %',
  pa_tax_rem_basis_ph: 'statutory basis',
  pa_tax_rem_add: 'Add',
  pa_tax_rem_th_paid: 'Paid',
  pa_tax_rem_th_payee: 'Payee',
  pa_tax_rem_th_for: 'For',
  pa_tax_rem_th_base: 'Base',
  pa_tax_rem_th_rate: 'Rate',
  pa_tax_rem_th_tax: 'Tax',
  pa_tax_rem_th_basis: 'Basis',
  pa_tax_rem_not_recorded: 'not recorded',
  pa_tax_rem_empty: 'Nothing on this month\'s return.',
  pa_tax_rem_filed_prompt: 'Remitted on (YYYY-MM-DD)?',
  pa_tax_rem_mark_filed: 'Mark filed',
  pa_tax_stamp_rule: 'ss.104 and 118 — an instrument in the Schedule must be fully stamped, and an unstamped one cannot be used in evidence until duty and surcharge are paid. As the contractor under its own engagement contracts the firm owes 1 baht per 1,000 — 0.1% — on them.',
  pa_tax_stamp_party_ph: 'counterparty',
  pa_tax_stamp_base_ph: 'rent / fee / principal',
  pa_tax_stamp_pick_none: '— which figure? —',
  pa_tax_baht_amount: '{{amount}} baht',
  pa_tax_stamp_one_act: ' (one act)',
  pa_tax_stamp_several_acts: ' (several acts)',
  pa_tax_stamp_vat_exempt: 'VAT on this amount (exempt)',
  pa_tax_stamp_record: 'Record',
  pa_tax_stamp_schedule_head: 'ลักษณะแห่งตราสาร',
  pa_tax_stamp_number_inferred: ' (number inferred — the source gives the seven as a set)',
  pa_tax_stamp_band: ' · the schedule gives {{min}}–{{max}} baht, a range and not a formula',
  pa_tax_stamp_th_date: 'Date',
  pa_tax_stamp_th_instrument: 'Instrument',
  pa_tax_stamp_th_party: 'Party',
  pa_tax_stamp_th_base: 'Base',
  pa_tax_stamp_th_duty: 'Duty',
  pa_tax_stamp_th_stamped: 'Stamped',
  pa_tax_stamp_not_established: 'not established',
  pa_tax_stamp_exempt: 'exempt',
  pa_tax_stamp_unpaid: 'no',
  pa_tax_stamp_method_prompt: 'Paid by adhesive, impressed or cash?',
  pa_tax_stamp_paid_on_prompt: 'Paid on (YYYY-MM-DD)?',
  pa_tax_stamp_duty_prompt: 'Duty paid (baht)?',
  pa_tax_stamp_receipt_prompt: 'อ.ส. receipt number?',
  pa_tax_stamp_mark_btn: 'Stamped',
  pa_tax_stamp_empty: 'Nothing on the register.',
  pa_tax_stamp_unstamped_count: '{{n}} unstamped.',
  pa_tax_stamp_duty_established: 'Duty established: {{amount}}',
  pa_tax_stamp_unquantified: ' · {{n}} with no duty figure yet',
  pa_tax_loss_year_ph: 'year',
  pa_tax_loss_amount_ph: 'loss ($t(cur_word))',
  pa_tax_loss_note_ph: 'note',
  pa_tax_loss_record: 'Record loss',
  pa_tax_loss_years_prompt: 'Carry-forward period, in years?',
  pa_tax_loss_basis_prompt: 'Which provision does that come from? (required)',
  pa_tax_loss_set_period: 'Set the period…',
  pa_tax_loss_th_year: 'Year',
  pa_tax_loss_th_loss: 'Loss',
  pa_tax_loss_th_used: 'Used',
  pa_tax_loss_th_left: 'Left',
  pa_tax_loss_th_expires: 'Expires',
  pa_tax_loss_th_used_in: 'Used in',
  pa_tax_loss_use_year_prompt: 'Used in which year?',
  pa_tax_loss_use_amount_prompt: 'How much of it ($t(cur_word))?',
  pa_tax_loss_use_btn: 'Use…',
  pa_tax_loss_total: 'Total',
  pa_tax_loss_empty: 'No losses on the register.',
  pa_tax_loss_unreconciled: 'The recorded totals and the sum of the individual uses disagree. Nothing here is safe to file until that is sorted out.',
  pa_tax_rc_no_threshold: 'No threshold is recorded, so this check will not run. s.105 gives only a ceiling — 1,000 baht for a VAT/SBT registrant, 10,000 otherwise — and the Director-General\'s notified amount is not held here. Record it, or record 0 to mean every payment, which is the safe reading while it is unknown.',
  pa_tax_rc_registrant_prompt: 'VAT/SBT registrant? type vat_sbt or other',
  pa_tax_rc_threshold_prompt: 'Threshold in baht (0 = every payment; ceiling {{ceiling}})',
  pa_tax_rc_notification_prompt: 'Director-General notification number, if the office has it',
  pa_tax_rc_change_threshold: 'Change the threshold…',
  pa_tax_rc_record_threshold: 'Record the threshold…',
  pa_tax_rc_registrant_yes: 'VAT/SBT registrant',
  pa_tax_rc_registrant_no: 'not a registrant',
  pa_tax_rc_scope_level: '{{scope}}-level',
  pa_tax_rc_th_received: 'Received',
  pa_tax_rc_th_against: 'Against',
  pa_tax_rc_th_amount: 'Amount',
  pa_tax_rc_th_desc: 'Description',
  pa_tax_rc_all_covered: 'Every matched payment in this range has a receipt behind it ({{n}} checked).',
  pa_tax_rc_daily_head: 's.105 ter daily aggregate',
  pa_tax_rc_daily_body: '{{n}} days, {{amount}} in total.',
  pa_tax_title: 'Tax ledgers',
  pa_tax_sec_fx_title: 'Exchange rates and year-end retranslation',
  pa_tax_sec_fx_sub: 's.65 bis (5) — one method, applied consistently. No rate here, no revaluation.',
  pa_tax_sec_rem_forms: 'ภ.ง.ด.54 · ภ.พ.36',
  pa_tax_sec_rem_title: 'payments abroad',
  pa_tax_sec_rem_sub: 'A register, not a rate engine: nothing here knows the rate for either form.',
  pa_tax_sec_stamp_title: 'Stamp duty',
  pa_tax_sec_stamp_sub: 'The seven schedule rates a law or accounting firm meets, and what is not established.',
  pa_tax_sec_loss_title: 'Loss carry-forward register',
  pa_tax_sec_loss_sub: 'One row per loss-making year, with no expiry claimed until the period is recorded.',
  pa_tax_sec_rc_doc: 'ใบรับ',
  pa_tax_sec_rc_title: 'Receipt issued on every payment (s.105)',
  pa_tax_sec_rc_sub: 'Money in with no receipt behind it. Refuses to run until the threshold is recorded.',

};

export const acctZh: Record<string, string> = {
  pa_sb_period_label: '会计期间',
  pa_sb_open_books_gloss: '开立会计期间',
  pa_sb_open_books_why: '会计法要求每十二个月结账一次,因此必须先有会计期间才能过账。按常规十二个月办理时,结束日期留空;第一个期间可以短于十二个月,此后任何不足或超过十二个月的期间,都须先登记会计监督官的书面许可。开立时会把事务所的会计科目表一并复制进来。',
  pa_sb_from: '开始',
  pa_sb_to: '结束',
  pa_sb_optional: '可不填',
  pa_sb_open_period: '开立期间',
  pa_sb_open_next: '开立下一期间',
  pa_sb_status_closed: '已结账',
  pa_sb_n_entries_other: '{{count}} 笔分录',
  pa_sb_nothing_posted: '本期间还没有任何过账 —— 先在上方生成待过账分录,核对后过账。',

  pa_sb_close_gloss: '结账…',
  pa_sb_close_confirm: '结账不可撤销。本期损益将转入未分配利润,结转余额随之固定,并对本期间的凭证启动五年保存期限。确定继续?',
  pa_sb_close_sum: '收入 {{income}} − 费用 {{expense}} =',
  pa_sb_profit: '利润',
  pa_sb_loss: '亏损',
  pa_sb_close_drafts_other: '还有 {{count}} 笔待过账分录 —— 请先处理完。',
  pa_sb_close_outofrange_other: '有 {{count}} 笔已过账分录落在本期间之外。',
  pa_sb_close_unbalanced: '本期间借贷不平。',
  pa_sb_close_payroll: '工资单 {{docNos}} 已批准但未过账,其期间结束日在本会计期间内。请先在 工资 › 工资单 中过账,再结账 —— 已结账的期间不能再写入任何东西。',
  pa_sb_close_it: '确认结账',
  pa_sb_closed_detail: '{{entries}} 笔分录 · {{result}} {{amount}} · 结转余额 {{carried}} 项 · {{vouchers}} 张凭证已标记五年保存。',

  pa_sb_h_journals: '日记账',
  pa_sb_h_ledger: '总账与试算表',
  pa_sb_h_statements: '财务报表',
  pa_sb_h_extras: '现金流量表、附注、存货与增值税',

  pa_sb_c_gl: '总账',
  pa_sb_c_tb: '试算表',
  pa_sb_c_ar: '应收明细账',
  pa_sb_c_ap: '应付明细账',
  pa_sb_c_stock: '存货账 —— 仅商品类客户',
  pa_sb_c_goods: '商品及原材料报表',
  pa_sb_c_vatout: '销项税报表 —— 本月',
  pa_sb_c_vatin: '进项税报表 —— 本月',

  pa_sb_pl_line: '收入 {{income}} − 费用 {{expense}}',
  pa_sb_pl_gross_line: '销售额 {{sales}} − 销货成本 {{cost}}',
  pa_sb_pl_gross_unplaced: '（{{n}} 个科目未归类——未计入 {{amount}}）',
  pa_sb_assets_line: '资产 {{amount}}',
  pa_sb_unbalanced_why: '资产不等于负债加所有者权益',

  pa_fs_bound_what: '财务报表合订本:封面、带页码的目录、连续页码',
  pa_fs_bound_build: '生成合订本',
  pa_fs_bound_building: '正在装订…',
  pa_fs_cover_issues: '将印在封面上',
  pa_fs_pick_least: '请至少选择一张报表。',
  pa_fs_sfp: '资产负债表',
  pa_fs_pl: '损益表',
  pa_fs_sce: '所有者权益变动表',
  pa_fs_cf: '现金流量表',
  pa_fs_notes: '财务报表附注',
  pa_fs_wa: '营业账 —— 随 ภ.ง.ด.50 一并报送',

  pa_sb_foot_books: '账簿以泰文打印,封面载明 {{client}}、账簿种类及册次,并连续编页 —— 即商业发展厅 2544 年公告所规定的格式。',
  pa_sb_foot_statements: '报表依权责发生制自上述账簿编制。尚未按商业发展厅 2566 年公告规定整理为简式项目(รายการย่อ),因此不是向商业发展厅报送的格式 —— 该对应关系有待公告正式条文。',

  // ── AccountingBooks — the client picker ───────────────────────────────────────
  pa_books_all_clients: '全部客户',
  pa_books_title: '法定账簿',
  pa_books_pinned_missing_title: '这个登录对应的客户读不到',
  pa_books_pinned_missing_body: '它只绑定一家客户,而这家客户不在该登录可读的名单里。请让事务所在 Admin → Users 里核对绑定。',
  pa_books_intro: '会计科目表、会计期间、五本日记账、总账、年度结账和财务报表。所有记账客户都会列出 — 包括尚无发票的客户，因为新设立的公司正是最需要开立会计期间的那一家。',
  pa_books_search_ph: '搜索客户…',
  pa_books_empty: '没有匹配的记账客户。',
  pa_books_drafts_pill: '{{n}} 草稿',
  pa_books_no_period: '尚未开账',
  pa_books_pill_closed: '已结账',
  pa_books_pill_open: '未结账',
  pa_books_card_counts: '{{accounts}} 个会计科目 · {{entries}} 笔已过账分录',
  pa_books_card_vat: '增值税',

  // ── Bookkeeping — the invoice-line-item books ─────────────────────────────────
  pa_bk_mon_jan: '1月',
  pa_bk_mon_feb: '2月',
  pa_bk_mon_mar: '3月',
  pa_bk_mon_apr: '4月',
  pa_bk_mon_may: '5月',
  pa_bk_mon_jun: '6月',
  pa_bk_mon_jul: '7月',
  pa_bk_mon_aug: '8月',
  pa_bk_mon_sep: '9月',
  pa_bk_mon_oct: '10月',
  pa_bk_mon_nov: '11月',
  pa_bk_mon_dec: '12月',
  pa_bk_individual: '自然人',
  pa_bk_item_remove: '删除',
  pa_bk_zero_rate: '0%',
  pa_bk_loading: '加载中…',
  pa_bk_books_link: '法定账簿',
  pa_bk_books_link_title: '会计科目表、日记账、总账、结账与财务报表',
  pa_bk_err_items_load: '无法加载明细',
  pa_bk_err_not_saved: '未保存',
  pa_bk_err_add: '无法添加',
  pa_bk_err_delete: '无法删除',
  pa_bk_err_clear: '无法清空明细',
  pa_bk_err_regen: '重新识别失败',
  pa_bk_err_month_load: '无法加载本月',
  pa_bk_err_photo: '无法加载图片',

  // ── EntityInfo — juristic form and dividends ──────────────────────────────────
  pa_entity_form_not_recorded: '法人形式未记录',
  pa_entity_unknown_why: '本客户是否依 s.8 有记账义务、其财务报表适用哪一种 DBD 表单、以及能否免予审计,全都取决于这一项。',
  pa_entity_must_keep: '有记账义务 (ม.8)',
  pa_entity_not_required: '非记账义务人',
  pa_entity_dbd_form: 'DBD แบบ {{n}}',
  pa_entity_reg_capital_val: '注册资本 {{amount}}',
  pa_entity_registered_on_val: '注册日 {{date}}',
  pa_entity_dividend: '股利',
  pa_entity_f_form: '法人形式',
  pa_entity_f_form_select: '— 请选择 —',
  pa_entity_form_dbd_suffix: '(แบบ {{n}})',
  pa_entity_f_capital: '注册资本 ($t(cur_unit))',
  pa_entity_f_registered_on: '注册日期',
  pa_entity_div_law: 'ประมวลกฎหมายแพ่งและพาณิชย์ ม.1201–1202 — 股利只能从利润中支付,存在累计亏损时一律不得支付;并须将其中至少 5% 拨入法定公积金,直至该公积金达到注册资本的十分之一。股利须自决议之日起一个月内付清。',
  pa_entity_div_amount: '金额 ($t(cur_unit))',
  pa_entity_div_resolution_date: '决议日期',
  pa_entity_div_payable_account: '应付股利科目',
  pa_entity_div_retained: '未分配利润 {{amount}}',
  pa_entity_div_reserve: '法定公积金 {{amount}}',
  pa_entity_div_ceiling: '上限 {{amount}}',
  pa_entity_div_appropriation: '拨入法定公积金 {{amount}}',
  pa_entity_div_create: '创建草稿',
  pa_entity_div_created: '{{doc}} 已创建为草稿。',
  pa_entity_div_created_appr: '{{doc}} 已创建为草稿,其中 {{amount}} 拨入法定公积金。',

  // ── DraftEntries — generate, review, post ─────────────────────────────────────
  pa_jnl_cash: 'เงินสด',
  pa_jnl_bank: 'ธนาคาร',
  pa_jnl_purchase: 'รายวันซื้อ',
  pa_jnl_sale: 'รายวันขาย',
  pa_jnl_general: 'รายวันทั่วไป',
  pa_draft_title: '待过账分录',
  pa_draft_sub_pending: '在你过账之前,这里的内容都不在账簿里。',
  pa_draft_sub_empty: '来自增值税明细账 — 已记录的销项与进项单据。',
  pa_draft_generate: '从明细账生成',
  pa_draft_stale_btn: '重建 {{n}} 条过期',
  pa_draft_stale_btn_title: '重建那些单据已变更的草稿——只重建没人动过的，其余会列出来由你处理。',
  pa_draft_stale_rebuilt: '已按单据重建 {{n}} 条',
  pa_draft_stale_kept: '留给你处理 {{n}} 条（有人动过）',
  pa_draft_stale_withdrawn: '清除 {{n}} 条——其单据已被撤回',
  pa_draft_stale_r_line: '本草稿所依据的单据之后已被修改。请作废本草稿并重新生成，使账簿与单据现状一致。',
  pa_draft_stale_r_items: '本草稿所依据单据的明细行之后已被修改。请作废本草稿并重新生成，使账簿与单据现状一致。',
  pa_draft_stale_r_both: '本草稿所依据的单据及其明细之后均已被修改。请作废本草稿并重新生成。',
  pa_draft_stale_r_gone: '本草稿所依据的单据已被撤回——重新入账、作废或删除。请作废本草稿：过账会把一张已不存在的单据记入账簿。',
  pa_draft_stale_r_vouchers: '本草稿生成之后，又过账了一张同月、同方向的手工凭证。请作废本草稿并重新生成——届时会请您逐张确认那张凭证。',
  pa_draft_stale_r_prepaid: '本草稿所属收据的客户预扣税，现在由该收据其他银行流水的分录记账的情况已经改变——已由另一张分录记账，或已没有分录记账。请作废本草稿并重新生成，使预扣税恰好只记一次。',
  pa_draft_stale_r_prepaid_wait: '本草稿把所属收据的客户预扣税留给该收据的另一张分录记账，而那张分录仍是草稿（悬停查看是哪一张）——请先过账那一张，再过账本草稿。',
  pa_draft_stale_r_vouchers_named: '本草稿生成之后，又过账了同月、同方向的手工凭证（{{docNos}}），且尚未有人确认。请作废本草稿并重新生成——届时会请您逐张确认。',
  pa_draft_stale_r_prepaid_twice: '收据 {{receipt}} 的客户预扣税已由 {{by}} 记账——请作废本草稿并重新生成，以免预扣税记两次。',
  pa_draft_stale_r_prepaid_nowhere: '收据 {{receipt}} 的客户预扣税没有任何分录记账——本草稿不记，该收据其他分录也不记。请作废并重新生成，使预扣税恰好记一次。',
  pa_draft_stale_r_prepaid_part: '收据 {{receipt}} 的客户预扣税只记了一部分——{{by}} 记了 {{part}}——本草稿一分未记。请作废并重新生成，届时会告诉您其余部分如何记账。',
  pa_draft_stale_r_prepaid_unclear: '收据 {{receipt}} 的客户预扣税可能已由 {{by}} 记账，但其流水匹配到的发票有两张以上带预扣的收据，无法判断。请作废本草稿，把那条流水改匹配到对应收据，再重新生成。',
  pa_draft_stale_r_prepaid_leaving: '本草稿把收据 {{receipt}} 的客户预扣税留给 {{by}}，而它的冲销 {{rv}} 已开出——{{rv}} 一过账，就没有分录记预扣税了。请先过账 {{rv}}，再作废本草稿并重新生成；或作废 {{rv}}。',
  pa_draft_stale_r_prepaid_wait_named: '本草稿把收据 {{receipt}} 的客户预扣税留给 {{by}} 记账，而它仍是草稿——请先过账那一张，再过账本草稿。',
  pa_draft_stale_not_rebuilt: '{{n}} 条无法重建，已原样保留：{{list}}',
  pa_draft_stale_other: '另为尚无草稿的单据新建 {{n}} 条',
  pa_draft_hide: '收起',
  pa_draft_review: '查看核对',
  pa_draft_post_all: '全部过账 {{n}} 笔',
  pa_draft_post: '过账',
  pa_draft_discard_title: '丢弃这条待过账分录',
  pa_draft_discard_confirm: '丢弃 {{docNo}} 的待过账分录?明细账那一行会保留 — 可以再生成一次。',
  pa_draft_discard_confirm_reversal: '丢弃冲销分录 {{docNo}}?{{reverses}} 仍在账上，直到再次冲销。',
  pa_draft_reversal_note: '逐行冲回 {{reverses}}：科目和各行就是 {{reverses}} 的，只是借贷对调，所以这里不能改科目，也不能拆行。',
  pa_draft_reversal_hover: '（悬停查看如何改用其他方式入账）',
  pa_draft_reversal_way_own: '照原样过账 {{rv}}，再另录一张分录记正确的金额。',
  pa_draft_reversal_way_putBack: '过账 {{rv}} 会把 {{orig}} 原样放回账簿；如要改用其他方式入账，请作废 {{rv}}，另录一张分录记正确的金额。',
  pa_draft_reversal_way_asset: '照原样过账 {{rv}}——过账时资产登记会一并恢复。然后重新运行，并照原样过账新草稿（其科目取自资产登记——如要改用其他方式入账，请先在资产登记中更正该资产），切勿另录分录。',
  pa_draft_reversal_way_held: '{{orig}} 的来源已重新生成为 {{held}}。请照原样过账 {{rv}}，然后在 {{held}} 上入账：单据生成的草稿只可为非往来、非增值税、非现金的行选择科目或拆行；结算草稿照原样过账（其科目取自匹配与过账科目设置）。切勿另录分录。',
  pa_draft_reversal_way_heldPosted: '{{orig}} 的来源已再次入账为 {{held}}。请照原样过账 {{rv}}；如要改用其他方式入账，请冲销 {{held}}、过账该冲销、重新生成分录，并在新草稿上入账（单据生成的草稿只可为非往来、非增值税、非现金的行选择科目；结算草稿取自匹配与过账科目设置）。',
  pa_draft_reversal_way_heldBlocked: '{{orig}} 的来源已重新生成为 {{held}}，但它已无法过账——请先在 Journal 作废 {{held}}，再照原样过账 {{rv}}，并按来源现状入账。',
  pa_draft_reversal_way_gone: '照原样过账 {{rv}}：{{orig}} 的来源已不再成立（已作废、已删除、已冲回或由工资占用），不会再被入账。',
  pa_draft_reversal_way_now: '照原样过账 {{rv}}——之后 {{orig}} 的来源即可重新生成。请重新生成，并在新草稿上入账：单据生成的草稿只可为非往来、非增值税、非现金的行选择科目或拆行；结算草稿照原样过账（其科目取自匹配与过账科目设置）。切勿另录分录。',
  pa_draft_reversal_way_fix: '照原样过账 {{rv}}，先改正使「生成」不能处理 {{orig}} 来源的问题，再重新生成，并在新草稿上入账（单据生成的草稿只可为非往来、非增值税、非现金的行选择科目或拆行；结算草稿照原样过账）。',
  pa_draft_reversal_way_later: '照原样过账 {{rv}}；{{orig}} 的来源日期在开放期间之后——请依次开启各期间直到涵盖该日期的期间，在那里生成，并在新草稿上入账（单据生成的草稿只可为非往来、非增值税、非现金的行选择科目；结算草稿照原样过账）。',
  pa_draft_reversal_way_redate: '照原样过账 {{rv}}，在税务模块改正 {{orig}} 来源的日期并重新生成——若正确日期落在已结账期间，则以「为该来源手工记录」的分录记录。',
  pa_draft_reversal_way_byHand: '照原样过账 {{rv}}，并以「为 {{orig}} 的来源手工记录」的分录记正确金额（在「手工分录 › 冲销」中点 Record by hand）：该分录会占住来源，「生成」不会再次入账。',
  pa_draft_made_for: '为{{what}}手工记录。',
  pa_draft_edit: '编辑',
  pa_draft_edit_title: '编辑草稿分录',
  pa_edit_date: '日期',
  pa_edit_book: '账簿',
  pa_edit_bank_acct: '银行账户',
  pa_edit_docno: '单据号',
  pa_edit_docno_jv: '记账凭证号（保持不变）',
  pa_edit_narration: '摘要',
  pa_edit_calcnote: '金额的计算依据',
  pa_edit_reverse_on: '自动冲回日',
  pa_edit_account: '科目',
  pa_edit_dr: '借方',
  pa_edit_cr: '贷方',
  pa_edit_partner: '往来单位',
  pa_edit_ref: '参考号',
  pa_edit_memo: '行摘要',
  pa_edit_add_line: '添加一行',
  pa_edit_remove_line: '删除此行',
  pa_edit_balanced: '借贷平衡',
  pa_edit_unbalanced: '借贷不平衡，差额 {{diff}}',
  pa_edit_save: '保存修改',
  pa_edit_saved: '{{docNo}} 已保存——仍为草稿。',
  pa_edit_why: '修改原因（可选，随本次修改保存）',
  pa_edit_jv_month: '{{docNo}} 是 {{month}} 的记账凭证号：其日期只能留在该月。如要记入其他月份，请作废后在该月重新录入。',
  pa_edit_jv_typed: '记账凭证号由系统编号——请填写外部单据本身的号码，或留空。',
  pa_edit_review_hint: '由单据生成：此处只能为非客户/供应商行、非增值税行、非现金行选择科目或拆行。其他内容请更正单据，然后刷新或重新生成。',
  pa_edit_changed_elsewhere: '打开后此草稿已在别处被修改，现已重新读取——请再做一次修改。',
  pa_edit_need_narration: '每张分录都需要摘要。',
  pa_edit_need_calc: '没有外部单据号即为内部凭证：请写明金额的计算依据。',
  pa_edit_need_bank: '银行日记账分录须注明银行账户。',
  pa_edit_need_lines: '至少两行，每行有科目并只在一方填写金额。',
  pa_edit_date_out: '日期须在此草稿的期间 {{from}} – {{to}} 之内。',
  pa_edit_reverse_inside: '冲回日须在本期间结束（{{to}}）之后。',
  pa_edit_account_switched_off: '其科目 {{code}} 已停用——请另选科目。',
  pa_edit_account_not_postable: '其科目 {{code}} 不能过账——请另选科目。',
  pa_edit_account_missing: '其科目已不在科目表中——请另选科目。',
  pa_edit_refused_again: '请先做些修改：同样的内容会再次被拒绝。',
  pa_draft_mirror_broken: '{{docNo}} 已不再逐行冲回 {{reverses}} — 请丢弃 {{docNo}}，再重新冲销 {{reverses}}。',
  pa_draft_hand_source_gone: '{{docNo}} 是为{{what}}手工记录的分录，但该来源已不存在（之后已撤回、删除或冲回）— 请丢弃 {{docNo}}：过账会把已不存在的东西记入账簿。',
  pa_draft_hand_source_held: '{{docNo}} 是为{{what}}手工记录的分录，但该来源现已由其他分录入账 — 请丢弃 {{docNo}}：过账会重复入账。',
  pa_draft_recorded_by_hand: '已手工记录：{{what}}已由为其手工记录的分录 {{by}} 占住 — 请丢弃本草稿（悬停查看另一种做法），否则两笔会重复入账。',
  pa_draft_hand_prepaid_taken: '{{docNo}} 是为{{what}}手工记录的分录，其中记的客户预扣税超过了留给它的部分——即实际预扣额减去所属收据其他分录已记的金额（悬停查看是哪一张、记了多少）。请丢弃 {{docNo}}，重新手工记录时在客户预扣税科目只记剩下的部分（若其他分录已全部记过则不记），或先把其他分录撤出账簿：过账会使预扣税记账超过实际预扣额。',
  pa_draft_hand_changed: '来源已改动：{{what}}已与为其手工记录 {{docNo}} 时不同，而手工录入的分录不会随之更新。过账前请核对 {{docNo}} 的数字——如已不再相符，请丢弃 {{docNo}} 并重新记录（在「调整与更正 › 冲销」提供「手工记录」之处手工记录；该处如说明可「从明细账生成」，则改为生成）。',
  pa_draft_post_all_hand_left: '未纳入「全部过账」：{{n}} 笔手工记录的分录，其来源在记录后已有改动——请逐笔核对数字，并在各自的卡片上过账。',
  pa_draft_post_many_hand_changed: '{{docNo}} 未过账：其来源在手工记录后已有改动。请核对其数字并在它自己的卡片上过账，或丢弃后重新记录。',
  pa_draft_gen_created: '已生成待过账分录 {{n}} 笔',
  pa_draft_gen_skipped: '已有待过账分录 {{n}} 笔',
  pa_draft_gen_by_hand: '已手工记录、由其手工分录入账 {{n}} 笔({{list}})',
  pa_draft_gen_needs_account: '需选择科目 {{n}} 笔',
  pa_draft_gen_blocked: '拒绝生成 {{n}} 行 — {{list}}',
  pa_draft_gen_blocked_more: ' …另有 {{n}} 条',
  pa_draft_no_doc_no: '(无编号)',
  pa_draft_posted_single: '已过账。',
  pa_draft_posted_n: '已过账 {{n}} 笔',

  // ── SplitLeg — 手工把一腿拆到多个科目,以及把科目记住 ──────────────────────────────
  pa_split_btn: '拆分',
  pa_split_btn_title: '把这一腿拆到多个科目',
  pa_split_head: '把 {{amount}} 拆到多个科目',
  pa_split_now_on: '现在挂在 {{account}}',
  pa_split_account: '科目',
  pa_split_amount: '金额',
  pa_split_choose: '— 选择科目 —',
  pa_split_add_row: '加一行',
  pa_split_remove_row: '删掉这一行',
  pa_split_even: '平均拆分',
  pa_split_carries_odd: '这一行多担 {{amount}}',
  pa_split_remaining: '还差 {{amount}} 未分配',
  pa_split_over: '比这一腿多出 {{amount}}',
  pa_split_balanced: '各行合计正好等于这一腿。',
  pa_split_pin_why: '各行必须合计为 {{amount}},也就是这一腿本身的金额,否则分录就不平 — 服务器在写入之前还会再核一次。',
  pa_split_draft_only: '这只改这条待过账分录;在你过账之前什么都没进账簿。',
  pa_split_save: '保存拆分',
  pa_split_cancel: '取消',
  pa_split_bad_amount: '第 {{n}} 行不是金额。',
  pa_split_dup: '有两行指向同一个科目 — 请并成一行。',
  pa_split_need_account: '每一行都要选科目。',
  pa_split_saved: '拆分已保存,没有过账。',
  pa_split_group_other: '拆成 {{count}} 行',
  pa_split_group_of: '原来挂在 {{account}} 的 {{amount}}',
  pa_split_by: '由 {{name}} 操作',
  pa_split_undo: '撤销',
  pa_split_undo_confirm: '撤销这次拆分?各行会合回 {{account}} 上的一腿。',
  pa_split_undone: '拆分已撤销 — 这一腿回到单一科目。',
  pa_rem_link: '记住',
  pa_rem_head: '下次自动用 {{account}}',
  pa_rem_party: '单据来自 {{name}}',
  pa_rem_which: '这个科目对应单据上的哪一项收费?',
  pa_rem_other: '其他…',
  pa_rem_type: '按单据上的写法填收费名称',
  pa_rem_none: '这张单据没有写收费名称,请按纸面上的写法填。',
  pa_rem_scope_client_only: '仅记住本客户',
  pa_rem_save: '记住',
  pa_rem_cancel: '取消',
  pa_rem_saved: '已记住:{{label}} → {{account}}。从下一张单据起会自动进这个科目。',
  pa_rem_need_label: '请说明这个科目对应哪一项收费。',
  pa_rem_why: '拆分只改了这一张单据。「记住」是另一个动作,只有你在这里明确点下,它才会成为以后的默认。',


  pa_chart_title: '会计科目表',
  pa_chart_download: '下载 PDF',
  pa_chart_download_failed: 'PDF 生成失败。',
  pa_chart_counts: '{{n}} 个科目 · {{used}} 个已使用',
  pa_chart_empty: '尚无科目 — 先复制事务所模板。',
  pa_chart_copy_template: '复制事务所模板',
  pa_chart_sync_template: '从模板同步',
  pa_chart_sync_title: '预览本科目表尚未包含的模板科目;由管理员添加勾选的科目',
  pa_chart_add_account: '添加科目',
  pa_chart_open: '展开',
  pa_chart_hide: '收起',
  pa_chart_ph_code: '科目代码',
  pa_chart_ph_name_th: '科目名称(泰文)',
  pa_chart_ph_name_en: '科目名称(英文)',
  pa_chart_save: '保存',
  pa_chart_cancel: '取消',
  pa_chart_switch_off: '停用',
  pa_chart_switch_on: '启用',
  pa_chart_name_th_manual: '泰文名称即 ม.21(1) 所要求的科目代码对照手册 — 必填',
  pa_chart_template_complete: '事务所模板里的科目都已经在这本科目表里了。',
  pa_chart_remove_confirm: '删除科目 {{code}} {{name}}?',
  pa_chart_class_asset: '资产',
  pa_chart_class_liability: '负债',
  pa_chart_class_equity: '所有者权益',
  pa_chart_class_income: '收入',
  pa_chart_class_expense: '费用',
  pa_chart_cf_prefixed: 'งบกระแสเงินสด: {{section}}',
  pa_chart_cf_none: '— 未选择 —',
  pa_chart_cf_operating: '经营活动',
  pa_chart_cf_investing: '投资活动',
  pa_chart_cf_financing: '筹资活动',
  pa_chart_cf_cash: '现金及现金等价物',
  pa_chart_cf_noncash: '非现金项目',
  pa_chart_cf_missing_warn: '⚠️ 资产/负债/权益科目若不选现金流分类,现金流量表会对不平(差额正好等于走过这个科目的金额)。',
  pa_chart_cf_missing_title: '现金流量表会对不平,直到这个科目选了分类',
  pa_chart_cf_missing_pill: '未选 งบกระแสเงินสด 分类',
  pa_chart_fs_prefixed: 'บัญชีทำการ: {{section}}',
  pa_chart_fs_none: '— 未选择 —',
  pa_chart_fs_trading: '计入 — 销货收入/销货成本',
  pa_chart_fs_other: '不计入 — 列在毛利之下',
  pa_chart_fs_missing_title: '这个科目有金额但还没有归类,บัญชีทำการ 会把它单独列出,不计入合计',
  pa_chart_fs_missing_pill: '未选 บัญชีทำการ 分类',
  pa_chart_wd_title: '存货已减值 — 这笔金额显示在哪里?',
  pa_chart_wd_account: '{{code}} {{name}} · {{amount}}',
  pa_chart_wd_q: '这家客户有存货减值。请选择这笔金额在 บัญชีทำการ 上显示的位置。',
  pa_chart_wd_trading: '计入销货成本之内',
  pa_chart_wd_trading_hint: 'บัญชีทำการ 会把这笔算进去,那张表上的毛利因此减少同样的金额。',
  pa_chart_wd_other: '列在毛利之下',
  pa_chart_wd_other_hint: 'บัญชีทำการ 不把这笔算进去,那张表上的毛利因此高出同样的金额。',
  pa_chart_wd_unanswered: '在有人回答之前,บัญชีทำการ 会把这个科目连同金额一并列出并提示,两边的合计都不包含它。',
  pa_chart_wd_note: '两种选择都不改动账簿里的任何数字,只改变这笔金额显示在哪里。这是按这家客户自己的财务报告框架作出的选择,系统不替它决定。每家客户只记一次,以后不再询问。',
  pa_chart_wd_failed: '没能记下。什么都没有改动。',

  // ── CompliancePanel — Accounting Act records ──────────────────────────────────
  pa_compliance_title: '审计与 DBD 报送',
  pa_compliance_file_by: '报送截止日 {{date}}',
  pa_compliance_due_unknown: '报送截止日尚未确定',
  pa_compliance_record_meeting_audit: '登记股东大会与审计',
  pa_compliance_record_filing: '登记报送',
  pa_compliance_audit_unknown: '⚠ 是否必须审计尚未确定',
  pa_compliance_audit_required: '必须由注册会计师 (CPA) 审计',
  pa_compliance_audit_exempt: '获得审计豁免',
  pa_compliance_unbacked: '{{n}} 笔分录没有凭证、没有编号、也没有说明依据 — 不能过账 (ข้อ 5)。',
  pa_compliance_filed_msg: '已登记 {{kind}} 于 {{date}} 报送。',
  pa_compliance_agm_due: '股东大会截止日 {{date}}',
  pa_compliance_agm_held: '已于 {{date}} 召开',
  pa_compliance_agm_not_held: '尚未召开',
  pa_compliance_statements_due: '{{date}} 前将财务报表送达股东',
  pa_compliance_bojo5_due: '{{date}} 前报送 บอจ.5',
  pa_compliance_filed_label: '已报送:',
  pa_compliance_bookkeeper_label: '记账人:',
  pa_compliance_tfac: '(TFAC {{no}})',
  pa_compliance_tfac_lapsed: '会员资格已失效',
  pa_compliance_no_bookkeeper: '尚未委任记账人 (Accounting Act s.19)',
  pa_compliance_eacct_pending: '尚未在 e-Accountant 申报 {{name}}(自 {{date}} 起 30 天内)',
  pa_compliance_notified_today: '今日已申报',
  pa_compliance_f_agm_date: '股东大会日期',
  pa_compliance_f_statements_sent: '财务报表送达股东',
  pa_compliance_f_audit_completed: '审计完成',
  pa_compliance_f_opinion: '审计意见',
  pa_compliance_opinion_unqualified: '无保留意见',
  pa_compliance_opinion_qualified: '保留意见',
  pa_compliance_opinion_adverse: '否定意见',
  pa_compliance_opinion_disclaimer: '无法表示意见',
  pa_compliance_f_auditor: '审计师',
  pa_compliance_f_auditor_license: '注册编号',
  pa_compliance_f_auditor_kind: '类别',
  pa_compliance_auditor_cpa: '注册会计师 (CPA)',
  pa_compliance_auditor_ta: '税务审计师 (TA)',
  pa_compliance_f_audit_fee: '审计费',
  pa_compliance_save: '保存',
  pa_compliance_f_kind: '报送内容',
  pa_compliance_kind_fs: 'งบการเงิน',
  pa_compliance_kind_fs_gloss: '财务报表',
  pa_compliance_kind_bojo5: 'บอจ.5',
  pa_compliance_kind_bojo5_gloss: '股东名册',
  pa_compliance_f_filed_on: '报送日期',
  pa_compliance_f_receipt_no: 'เลขที่ใบรับ · 收据编号',
  pa_compliance_record: '登记',

  // ── FixedAssets — the asset register ──────────────────────────────────────────
  pa_assets_register_name: 'ทะเบียนทรัพย์สิน',
  pa_assets_title: '固定资产',
  pa_assets_summary: '{{n}} 项固定资产 · 账面净值 {{nbv}}',
  pa_assets_empty: '登记簿是空的。',
  pa_assets_due: '本期应计提折旧 {{amount}}',
  pa_assets_open_register: '登记簿',
  pa_assets_charge_dep: '计提折旧 {{amount}}',
  pa_assets_dep_confirm: '本期计提折旧 {{amount}}?\n\n生成的是待过账草稿 —— 请到「会计分录」里核对后再过账,过账后才会进总账。',
  pa_assets_add: '固定资产',
  pa_assets_open: '展开',
  pa_assets_hide: '收起',
  pa_assets_registered_msg: '{{no}} 已登记,折旧率每年 {{rate}}%。',
  pa_assets_dep_draft: '{{n}} 项固定资产,{{amount}} — 已生成待过账分录供核对。',
  pa_assets_dep_none: '本期所有固定资产都已计提折旧。',
  pa_assets_f_class: '类别',
  pa_assets_no_dep: '不计提折旧',
  pa_assets_f_acquired: '取得日期',
  pa_assets_f_cost: '原值',
  pa_assets_f_salvage: '残值',
  pa_assets_import_btn: '导入资产清单',
  pa_assets_import_note: '资产清单 CSV 或 .xlsx 文件：一行一项资产，第一行为列名，可用英文、泰文或中文，顺序不限。必填：“名称”（另有“英文名称”可一并填写）、“购入日期”（或“取得日期”）和“原值”（或“成本”）。选填：“资产分类”（未写的行归入此处所选的类别）、“资产类别”、“折旧率”、“残值”、“资产科目”、“累计折旧科目”和“折旧费用科目”（填科目代码）、“期初累计折旧”及其“截止日期”、“备注”、“供应商”、“发票号”、“资本化成本”、“可使用日期”、“折旧开始日期”、“存放地点”或“部门”、“状态”（“使用中”、“尚未可供使用”或“闲置”）以及“使用年限”。列名不在其中的列会被忽略，检查文件时会列出。每一行都按逐条录入的同一套规则校验：每项资产必须有分类、可折旧类别必须填折旧率且不得超过法定上限、残值必须低于成本、三个科目必须在本客户科目表内。',
  pa_assets_import_preview: '检查文件',
  pa_assets_import_commit: '确认导入',
  pa_assets_import_summary: '可登记 {{ok}} 项 · 被拒 {{refused}} 项',
  pa_assets_import_will_add: '可登记',
  pa_assets_import_ignored: '有 {{n}} 列将被忽略——其中的内容不会登记。如其中某列有用，请把列名改为上方说明所列的名称（或删除该列），然后重新检查文件。',
  pa_assets_import_done: '已登记 {{n}} 项资产。',
  pa_assets_cadence_annual: '按年度计提',
  pa_assets_cadence_monthly: '按月计提',
  pa_assets_month: '计提哪个月',
  pa_assets_pick_month: '请选择要计提的月份。',
  pa_assets_f_opening: '期初累计折旧',
  pa_assets_f_opening_as_of: '……截止日期',
  pa_assets_opening_hint: '仅用于在本系统建账之前就已投入使用的资产。若资产是在本系统建账之后购入的，两项都留空。系统将从该截止日期的次日开始计提，已在其他账套计提过的期间不会重复计提。',
  pa_assets_f_rate: '折旧率 %',
  pa_assets_f_name_th: '名称(泰文)— 必填',
  pa_assets_f_name_en: '名称(英文)',
  pa_assets_f_acct_asset: '资产科目',
  pa_assets_f_acct_accum: '累计折旧',
  pa_assets_f_acct_expense: '折旧费用',
  pa_assets_max_rate_source: '最高折旧率依据 {{source}}',

  // ── Asset categories (batch 5 round 8, H2a) ───────────────────────────────────
  pa_cat_title: '资产类别',
  pa_cat_count: '共 {{n}} 个类别',
  pa_cat_none: '尚未设置资产类别 —— 登记资产时也可以不选类别。',
  pa_cat_add: '资产类别',
  pa_cat_th_name: '类别',
  pa_cat_th_class: '法令类别 · 折旧率',
  pa_cat_th_accounts: '科目',
  pa_cat_th_assets: '资产数',
  pa_cat_edit: '修改',
  pa_cat_on: '启用',
  pa_cat_off: '停用',
  pa_cat_delete: '删除',
  pa_cat_delete_confirm: '删除类别「{{name}}」?\n\n只有从未有资产登记在该类别下时才能删除(含已处置的资产)。若只是不再使用,请改为停用。',
  pa_cat_inactive: '已停用',
  pa_cat_saved: '类别已保存。',
  pa_cat_f_name_th: '类别名称(泰文)— 必填',
  pa_cat_f_name_en: '类别名称(英文)',
  pa_cat_f_class: '法令类别(折旧率上限)',
  pa_cat_f_nature: '计提方式',
  pa_cat_f_rate: '折旧率 %',
  pa_cat_nature_depreciation: '折旧',
  pa_cat_nature_amortisation: '摊销',
  pa_cat_nature_none: '不计提(土地)',
  pa_cat_never_note: '土地只持有、不计提,因此该类别没有累计折旧科目、没有费用科目,也没有折旧率。它只能指向法令规定不可折旧的类别。',
  pa_cat_note: '类别是本客户自己的会计分组;它指向的法令类别只用来确定折旧率上限。登记资产时会把类别的三个科目和折旧率快照到该项资产上,因此以后修改类别不会影响已登记的资产。',
  pa_assets_f_category: '类别',
  pa_assets_f_category_none: '不选类别',
  pa_assets_f_category_pick: '请选择资产类别…',
  pa_assets_import_category: '未写类别的行归入',
  pa_assets_cancel: '取消',
  pa_assets_submit: '登记',
  pa_assets_th_no: '编号',
  pa_assets_th_item: '名称',
  pa_assets_th_acquired: '取得',
  pa_assets_th_cost: '原值',
  pa_assets_th_accum: '累计折旧',
  pa_assets_th_nbv: '账面净值',
  pa_assets_th_year: '本年',
  pa_assets_disposed_on: '已处置 {{date}}',
  pa_assets_days: '{{n}} 天',

  // ── ManualEntry — the general journal ─────────────────────────────────────────
  pa_manual_book_general_gloss: '普通',
  pa_manual_book_cash_gloss: '现金',
  pa_manual_book_bank_gloss: '银行',
  pa_manual_book_purchase_gloss: '采购',
  pa_manual_book_sale_gloss: '销售',
  pa_manual_title: '调整与更正',
  pa_manual_subtitle: '应收应付预提、预付费用、折旧与各项准备 — 以及冲销,这是更正已过账分录的唯一方式。',
  pa_manual_new_btn: '登记分录',
  pa_manual_reverse_btn: '冲销',
  pa_manual_sweep_title: '将更早会计期间中应在本期冲销的应收应付预提带入本期',
  pa_manual_accruals_btn: '应收应付预提',
  pa_manual_import_btn: '导入文件',
  pa_manual_import_note: '导入你方自己的会计凭证 CSV：一行一条分录行，凭证号相同的行归为一张凭证。列按名称识别——日期、凭证号、摘要、科目代码、借方、贷方（中文、泰文表头同样识别）。每张凭证都按手工录入的同一套规则校验：必须借贷平衡、科目必须在本客户科目表内、日期必须落在未结账期间内、无凭证号的内部凭证必须填写计算依据。',
  pa_manual_import_preview: '检查文件',
  pa_manual_import_commit: '确认导入',
  pa_manual_import_blocked: '文件中有凭证不能入账——请在文件里改好后重新检查。只要有一张被拒，整个文件都不会导入。',
  pa_manual_import_summary: '可入账 {{ok}} 张 · 被拒 {{refused}} 张',
  pa_manual_import_will_enter: '可入账',
  pa_manual_import_done: '已生成 {{n}} 张草稿凭证——请复核后过账。',
  pa_manual_copy_btn: '再记一笔',
  pa_manual_copy_title: '按同一张凭证在其他日期再记一笔——每月手续费、常设预提。系统会分配新的凭证号。',
  pa_manual_copy_ref_hand: '{{docNo}} 是为某一来源手工记录的分录（或冲回这样的分录），因此不能再记一笔：再记一笔会把同样的数字再记一次，却不占住任何来源。要为来源手工记录，请在本列表提供「手工记录」之处使用。',
  pa_manual_copy_ref_settlement: '{{docNo}} 是由银行流水或零用金行生成的结算分录（或冲回这样的分录），因此不能再记一笔：再记一笔会把这笔钱再记一次，却不占住任何来源。来源释放后由「生成」重新结算；「生成」不能处理的，用「手工记录」。',
  pa_manual_copy_prompt: '把 {{docNo}} 复制到哪一天？（YYYY-MM-DD）',
  pa_manual_copy_done: '已生成 {{docNo}}——请复核后过账。',
  pa_manual_late_entries: '晚于分录日期 15 天以上才过账的分录:{{n}} 笔 — 最晚的一笔迟 {{days}} 天({{docNo}})。',
  pa_manual_late_rule: 'DBD 要求日记账须在交易发生后 15 天内记录完毕。',
  pa_manual_closing_overdue: '本会计期间已于 {{date}} 结束,距今 {{days}} 天,仍未结账。账目须在 60 天内结账。',
  pa_manual_saved: '待过账分录 {{docNo}} 已生成 — 请在下方核对后过账。',
  pa_manual_reverse_confirm: '冲销 {{docNo}}?原分录仍留在账簿中并保留其页码;更正分录将作为待过账分录在其旁边生成。',
  pa_manual_reversed_msg: '{{docNo}} 已作为待过账分录生成,日期 {{date}}。',
  pa_manual_sweep_done: '已生成 {{n}} 笔应收应付预提的冲销分录(待过账)。',
  pa_manual_sweep_none: '没有更早会计期间的应收应付预提需要冲销到本期。',
  pa_manual_f_date: '日期',
  pa_manual_f_book: '日记账',
  pa_manual_f_bank_acct: '银行账号',
  pa_manual_f_bank_acct_hint: '一个账号一本账',
  pa_manual_f_bank_acct_ph: '例如 XXX-X-XXXXX-X',
  pa_manual_bank_acct_new: '另一个账号…',
  pa_manual_bank_acct_used: '已有 {{n}} 笔',
  pa_manual_bank_acct_none: '这个客户还没有银行账簿 —— 输入账号，下次就会出现在列表里。',
  pa_manual_f_docno: '单据编号',
  pa_manual_f_docno_hint: '(留空 → 自动分配 JV 编号)',
  pa_manual_f_docno_ph: 'JV-… 自动分配',
  pa_manual_f_reverse_on: '冲销日期',
  pa_manual_f_reverse_on_hint: '(应收应付预提)',
  pa_manual_narration_ph: '摘要 — 必填',
  pa_manual_calcnote_ph: '计算方法 — 没有原始凭证时必填(ข้อ 4)',
  pa_manual_th_account: '科目',
  pa_manual_th_dr: '借方',
  pa_manual_th_cr: '贷方',
  pa_manual_pick_account: '— 选择科目 —',
  pa_manual_add_line: '分录行',
  pa_manual_no_amounts: '尚未输入金额',
  pa_manual_balances: '借贷平衡',
  pa_manual_out_by: '差额 {{amount}}',
  pa_manual_save_draft: '存为草稿',
  pa_manual_reverse_note: '已过账分录不会被撤回过账。它保留原有位置与页码,更正是记在其旁边的一笔新分录 — 这正是审计轨迹的由来。',
  pa_manual_search_ph: '搜索已过账分录…',
  pa_manual_filter_by_hand: '可手工记录的',
  pa_manual_none_posted: '没有匹配的已过账分录。',
  pa_manual_reversed_by: '已冲销 {{docNo}}',
  pa_manual_rev_ref_asset: '此处不能冲销：{{docNo}} 受固定资产规则限制（悬停查看原因）。',
  pa_manual_rev_ref_asset_reversal: '{{docNo}} 已将资产登记簿恢复——请重新计提折旧或重新处置，而不是冲销它。',
  pa_manual_rev_ref_reversal_of_released: '此处不能冲销：{{docNo}} 冲回的分录来源已释放（悬停查看原因）。',
  pa_manual_rev_ref_no_open_period: '没有开放的会计期间可放 {{docNo}} 的冲销分录。',
  pa_manual_rev_ref_no_lines: '{{docNo}} 没有可冲销的行。',
  pa_manual_by_hand_btn: '手工记录',
  pa_manual_by_hand_title: '以一笔为该来源手工记录的分录作更正：该分录占住来源，分录存续期间“从明细账生成”不会再次入账。',
  pa_manual_for_tax_line: '原始单据 {{label}}',
  pa_manual_for_bank_line: '银行流水 {{label}}',
  pa_manual_prepaid_taken: '{{receipt}} 的客户预扣税已由{{by}}记账——{{way}}',
  pa_manual_prepaid_by_settlement: '银行流水 {{line}} 的结算分录',
  pa_manual_prepaid_by_hand: '{{docNo}}（为银行流水 {{line}} 手工记录的分录）',
  pa_manual_prepaid_way_discard: '请从本分录中去掉科目 {{codes}}，或先丢弃那张草稿。',
  pa_manual_prepaid_way_reverse: '请从本分录中去掉科目 {{codes}}，或先冲销那张分录并过账冲销分录。',
  pa_manual_prepaid_way_post_reversal: '请从本分录中去掉科目 {{codes}}，或先过账那张分录的冲销分录 {{rv}}。',
  pa_manual_prepaid_way_closed: '那张分录过账在已结账的会计期间，会一直留在账上——请从本分录中去掉科目 {{codes}}。',
  pa_manual_prepaid_unclear_self: '这条银行流水匹配的是发票 {{invoice}}，而该发票有不止一张带预扣税的收据（{{receipts}}）——无法判断本分录记的是哪一张收据的预扣税。请把这条流水改为匹配到对应的收据而不是发票，或从本分录中去掉科目 {{codes}}。',
  pa_manual_prepaid_unclear_other: '{{docNo}}（为匹配到发票 {{invoice}} 的银行流水 {{line}} 手工记录的分录）可能已记了 {{receipt}} 的客户预扣税——该发票有不止一张带预扣税的收据，无法判断它记的是哪一张。请先把那条流水改为匹配到对应的收据，或从本分录中去掉科目 {{codes}}。',
  pa_manual_prepaid_over: '{{receipt}} 的客户预扣税（{{withheld}}）中已有 {{booked}} 由{{by}}记账——本分录在科目 {{codes}} 借记的 {{mine}} 会使记账金额超过实际预扣：最多只能借记 {{room}}。',
  pa_manual_prepaid_over_whole: '客户在 {{receipt}} 上实际预扣 {{withheld}}——本分录在科目 {{codes}} 借记的 {{mine}} 超过了实际预扣：最多只能借记 {{room}}。',
  pa_manual_prepaid_room_hint: '如客户在这笔付款中预扣了税，这条流水可在 WHT 预付科目上记收据 {{receipt}} 其余的 {{room}}。',
  pa_manual_prepaid_by_more: '{{by}}及该收据的另外 {{n}} 张分录',
  pa_manual_prepaid_way_several: '请从本分录中去掉科目 {{codes}}，或先把其中一张分录撤出账簿。',
  pa_manual_for_cash_line: '零用金记录 {{label}}',
  pa_manual_for_banner: '为{{what}}手工记录：本分录占住它，分录存续期间“从明细账生成”不会再次入账。',
  pa_manual_row_closed_period: '已结账期间 {{from}} – {{to}}',
  pa_manual_payroll_owned: '工资单 {{docNo}} —— 在 工资 › 工资单 里从该工资单冲销,不在这里',
  pa_manual_payroll_pay_owned: '工资单 {{docNo}} 的发薪 {{payNo}} —— 在 工资 › 工资单 中该工资单的窗口里冲销,不在这里',
  pa_manual_payroll_remit_owned: '{{month}} 的汇缴 —— 在 工资 › 工资单 › 汇缴 里冲销,不在这里',

  // ── Headless — the records with no other home ─────────────────────────────────
  pa_headless_wa_rule_a: 'Revenue Code s.68 bis — 为税务目的,每家公司都要编制资产负债表、',
  pa_headless_wa_rule_b: '损益计算表',
  pa_headless_wa_rule_c: '和利润表。⚠️ 本系统未持有 DBD 的法定格式,因此按本系统自有格式打印。',
  pa_headless_wa_income: '收入',
  pa_headless_wa_cost: '销售成本',
  pa_headless_wa_gross: '毛利',
  pa_headless_wa_expenses: '费用',
  pa_headless_wa_net: '净利润(亏损)',
  pa_headless_wa_unplaced: '有 {{n}} 个科目未归入工作账——上面的毛利不含这 {{amount}}。',
  pa_headless_wa_doc_name: 'บัญชีทำการ',
  pa_headless_wa_doc: '损益计算表',
  pa_headless_fw_rule: '表 1、2、4、5 以 TFRS for NPAEs 为基础;表 3 以 TFRS for PAEs 为基础。NPAE 若完整适用 TFRS for PAEs,可以选用,并须在财务报表附注中披露格式的变更。',
  pa_headless_fw_period: '记录在本会计期间上',
  pa_headless_fw_period_tail: ' — 已结账的期间永不重述。',
  pa_headless_fw_none: '— 未记录 —',
  pa_headless_fw_npae: 'TFRS สำหรับ NPAEs',
  pa_headless_fw_pae: 'TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะ',
  pa_headless_fw_dbd_form: 'DBD แบบ {{form}}',
  pa_headless_fw_locked: '已结账 — 已锁定',
  pa_headless_sh_rule: 'CCC — 公司自设立起即须备置股东名簿,并须按股东大会当日的状况在 14 天内报送 บอจ.5。⚠️ 本系统未持有登记官的 บอจ.5 空白表格,因此名簿可供查阅核对,但无法套印到法定表格上。',
  pa_headless_sh_as_at: '截至日期',
  pa_headless_sh_holders: '{{n}} 名股东',
  pa_headless_sh_shares: '{{n}} 股',
  pa_headless_sh_form_name: 'บอจ.5',
  pa_headless_sh_form_not_held: '表格未持有',
  pa_headless_sh_name_ph: '股东姓名',
  pa_headless_sh_individual: '自然人',
  pa_headless_sh_juristic: '法人',
  pa_headless_sh_idno: '身份/统一编号',
  pa_headless_sh_nationality: '国籍',
  pa_headless_sh_shares_ph: '股数',
  pa_headless_sh_paid_per_share: '每股已缴',
  pa_headless_sh_held_from: '持有起始日',
  pa_headless_sh_add: '添加',
  pa_headless_sh_th_name: '姓名',
  pa_headless_sh_th_kind: '类别',
  pa_headless_sh_th_shares: '股数',
  pa_headless_sh_end_prompt: '持有终止日 (YYYY-MM-DD)?',
  pa_headless_sh_end: '终止…',
  pa_headless_sh_empty: '该日期尚无持股记录。',
  pa_headless_pkg_rule: '财务报表与审计师报告通过 DBD e-Filing 报送:报表以 XBRL 格式,审计师报告为黑白扫描件,另附股东名单。',
  pa_headless_pkg_unit: '单位',
  pa_headless_pkg_file: '文件 {{id}}',
  pa_headless_pkg_declared: '已声明',
  pa_headless_pkg_not_attached: '未附上',
  pa_headless_pkg_not_producible: '本系统无法生成',
  pa_headless_pkg_file_prompt: '{{slot}} 的文件 ID?',
  pa_headless_pkg_attach: '附加…',
  pa_headless_pkg_declare_bw: '声明审计师报告为黑白件',
  pa_headless_cit_rule: 'Revenue Code s.65 ter — 哪些科目在企业所得税上可以扣除、哪些不可以。⚠️ 本系统仅持有第 (1)–(6) 项;s.65 ter 其余各项尚未取得。',
  pa_headless_cit_th_account: '会计科目',
  pa_headless_cit_th_movement: '发生额',
  pa_headless_cit_th_treatment: '企业所得税处理',
  pa_headless_cit_th_note: '备注',
  pa_headless_cit_undecided: '— 尚未判定 —',
  pa_headless_cit_yes: '可扣除',
  pa_headless_cit_no: '不可扣除',
  pa_headless_cit_limited: '部分可扣除',
  pa_headless_cit_empty: '本期无需复核的科目。',
  pa_headless_fx_rule_a: 'Revenue Code s.86/4 — 税务发票以泰铢开立,除非税务局批准使用其他币种。',
  pa_headless_fx_rule_b: '开立或编辑税务发票时现已强制校验此闸门',
  pa_headless_fx_rule_c: ':在此处录入批准之前,非 THB 的税务发票一律被拒。',
  pa_headless_fx_approval_no: '税务局批准编号',
  pa_headless_fx_approved_on: '批准日',
  pa_headless_fx_effective_from: '生效日',
  pa_headless_fx_effective_to: '至',
  pa_headless_fx_recorded: '{{currency}} 的批准已登记。',
  pa_headless_fx_record: '登记批准',
  pa_headless_fx_check: '查询 {{currency}} 今日是否获批',
  pa_headless_fx_allowed: '已获批准。',
  pa_headless_fx_not_allowed: '未获批准。',
  pa_headless_ret_label: '保存期限',
  pa_headless_ret_5y: '5 年(มาตรา 14 规定的下限)',
  pa_headless_ret_6y: '6 年',
  pa_headless_ret_7y: '7 年(DBD 可延长的上限)',
  pa_headless_ret_th_when: '时间',
  pa_headless_ret_th_what: '对象',
  pa_headless_ret_th_mode: '方式',
  pa_headless_ret_th_reason: '原因',
  pa_headless_ret_th_by: '经办人',
  pa_headless_ret_none: '尚无文件被销毁。',
  pa_headless_agm_registered: '注册日',
  pa_headless_agm_first_due: '首次会议截止日',
  pa_headless_agm_last_held: '最近一次会议',
  pa_headless_agm_next_due: '下次截止日',
  pa_headless_agm_overdue: '逾期',
  pa_headless_wht_settled: '{{n}} 笔已结清',
  pa_headless_wht_payer_alone: '付款方单独承担',
  pa_headless_wht_joint: '连带责任',
  pa_headless_wht_at_risk: '尚有风险的税款合计 {{amount}} $t(cur_unit)。',
  pa_headless_wht_th_paid: '付款日',
  pa_headless_wht_th_payee: '收款方',
  pa_headless_wht_th_form: '表单',
  pa_headless_wht_th_gross: '支付总额',
  pa_headless_wht_th_withheld: '扣缴',
  pa_headless_wht_th_who: '谁承担',
  pa_headless_wht_th_why: '原因',
  pa_headless_wht_all_clear: '每一行都已扣缴并解缴。',
  pa_headless_sso_rule_a: '新注册的公司依法即为雇主,须为员工办理社会保险登记。每位员工的社保号本系统原已持有,唯独',
  pa_headless_sso_rule_b: '雇主自身',
  pa_headless_sso_rule_c: '的账号此前没有。',
  pa_headless_sso_account_no: '雇主账号',
  pa_headless_sso_branch: '分支',
  pa_headless_sso_registered_on: '登记日',
  pa_headless_sso_form_101: 'สปส.1-01',
  pa_headless_sso_office: '辖区办事处',
  pa_headless_sso_saved: '已保存。',
  pa_headless_sso_save: '保存',
  pa_headless_sso_employees: '{{n}} 名员工',
  pa_headless_sso_no_number: '{{n}} 人尚无社保号',
  pa_headless_rev_rule_a: 'Revenue Code s.65 bis (3)-(4) — 资产向上重估产生的增值既不计入应税利润,',
  pa_headless_rev_rule_b: '也',
  pa_headless_rev_rule_c: '不计入折旧基数,因此账面基数与税务基数将永久分离。',
  pa_headless_rev_th_asset: '资产',
  pa_headless_rev_th_cost: '原值',
  pa_headless_rev_th_book: '账面基数',
  pa_headless_rev_th_tax: '税务基数',
  pa_headless_rev_th_reval: '资产重估',
  pa_headless_rev_amount_prompt: '{{name}} 的新账面金额 ($t(cur_word, {"lng":"en"}))?',
  pa_headless_rev_date_prompt: '重估日 (YYYY-MM-DD)?',
  pa_headless_rev_basis_prompt: '由谁评估、依据哪份报告?(必填)',
  pa_headless_rev_revalue: '重估…',
  pa_headless_rev_empty: '登记簿上没有资产。',
  pa_headless_module_title: '其余事项',
  pa_headless_sec_wa_name: 'บัญชีทำการ',
  pa_headless_sec_wa: '损益计算表(s.68 bis)',
  pa_headless_sec_wa_sub: '早已建好,此前一直没有入口按钮。',
  pa_headless_sec_fw_name: 'แม่บทการรายงานทางการเงิน',
  pa_headless_sec_fw: '财务报告框架',
  pa_headless_sec_fw_sub: '现记录在会计期间上 — 已结账的期间永不重述。',
  pa_headless_sec_sh_name: 'ทะเบียนผู้ถือหุ้น',
  pa_headless_sec_sh: '股东名簿 / บอจ.5',
  pa_headless_sec_sh_sub: '按指定日期的持股情况,含转让链条。',
  pa_headless_sec_pkg: 'DBD e-Filing 电子报送材料包',
  pa_headless_sec_pkg_sub: '三个附件位、单位声明,以及本系统无法生成的部分。',
  pa_headless_sec_cit: '按科目的企业所得税处理(s.65 ter)',
  pa_headless_sec_cit_sub: '仅第 (1)–(6) 项 — 其余尚未取得。',
  pa_headless_sec_fx_name: 'ใบกำกับภาษีสกุลเงินต่างประเทศ',
  pa_headless_sec_fx: '外币开票批准(s.86/4)',
  pa_headless_sec_fx_sub: '⚠️ 开立或编辑税务发票时现已强制校验。',
  pa_headless_sec_ret: '保存期限与销毁登记簿(s.14)',
  pa_headless_sec_ret_sub: '底层机制早已存在,却完全没有接口路由。',
  pa_headless_sec_agm: '股东大会(CCC s.1197)',
  pa_headless_sec_agm_sub: '首次须在注册后 6 个月内召开,其后每 12 个月一次。',
  pa_headless_sec_wht: '扣缴义务由谁承担(s.54)',
  pa_headless_sec_wht_sub: '已扣未解缴由付款方单独承担;自始未扣则为连带责任。',
  pa_headless_sec_sso: '雇主社保登记',
  pa_headless_sec_sso_sub: '雇主自身的账号,而非员工的。',
  pa_headless_sec_rev: '资产重估:账面基数与税务基数(s.65 bis)',
  pa_headless_sec_rev_sub: '该增值既不计入应税利润,也不计入折旧基数。',

  // ── Registers — the statutory registers ───────────────────────────────────────
  pa_reg_title: '法定登记簿',
  pa_reg_record: '登记',
  pa_reg_save: '保存',
  pa_reg_saved: '已保存。',
  pa_reg_empty: '登记簿上还没有记录。',
  pa_reg_overdue: '逾期',
  pa_reg_days_late: '逾期 {{n}} 天',
  pa_reg_doc_directors_report: 'รายงานของกรรมการ',
  pa_reg_doc_firm_register: 'ทะเบียนสำนักงานบัญชี',
  pa_reg_doc_liquidation_report: 'รายงานการชำระบัญชี',
  pa_reg_doc_sales_tax_invoice: 'ใบกำกับภาษีขาย',
  pa_reg_incident_kind_lost: '灭失',
  pa_reg_incident_kind_damaged: '损毁',
  pa_reg_incident_discovered: '发现于',
  pa_reg_incident_happened: '发生于',
  pa_reg_incident_scope_ph: '哪些账簿 / 凭证',
  pa_reg_incident_cause_ph: '原因(s.16:留空 = 推定为故意)',
  pa_reg_incident_presumed_alert: 's.16:未说明原因的灭失,一律推定为故意。实情一旦查明,请立即记录。',
  pa_reg_incident_th_discovered: '发现日',
  pa_reg_incident_th_what: '情形',
  pa_reg_incident_th_scope: '范围',
  pa_reg_incident_th_cause: '原因',
  pa_reg_incident_th_notify_by: '通知截止日',
  pa_reg_incident_th_notified: '已通知',
  pa_reg_incident_recovered: '已找回',
  pa_reg_incident_cause_blank: '未说明 — s.16 推定为故意',
  pa_reg_incident_notified_prompt: '向 DBD 通知的日期(YYYY-MM-DD)?',
  pa_reg_incident_epermit_prompt: 'e-Permit 参考号?',
  pa_reg_incident_notified_btn: '已通知…',
  pa_reg_incident_unexplained: '未说明原因的事件:{{n}} 件。s.16 对原因不明的灭失推定为故意 — 请记录实情。',
  pa_reg_cess_ceased: '停业于',
  pa_reg_cess_reason_ph: '原因',
  pa_reg_cess_record: '登记停业',
  pa_reg_cess_ceased_on: '停业 {{date}}',
  pa_reg_cess_deliver_by: '移交截止',
  pa_reg_cess_extended_to: '已延至 {{date}}',
  pa_reg_cess_max_extension: '(最迟可延至 {{date}})',
  pa_reg_cess_delivered: '已移交 {{date}}',
  pa_reg_cess_not_delivered: '尚未移交',
  pa_reg_cess_overdue_days: '逾期 {{n}} 天',
  pa_reg_cess_extend_prompt: '延至(YYYY-MM-DD,最迟 {{date}})?',
  pa_reg_cess_extend_ref_prompt: '延期批文参考号?',
  pa_reg_cess_extend_btn: '已获延期…',
  pa_reg_cess_delivered_prompt: '移交 DBD 的日期(YYYY-MM-DD)?',
  pa_reg_cess_receipt_prompt: 'DBD 的受理回执参考号?',
  pa_reg_cess_delivered_btn: '已移交…',
  pa_reg_insp_demanded: '调阅日',
  pa_reg_insp_authority_ph: '机关',
  pa_reg_insp_inspector_ph: '稽查人员',
  pa_reg_insp_due: '提交期限',
  pa_reg_insp_record: '登记调阅',
  pa_reg_insp_asked_label: '要求提交',
  pa_reg_insp_preview: '预览清单',
  pa_reg_insp_build: '生成检查包',
  pa_reg_insp_th_demanded: '调阅日',
  pa_reg_insp_th_by: '机关',
  pa_reg_insp_th_asked: '调阅内容',
  pa_reg_insp_th_due: '截止日',
  pa_reg_insp_th_answered: '已回复',
  pa_reg_insp_answered_prompt: '回复日期(YYYY-MM-DD)?',
  pa_reg_insp_answered_btn: '已回复…',
  pa_reg_chg_resolved: '决议日',
  pa_reg_chg_from_ph: '原',
  pa_reg_chg_to_ph: '新',
  pa_reg_chg_liquidation_due: '下一次清算报告应于 {{due}} 报送(自 {{since}} 起三个月)。',
  pa_reg_chg_th_resolved: '决议日',
  pa_reg_chg_th_what: '事项',
  pa_reg_chg_th_fromto: '原 → 新',
  pa_reg_chg_th_register_by: '登记截止日',
  pa_reg_chg_th_registered: '已登记',
  pa_reg_chg_registered_prompt: '向 DBD 登记的日期(YYYY-MM-DD)?',
  pa_reg_chg_receipt_prompt: '受理回执编号?',
  pa_reg_chg_registered_btn: '已登记…',
  pa_reg_dr_meeting: '会议日',
  pa_reg_dr_presented: '提交日',
  pa_reg_dr_body_th_label: '董事报告(泰文)',
  pa_reg_dr_body_en_label: '董事报告(英文)',
  pa_reg_vat_rev12: '12 个月收入 {{from}} – {{to}}',
  pa_reg_vat_source: '来源:{{source}}',
  pa_reg_vat_source_ledger: '账簿',
  pa_reg_vat_exceeded_head: '已超过 1.8 ล้านบาท。',
  pa_reg_vat_exceeded_body: '滚动十二个月收入已超过增值税登记起征点。',
  pa_reg_vat_notice_ack_by: '已确认 — 确认人',
  pa_reg_vat_notice_ack_tail: ',每日提醒已停止。',
  pa_reg_vat_notice_sending: '每日提醒 — 已发往 Telegram 与 {{mailbox}},共 {{n}} 次。',
  pa_reg_vat_notice_sending_last: '每日提醒 — 已发往 Telegram 与 {{mailbox}},共 {{n}} 次,最近一次 {{date}}。',
  pa_reg_vat_notice_stops: '有人点击邮件中的链接后即停止发送。',
  pa_reg_vat_notice_last_send: '上次发送:',
  pa_reg_vat_notice_resume: '重新开始发送',
  pa_reg_stock_cost_formula: '成本计算方法',
  pa_reg_stock_method_none: '— 未选择 —',
  pa_reg_stock_th_item: '存货项目',
  pa_reg_stock_th_qty: '数量',
  pa_reg_stock_th_cost: '成本',
  pa_reg_stock_th_market: '市价',
  pa_reg_stock_th_carried: '账面金额',
  pa_reg_stock_th_writedown: '计价减损',
  pa_reg_stock_total: '合计',
  pa_reg_stock_empty: '没有可计价的存货。',
  pa_reg_stock_struck_msg: '已确定计价。计价减损 {{amount}},记入待过账分录 {{doc}} — 请与其他待过账分录一并核对。',
  pa_reg_stock_struck_none: '已确定计价。无需计价减损。',
  pa_reg_stock_strike_btn: '确定计价',
  pa_reg_stock_struck_line: '已确定计价:{{n}} 项,{{date}} 由 {{name}} 确定。',
  pa_reg_firm_rule: 'Accounting Professions Act s.11 — 提供会计服务的法人须向 TFAC 登记,并就对第三人的赔偿责任缴存保证金;提供审计服务者须由注册会计师 (CPA) 签署约束,并与其审计师承担连带责任。',
  pa_reg_firm_regno_ph: 'TFAC 登记号',
  pa_reg_firm_registered: '登记日',
  pa_reg_firm_expires: '到期日',
  pa_reg_firm_guarantee_ph: '已缴存保证金(บาท)',
  pa_reg_firm_guarantee_form_ph: '形式(银行保函…)',
  pa_reg_firm_offers_audit: '提供审计服务',
  pa_reg_firm_cpa_name_ph: '签字注册会计师 (CPA)',
  pa_reg_firm_cpa_no_ph: 'CPA 执业证号',
  pa_reg_firm_expired: '已过期 — 该登记已于 {{date}} 到期。',
  pa_reg_firm_expiring: '{{date}} 到期 — 不足 60 天。',
  pa_reg_sec_incidents_title: '账簿或凭证灭失 / 损毁 (ss.15-16)',
  pa_reg_sec_incidents_sub: '自发现之日起 15 天内通知。原因不明的灭失推定为故意。',
  pa_reg_sec_cess_title: '停业 (s.17)',
  pa_reg_sec_cess_sub: '账簿须在 90 天内移交 DBD,可延长至 180 天。',
  pa_reg_sec_insp_title: '稽查调阅 (ss.22, 24, 25)',
  pa_reg_sec_insp_sub: '登记调阅,并将账簿、凭证与科目代码对照表打成一个检查包。',
  pa_reg_sec_chg_title: '变更登记(14 天)',
  pa_reg_sec_chg_sub: '资本、章程、董事;以及清算的三个月报送周期。',
  pa_reg_sec_dr_title_gloss: '董事报告 (CCC s.1198)',
  pa_reg_sec_dr_sub: '与资产负债表一并提交股东大会。法律未规定格式。',
  pa_reg_sec_stock_title: '期末存货计价,成本与市价孰低 (s.65 bis)',
  pa_reg_sec_stock_sub: '先进先出或加权平均,一经采用即锁定。(进出相抵不是成本计算方法。)',
  pa_reg_sec_vat_title: '增值税登记起征点 1.8 ล้าน',
  pa_reg_sec_vat_sub: '滚动十二个月收入对比 1,800,000 บาท。',
  pa_reg_sec_firm_title_gloss: '事务所自身的 TFAC 登记 (APA s.11)',
  pa_reg_sec_firm_sub: '关于 {{brand}} 自身,不是客户:登记、保证金,以及签署约束的注册会计师 (CPA)。',

  // ── TaxLedgers — the six tax ledgers ──────────────────────────────────────────
  pa_tax_channel_label: '申报方式',
  pa_tax_channel_online: '网上申报',
  pa_tax_channel_paper: '纸质申报',
  pa_tax_channel_deadlines_paper: '月度申报表截止日为 7 日(增值税为 15 日);PND.50 为 150 天,PND.51 为两个月 — 不享有网上申报的延期。',
  pa_tax_channel_deadlines_online: '月度申报表截止日为 15 日(增值税为 23 日);PND.50 为 158 天,PND.51 为两个月零八天。',
  pa_tax_fx_rule_1: 's.65 bis (5) — 期末外币余额按泰国银行的汇率折算,并一贯采用同一方法。本系统没有汇率来源:汇率靠导入;在期末当日或之前没有汇率的余额,',
  pa_tax_fx_rule_not: '不会',
  pa_tax_fx_rule_2: '按任何汇率折算。',
  pa_tax_fx_method_label: '折算方法',
  pa_tax_fx_other_method_prompt: '获批准的方法是什么?由谁批准?',
  pa_tax_fx_method_none: '— 未选择 —',
  pa_tax_fx_no_method_warn: '尚未锁定折算方法。s.65 bis (5) 要求一贯采用同一方法,因此在选定之前不会进行任何重新折算 — 而一旦有会计期间按该方法重新折算过,就不能再更改。',
  pa_tax_fx_rate_label: '汇率',
  pa_tax_fx_kind_avg: 'BOT 平均汇率',
  pa_tax_fx_kind_buy: 'BOT 买入汇率',
  pa_tax_fx_kind_sell: 'BOT 卖出汇率',
  pa_tax_fx_source_ph: '来源',
  pa_tax_fx_rate_stored: '汇率已保存。',
  pa_tax_fx_nothing_imported: '没有导入任何数据。',
  pa_tax_fx_add_rate: '添加汇率',
  pa_tax_fx_account_none: '— 会计科目 —',
  pa_tax_fx_amount_fx_ph: '外币金额',
  pa_tax_fx_booked_ph: '账面$t(cur_word)金额',
  pa_tax_fx_add_balance: '添加外币余额',
  pa_tax_fx_th_account: '科目',
  pa_tax_fx_th_cur: '币种',
  pa_tax_fx_th_amount: '金额',
  pa_tax_fx_th_booked: '账面金额',
  pa_tax_fx_th_rate: '汇率',
  pa_tax_fx_th_at_rate: '按汇率折算',
  pa_tax_fx_th_diff: '收益 /(损失)',
  pa_tax_fx_empty: '本会计期间未记录外币余额。',
  pa_tax_fx_revalued_msg: '待过账分录 {{doc}} — 净额 {{amount}} · 重新折算的余额数:{{n}}。过账前请与其他待过账分录一并核对。',
  pa_tax_fx_revalue_btn: '重新折算 {{amount}}',
  pa_tax_rem_warn_1: '本系统对这两种申报表',
  pa_tax_rem_warn_norate: '不持有任何税率',
  pa_tax_rem_warn_2: ',也不持有其法定依据。适用于境内支付的 ท.ป.4/2528 税率属于另一项规定,不得在此使用 — 每一行的税率由顾问给出,并记录是谁录入的。',
  pa_tax_rem_lines: '{{n}} 行',
  pa_tax_rem_base: '计税基数 {{amount}}',
  pa_tax_rem_tax: '税额',
  pa_tax_rem_due: '截止日 {{date}}',
  pa_tax_rem_filed: '已申报',
  pa_tax_rem_payee_ph: '收款人',
  pa_tax_rem_country_ph: '国家',
  pa_tax_rem_nature_ph: '所得性质',
  pa_tax_rem_base_ph: '计税基数(泰铢)',
  pa_tax_rem_rate_ph: '税率 %',
  pa_tax_rem_basis_ph: '法定依据',
  pa_tax_rem_add: '添加',
  pa_tax_rem_th_paid: '支付日',
  pa_tax_rem_th_payee: '收款人',
  pa_tax_rem_th_for: '所得性质',
  pa_tax_rem_th_base: '计税基数',
  pa_tax_rem_th_rate: '税率',
  pa_tax_rem_th_tax: '税额',
  pa_tax_rem_th_basis: '法定依据',
  pa_tax_rem_not_recorded: '未记录',
  pa_tax_rem_empty: '本月申报表上没有任何记录。',
  pa_tax_rem_filed_prompt: '实际缴纳日期(YYYY-MM-DD)?',
  pa_tax_rem_mark_filed: '标记已申报',
  pa_tax_stamp_rule: 'ss.104 与 118 — 附表所列应税凭证必须足额贴花;未贴花的凭证在缴清印花税及附加金之前不得用作证据。事务所作为自身业务委任合同的承揽人,须按每 1,000 泰铢缴 1 泰铢 — 即 0.1% — 缴纳印花税。',
  pa_tax_stamp_party_ph: '交易对方',
  pa_tax_stamp_base_ph: '租金 / 费用 / 本金',
  pa_tax_stamp_pick_none: '— 哪一个金额? —',
  pa_tax_baht_amount: '{{amount}} 泰铢',
  pa_tax_stamp_one_act: '(单一行为)',
  pa_tax_stamp_several_acts: '(多次行为)',
  pa_tax_stamp_vat_exempt: '此金额已计增值税(免征)',
  pa_tax_stamp_record: '登记',
  pa_tax_stamp_schedule_head: 'ลักษณะแห่งตราสาร',
  pa_tax_stamp_number_inferred: '(项目编号系推断 — 来源将这七项作为一组给出)',
  pa_tax_stamp_band: ' · 附表给出 {{min}}–{{max}} 泰铢,是一个区间而不是公式',
  pa_tax_stamp_th_date: '日期',
  pa_tax_stamp_th_instrument: '应税凭证',
  pa_tax_stamp_th_party: '当事方',
  pa_tax_stamp_th_base: '计税基数',
  pa_tax_stamp_th_duty: '印花税额',
  pa_tax_stamp_th_stamped: '贴花',
  pa_tax_stamp_not_established: '尚未确定',
  pa_tax_stamp_exempt: '免征',
  pa_tax_stamp_unpaid: '未贴花',
  pa_tax_stamp_method_prompt: '以何种方式缴纳?请输入 adhesive(贴花)、impressed(压印)或 cash(现金)',
  pa_tax_stamp_paid_on_prompt: '缴纳日期(YYYY-MM-DD)?',
  pa_tax_stamp_duty_prompt: '已缴印花税额(泰铢)?',
  pa_tax_stamp_receipt_prompt: 'อ.ส. 收据号码?',
  pa_tax_stamp_mark_btn: '标记已贴花',
  pa_tax_stamp_empty: '登记簿上没有任何记录。',
  pa_tax_stamp_unstamped_count: '{{n}} 份未贴花。',
  pa_tax_stamp_duty_established: '已确定税额:{{amount}}',
  pa_tax_stamp_unquantified: ' · 其中 {{n}} 份尚无税额',
  pa_tax_loss_year_ph: '年度',
  pa_tax_loss_amount_ph: '亏损($t(cur_word))',
  pa_tax_loss_note_ph: '备注',
  pa_tax_loss_record: '登记亏损',
  pa_tax_loss_years_prompt: '结转年限(年)?',
  pa_tax_loss_basis_prompt: '这来自哪一条规定?(必填)',
  pa_tax_loss_set_period: '设定结转年限…',
  pa_tax_loss_th_year: '年度',
  pa_tax_loss_th_loss: '亏损',
  pa_tax_loss_th_used: '已使用',
  pa_tax_loss_th_left: '剩余',
  pa_tax_loss_th_expires: '到期',
  pa_tax_loss_th_used_in: '使用年度',
  pa_tax_loss_use_year_prompt: '在哪一年使用?',
  pa_tax_loss_use_amount_prompt: '使用多少($t(cur_word))?',
  pa_tax_loss_use_btn: '使用…',
  pa_tax_loss_total: '合计',
  pa_tax_loss_empty: '登记簿上没有亏损记录。',
  pa_tax_loss_unreconciled: '登记的合计数与各笔使用额之和不符。在查清之前,这里的任何数字都不宜用于申报。',
  pa_tax_rc_no_threshold: '尚未记录起征点,因此本项检查不会运行。s.105 只给出上限 — 增值税/特定业务税登记人为 1,000 泰铢,其他为 10,000 — 而税务局长公告的金额本系统并未持有。请记录该金额,或记录 0 表示每一笔收款都要开据;在该金额未知时,这是稳妥的读法。',
  pa_tax_rc_registrant_prompt: '是否为增值税/特定业务税登记人?请输入 vat_sbt 或 other',
  pa_tax_rc_threshold_prompt: '起征点(泰铢;0 = 每一笔收款;上限 {{ceiling}})',
  pa_tax_rc_notification_prompt: '税务局长公告号(如事务所持有)',
  pa_tax_rc_change_threshold: '更改起征点…',
  pa_tax_rc_record_threshold: '登记起征点…',
  pa_tax_rc_registrant_yes: '增值税/特定业务税登记人',
  pa_tax_rc_registrant_no: '非登记人',
  pa_tax_rc_scope_level: '{{scope}} 级',
  pa_tax_rc_th_received: '收款日',
  pa_tax_rc_th_against: '对应单据',
  pa_tax_rc_th_amount: '金额',
  pa_tax_rc_th_desc: '摘要',
  pa_tax_rc_all_covered: '此区间内每一笔已匹配的收款都有收据支持(已检查 {{n}} 笔)。',
  pa_tax_rc_daily_head: 's.105 ter 每日汇总',
  pa_tax_rc_daily_body: '共 {{n}} 天,合计 {{amount}}。',
  pa_tax_title: '税务台账',
  pa_tax_sec_fx_title: '汇率与期末重新折算',
  pa_tax_sec_fx_sub: 's.65 bis (5) — 一贯采用同一方法。此处没有汇率,就不会重新折算。',
  pa_tax_sec_rem_forms: 'ภ.ง.ด.54 · ภ.พ.36',
  pa_tax_sec_rem_title: '对外支付代扣代缴',
  pa_tax_sec_rem_sub: '这是一本登记簿,不是税率引擎:这里不知道这两种申报表的税率。',
  pa_tax_sec_stamp_title: '印花税',
  pa_tax_sec_stamp_sub: '律所或会计事务所会遇到的七项附表税率,以及尚未确定的部分。',
  pa_tax_sec_loss_title: '亏损结转登记簿',
  pa_tax_sec_loss_sub: '每个亏损年度一行;在未记录结转年限之前,不主张任何到期日。',
  pa_tax_sec_rc_doc: 'ใบรับ',
  pa_tax_sec_rc_title: '每笔收款均须出具收据 (s.105)',
  pa_tax_sec_rc_sub: '有收款但没有对应收据。在未记录起征点之前拒绝运行。',

  // ── 收费项目 → 收入科目 ──────────────────────────────────────────────────────────────────────
};

export const acctTh: Record<string, string> = {
  pa_sb_period_label: 'รอบระยะเวลาบัญชี',
  pa_sb_open_books_gloss: 'เปิดบัญชี',
  pa_sb_open_books_why: 'พระราชบัญญัติการบัญชีกำหนดให้ปิดบัญชีทุกสิบสองเดือน จึงต้องมีรอบระยะเวลาบัญชีก่อนจะบันทึกรายการใดได้ หากเป็นรอบสิบสองเดือนตามปกติให้เว้นวันสิ้นสุดไว้ รอบแรกอาจสั้นกว่านั้นได้ และรอบถัดไปที่ไม่ครบสิบสองเดือนต้องบันทึกหนังสืออนุญาตจากสารวัตรใหญ่บัญชีไว้ก่อน ผังบัญชีของสำนักงานจะถูกคัดลอกเข้ามาพร้อมกัน',
  pa_sb_from: 'เริ่ม',
  pa_sb_to: 'สิ้นสุด',
  pa_sb_optional: 'ไม่บังคับ',
  pa_sb_open_period: 'เปิดรอบบัญชี',
  pa_sb_open_next: 'เปิดรอบถัดไป',
  pa_sb_status_closed: 'ปิดบัญชีแล้ว',
  pa_sb_n_entries_other: '{{count}} รายการ',
  pa_sb_nothing_posted: 'ยังไม่มีการบันทึกรายการในรอบนี้ — สร้างรายการรอตรวจด้านบน ตรวจสอบ แล้วจึงผ่านรายการ',

  pa_sb_close_gloss: 'ปิดบัญชี…',
  pa_sb_close_confirm: 'การปิดบัญชีไม่สามารถย้อนกลับได้ ผลกำไรขาดทุนจะถูกโอนเข้ากำไรสะสม ยอดยกไปจะถูกตรึง และเริ่มนับระยะเวลาเก็บรักษาเอกสารประกอบการลงบัญชีห้าปีของรอบนี้ ดำเนินการต่อหรือไม่',
  pa_sb_close_sum: 'รายได้ {{income}} − ค่าใช้จ่าย {{expense}} =',
  pa_sb_profit: 'กำไร',
  pa_sb_loss: 'ขาดทุน',
  pa_sb_close_drafts_other: 'ยังมีรายการรอตรวจ {{count}} รายการ — จัดการให้เรียบร้อยก่อน',
  pa_sb_close_outofrange_other: 'มีรายการที่บันทึกแล้ว {{count}} รายการอยู่นอกรอบระยะเวลาบัญชี',
  pa_sb_close_unbalanced: 'รอบระยะเวลาบัญชีนี้ไม่ดุล',
  pa_sb_close_payroll: 'รอบเงินเดือน {{docNos}} อนุมัติแล้วแต่ยังไม่ลงบัญชี และงวดของรอบสิ้นสุดภายในรอบระยะเวลาบัญชีนี้ ให้ลงบัญชีที่ เงินเดือน › รายการเงินเดือน ก่อนปิดบัญชี — รอบที่ปิดแล้วจะบันทึกสิ่งใดเพิ่มไม่ได้',
  pa_sb_close_it: 'ยืนยันปิดบัญชี',
  pa_sb_closed_detail: '{{entries}} รายการ · {{result}} {{amount}} · ยอดยกไป {{carried}} รายการ · ประทับเอกสาร {{vouchers}} ฉบับให้เก็บรักษาห้าปี',

  pa_sb_h_journals: 'สมุดรายวันทั้งห้าเล่ม',
  pa_sb_h_ledger: 'บัญชีแยกประเภทและงบทดลอง',
  pa_sb_h_statements: 'งบการเงิน',
  pa_sb_h_extras: 'งบกระแสเงินสด หมายเหตุ สินค้า และภาษีมูลค่าเพิ่ม',

  pa_sb_c_gl: 'บัญชีแยกประเภททั่วไป',
  pa_sb_c_tb: 'งบทดลอง',
  pa_sb_c_ar: 'บัญชีย่อยลูกหนี้',
  pa_sb_c_ap: 'บัญชีย่อยเจ้าหนี้',
  pa_sb_c_stock: 'บัญชีสินค้า — เฉพาะกิจการที่มีสินค้า',
  pa_sb_c_goods: 'รายงานสินค้าและวัตถุดิบ',
  pa_sb_c_vatout: 'รายงานภาษีขาย — เดือนนี้',
  pa_sb_c_vatin: 'รายงานภาษีซื้อ — เดือนนี้',

  pa_sb_pl_line: 'รายได้ {{income}} − ค่าใช้จ่าย {{expense}}',
  pa_sb_pl_gross_line: 'ยอดขาย {{sales}} − ต้นทุนขาย {{cost}}',
  pa_sb_pl_gross_unplaced: '(มี {{n}} บัญชีที่ยังไม่ได้ระบุประเภท — ไม่ได้รวม {{amount}})',
  pa_sb_assets_line: 'สินทรัพย์ {{amount}}',
  pa_sb_unbalanced_why: 'สินทรัพย์ไม่เท่ากับหนี้สินรวมส่วนของเจ้าของ',

  pa_fs_bound_what: 'งบการเงินฉบับรวมเล่ม: ปก สารบัญพร้อมเลขหน้า และเลขหน้าต่อเนื่องชุดเดียว',
  pa_fs_bound_build: 'จัดทำฉบับรวมเล่ม',
  pa_fs_bound_building: 'กำลังจัดเล่ม…',
  pa_fs_cover_issues: 'ข้อสังเกตที่จะพิมพ์บนปก',
  pa_fs_pick_least: 'เลือกอย่างน้อยหนึ่งงบ',
  pa_fs_sfp: 'งบแสดงฐานะการเงิน',
  pa_fs_pl: 'งบกำไรขาดทุน',
  pa_fs_sce: 'งบแสดงการเปลี่ยนแปลงส่วนของเจ้าของ',
  pa_fs_cf: 'งบกระแสเงินสด',
  pa_fs_notes: 'หมายเหตุประกอบงบการเงิน',
  pa_fs_wa: 'บัญชีทำการ — ยื่นพร้อม ภ.ง.ด.50',

  pa_sb_foot_books: 'สมุดบัญชีพิมพ์เป็นภาษาไทย มีปกแสดงชื่อ {{client}} ชนิดของสมุดบัญชีและเล่มที่ และเรียงเลขหน้าต่อเนื่อง ตามที่ประกาศกรมพัฒนาธุรกิจการค้า พ.ศ. 2544 กำหนด',
  pa_sb_foot_statements: 'งบการเงินจัดทำจากสมุดบัญชีเหล่านั้นตามเกณฑ์คงค้าง ยังไม่ได้จัดรูปแบบเป็นรายการย่อตามประกาศกรมพัฒนาธุรกิจการค้า พ.ศ. 2566 จึงยังไม่ใช่แบบที่ยื่นต่อกรมพัฒนาธุรกิจการค้า — การจัดรูปแบบดังกล่าวรออยู่ที่ตัวบทประกาศฉบับจริง',

  // ── AccountingBooks — the client picker ───────────────────────────────────────
  pa_books_all_clients: 'ลูกค้าทั้งหมด',
  pa_books_title: 'สมุดบัญชีตามกฎหมาย',
  pa_books_pinned_missing_title: 'ไม่พบลูกค้าที่ผูกกับบัญชีผู้ใช้นี้',
  pa_books_pinned_missing_body: 'บัญชีผู้ใช้นี้ผูกกับลูกค้าหนึ่งราย และลูกค้ารายนั้นไม่อยู่ในรายชื่อที่บัญชีนี้อ่านได้ โปรดให้สำนักงานตรวจสอบการผูกบัญชีที่ Admin → Users',
  pa_books_intro: 'ผังบัญชี รอบระยะเวลาบัญชี สมุดรายวันทั้งห้าเล่ม บัญชีแยกประเภท การปิดบัญชีสิ้นปี และงบการเงิน — ลูกค้าบัญชีทุกรายจะแสดงที่นี่ รวมถึงรายที่ยังไม่มีใบแจ้งหนี้ เพราะบริษัทที่จดทะเบียนใหม่คือรายที่ต้องเปิดรอบระยะเวลาบัญชีมากที่สุด',
  pa_books_search_ph: 'ค้นหาลูกค้า…',
  pa_books_empty: 'ไม่พบลูกค้าบัญชีที่ตรงกัน',
  pa_books_drafts_pill: '{{n}} ร่าง',
  pa_books_no_period: 'ยังไม่เปิดบัญชี',
  pa_books_pill_closed: 'ปิดแล้ว',
  pa_books_pill_open: 'เปิดอยู่',
  pa_books_card_counts: '{{accounts}} บัญชี · {{entries}} รายการที่บันทึกบัญชีแล้ว',
  pa_books_card_vat: 'VAT',

  // ── Bookkeeping — the invoice-line-item books ─────────────────────────────────
  pa_bk_mon_jan: 'ม.ค.',
  pa_bk_mon_feb: 'ก.พ.',
  pa_bk_mon_mar: 'มี.ค.',
  pa_bk_mon_apr: 'เม.ย.',
  pa_bk_mon_may: 'พ.ค.',
  pa_bk_mon_jun: 'มิ.ย.',
  pa_bk_mon_jul: 'ก.ค.',
  pa_bk_mon_aug: 'ส.ค.',
  pa_bk_mon_sep: 'ก.ย.',
  pa_bk_mon_oct: 'ต.ค.',
  pa_bk_mon_nov: 'พ.ย.',
  pa_bk_mon_dec: 'ธ.ค.',
  pa_bk_individual: 'บุคคลธรรมดา',
  pa_bk_item_remove: 'ลบ',
  pa_bk_zero_rate: '0%',
  pa_bk_loading: 'กำลังโหลด…',
  pa_bk_books_link: 'สมุดบัญชี',
  pa_bk_books_link_title: 'ผังบัญชี สมุดรายวัน บัญชีแยกประเภท ปิดบัญชี และงบการเงิน',
  pa_bk_err_items_load: 'โหลดรายการไม่ได้',
  pa_bk_err_not_saved: 'ไม่ได้บันทึก',
  pa_bk_err_add: 'เพิ่มไม่ได้',
  pa_bk_err_delete: 'ลบไม่ได้',
  pa_bk_err_clear: 'ลบทั้งหมดไม่ได้',
  pa_bk_err_regen: 'สร้างใหม่จากรูปไม่สำเร็จ',
  pa_bk_err_month_load: 'โหลดเดือนนี้ไม่ได้',
  pa_bk_err_photo: 'โหลดรูปไม่ได้',

  // ── EntityInfo — juristic form and dividends ──────────────────────────────────
  pa_entity_form_not_recorded: 'ยังไม่ได้บันทึกรูปแบบนิติบุคคล',
  pa_entity_unknown_why: 'ลูกค้ารายนี้มีหน้าที่จัดทำบัญชีตาม s.8 หรือไม่ งบการเงินใช้แบบใดของ DBD และจะยกเว้นการสอบบัญชีได้หรือไม่ ทั้งหมดขึ้นอยู่กับข้อนี้',
  pa_entity_must_keep: 'ต้องจัดทำบัญชี (ม.8)',
  pa_entity_not_required: 'ไม่ใช่ผู้มีหน้าที่จัดทำบัญชี',
  pa_entity_dbd_form: 'DBD แบบ {{n}}',
  pa_entity_reg_capital_val: 'ทุนจดทะเบียน {{amount}}',
  pa_entity_registered_on_val: 'จดทะเบียน {{date}}',
  pa_entity_dividend: 'เงินปันผล',
  pa_entity_f_form: 'รูปแบบ',
  pa_entity_f_form_select: '— เลือก —',
  pa_entity_form_dbd_suffix: '(แบบ {{n}})',
  pa_entity_f_capital: 'ทุนจดทะเบียน ($t(cur_word))',
  pa_entity_f_registered_on: 'วันจดทะเบียน',
  pa_entity_div_law: 'ประมวลกฎหมายแพ่งและพาณิชย์ ม.1201–1202 — เงินปันผลจ่ายได้จากกำไรเท่านั้น จ่ายไม่ได้ในขณะที่ยังมีผลขาดทุนสะสม และต้องจัดสรรเงินปันผลนั้นอย่างน้อย 5% เข้าทุนสำรองจนกว่าทุนสำรองจะถึงหนึ่งในสิบของทุนจดทะเบียน และต้องจ่ายภายในหนึ่งเดือนนับแต่วันที่มีมติ',
  pa_entity_div_amount: 'จำนวน ($t(cur_word))',
  pa_entity_div_resolution_date: 'วันที่มีมติ',
  pa_entity_div_payable_account: 'บัญชีเงินปันผลค้างจ่าย',
  pa_entity_div_retained: 'กำไรสะสม {{amount}}',
  pa_entity_div_reserve: 'ทุนสำรอง {{amount}}',
  pa_entity_div_ceiling: 'เพดาน {{amount}}',
  pa_entity_div_appropriation: 'จัดสรรทุนสำรอง {{amount}}',
  pa_entity_div_create: 'สร้างร่าง',
  pa_entity_div_created: 'สร้าง {{doc}} เป็นร่างแล้ว',
  pa_entity_div_created_appr: 'สร้าง {{doc}} เป็นร่างแล้ว โดยจัดสรร {{amount}} เข้าทุนสำรอง',

  // ── DraftEntries — generate, review, post ─────────────────────────────────────
  pa_jnl_cash: 'เงินสด',
  pa_jnl_bank: 'ธนาคาร',
  pa_jnl_purchase: 'รายวันซื้อ',
  pa_jnl_sale: 'รายวันขาย',
  pa_jnl_general: 'รายวันทั่วไป',
  pa_draft_title: 'รายการรอตรวจ',
  pa_draft_sub_pending: 'รายการที่นี่จะยังไม่อยู่ในสมุดบัญชีจนกว่าคุณจะผ่านรายการ',
  pa_draft_sub_empty: 'มาจากสมุดย่อยภาษีมูลค่าเพิ่ม — รายการขายและซื้อที่บันทึกไว้แล้ว',
  pa_draft_generate: 'สร้างจากสมุดย่อย',
  pa_draft_stale_btn: 'สร้างใหม่ {{n}} รายการ',
  pa_draft_stale_btn_title: 'สร้างร่างใหม่สำหรับรายการที่เอกสารเปลี่ยนไป — เฉพาะรายที่ยังไม่มีใครแก้ไข',
  pa_draft_stale_rebuilt: 'สร้างใหม่แล้ว {{n}} รายการ',
  pa_draft_stale_kept: 'เหลือให้คุณจัดการ {{n}} รายการ (มีคนแก้ไขแล้ว)',
  pa_draft_stale_withdrawn: 'ล้างแล้ว {{n}} รายการ — เอกสารถูกถอนไปแล้ว',
  pa_draft_stale_r_line: 'ร่างนี้สร้างจากเอกสารที่ถูกแก้ไขไปแล้วหลังจากนั้น กรุณายกเลิกร่างนี้แล้วสร้างใหม่ เพื่อให้บัญชีตรงกับเอกสารปัจจุบัน',
  pa_draft_stale_r_items: 'ร่างนี้สร้างจากเอกสารที่รายการย่อยถูกแก้ไขไปแล้ว กรุณายกเลิกร่างนี้แล้วสร้างใหม่ เพื่อให้บัญชีตรงกับเอกสารปัจจุบัน',
  pa_draft_stale_r_both: 'ร่างนี้สร้างจากเอกสารที่ทั้งตัวเอกสารและรายการย่อยถูกแก้ไขไปแล้ว กรุณายกเลิกร่างนี้แล้วสร้างใหม่',
  pa_draft_stale_r_gone: 'เอกสารที่ใช้สร้างร่างนี้ถูกถอนไปแล้ว — บันทึกใหม่ ยกเลิก หรือลบ กรุณายกเลิกร่างนี้ การลงบัญชีจะทำให้เอกสารที่ไม่มีอยู่แล้วเข้าไปอยู่ในบัญชี',
  pa_draft_stale_r_vouchers: 'หลังจากสร้างร่างนี้ มีใบสำคัญที่บันทึกด้วยมือของเดือนเดียวกันและทิศทางเดียวกันถูกผ่านรายการ กรุณายกเลิกร่างนี้แล้วสร้างใหม่ — ระบบจะให้ยืนยันใบสำคัญนั้นทีละใบ',
  pa_draft_stale_r_prepaid: 'ภาษีที่ลูกค้าหัก ณ ที่จ่ายของใบเสร็จของร่างนี้ ตอนนี้ถูกบันทึกต่างไปจากเดิมโดยรายการเดินบัญชีอื่นของใบเสร็จนั้น — มีรายการอื่นบันทึกไว้แล้ว หรือไม่มีรายการใดบันทึกไว้ กรุณายกเลิกร่างนี้แล้วสร้างใหม่ เพื่อให้ภาษีหัก ณ ที่จ่ายถูกบันทึกเพียงครั้งเดียว',
  pa_draft_stale_r_prepaid_wait: 'ร่างนี้ปล่อยให้ภาษีที่ลูกค้าหัก ณ ที่จ่ายของใบเสร็จไปบันทึกในอีกรายการหนึ่งของใบเสร็จนั้น ซึ่งยังเป็นร่างอยู่ (ชี้เมาส์เพื่อดูว่ารายการใด) — กรุณาผ่านรายการนั้นก่อน แล้วจึงผ่านรายการร่างนี้',
  pa_draft_stale_r_vouchers_named: 'มีใบสำคัญด้วยมือของเดือนเดียวกันที่เคลื่อนไหวเงินทางเดียวกัน ({{docNos}}) ผ่านรายการหลังสร้างร่างนี้ และยังไม่มีผู้ยืนยัน กรุณายกเลิกร่างนี้แล้วสร้างใหม่ — ระบบจะให้ยืนยันทีละใบ',
  pa_draft_stale_r_prepaid_twice: 'ภาษีที่ลูกค้าหักไว้ในใบเสร็จ {{receipt}} ถูกบันทึกแล้วโดย {{by}} — กรุณายกเลิกร่างนี้แล้วสร้างใหม่ เพื่อไม่ให้บันทึกซ้ำ',
  pa_draft_stale_r_prepaid_nowhere: 'ไม่มีรายการใดบันทึกภาษีที่ลูกค้าหักไว้ในใบเสร็จ {{receipt}} — ร่างนี้ไม่บันทึก และรายการอื่นของใบเสร็จก็ไม่บันทึก กรุณายกเลิกแล้วสร้างใหม่ เพื่อให้บันทึกครั้งเดียว',
  pa_draft_stale_r_prepaid_part: 'ภาษีที่ลูกค้าหักไว้ในใบเสร็จ {{receipt}} ถูกบันทึกเพียงบางส่วน — {{part}} โดย {{by}} — และร่างนี้ไม่ได้บันทึกเลย กรุณายกเลิกแล้วสร้างใหม่ ระบบจะบอกวิธีบันทึกส่วนที่เหลือ',
  pa_draft_stale_r_prepaid_unclear: 'ภาษีที่ลูกค้าหักไว้ในใบเสร็จ {{receipt}} อาจถูกบันทึกแล้วโดย {{by}} ซึ่งรายการเดินบัญชีจับคู่กับใบแจ้งหนี้ที่มีใบเสร็จหักภาษีมากกว่าหนึ่งใบ กรุณายกเลิกร่างนี้ จับคู่รายการนั้นกับใบเสร็จแทน แล้วสร้างใหม่',
  pa_draft_stale_r_prepaid_leaving: 'ร่างนี้ปล่อยให้ {{by}} บันทึกภาษีที่ลูกค้าหักไว้ในใบเสร็จ {{receipt}} แต่ได้ออกรายการกลับ {{rv}} ไว้แล้ว — เมื่อ {{rv}} ผ่านรายการ จะไม่มีรายการใดบันทึกภาษีนั้น กรุณาผ่านรายการ {{rv}} แล้วยกเลิกร่างนี้และสร้างใหม่ หรือยกเลิก {{rv}}',
  pa_draft_stale_r_prepaid_wait_named: 'ร่างนี้ปล่อยให้ {{by}} บันทึกภาษีที่ลูกค้าหักไว้ในใบเสร็จ {{receipt}} ซึ่งยังเป็นร่างอยู่ — กรุณาผ่านรายการนั้นก่อน แล้วจึงผ่านร่างนี้',
  pa_draft_stale_not_rebuilt: 'สร้างใหม่ไม่ได้ {{n}} รายการ จึงเก็บไว้ตามเดิม: {{list}}',
  pa_draft_stale_other: 'สร้างร่างเพิ่มอีก {{n}} รายการ สำหรับเอกสารที่ยังไม่มีร่าง',
  pa_draft_hide: 'ซ่อน',
  pa_draft_review: 'ตรวจสอบ',
  pa_draft_post_all: 'ผ่านรายการทั้งหมด {{n}} รายการ',
  pa_draft_post: 'ผ่านรายการ',
  pa_draft_discard_title: 'ทิ้งร่างนี้',
  pa_draft_discard_confirm: 'ทิ้งร่างของ {{docNo}} หรือไม่ แถวในสมุดย่อยยังอยู่ — สร้างใหม่ได้อีกครั้ง',
  pa_draft_discard_confirm_reversal: 'ทิ้งรายการกลับรายการ {{docNo}} หรือไม่ {{reverses}} ยังอยู่ในบัญชีจนกว่าจะกลับรายการอีกครั้ง',
  pa_draft_reversal_note: 'กลับรายการ {{reverses}} ทีละบรรทัด: บัญชีและบรรทัดเป็นของ {{reverses}} โดยสลับเดบิตกับเครดิต จึงเปลี่ยนบัญชีหรือแยกบรรทัดที่นี่ไม่ได้',
  pa_draft_reversal_hover: '(ชี้เมาส์เพื่อดูวิธีบันทึกบัญชีแบบอื่น)',
  pa_draft_reversal_way_own: 'ผ่านรายการ {{rv}} ตามที่เป็นอยู่ แล้วบันทึกจำนวนที่ถูกต้องในรายการบันทึกใหม่ของตนเอง',
  pa_draft_reversal_way_putBack: 'การผ่านรายการ {{rv}} จะนำ {{orig}} กลับเข้าบัญชีตามเดิม หากต้องการบันทึกแบบอื่น ให้ยกเลิก {{rv}} แล้วบันทึกจำนวนที่ถูกต้องในรายการบันทึกใหม่ของตนเอง',
  pa_draft_reversal_way_asset: 'ผ่านรายการ {{rv}} ตามที่เป็นอยู่ — ทะเบียนสินทรัพย์จะถูกคืนเมื่อผ่านรายการ แล้วสั่งคำนวณใหม่และผ่านรายการร่างใหม่ตามที่เป็นอยู่ (บัญชีเป็นไปตามทะเบียนสินทรัพย์ — หากต้องการบันทึกแบบอื่นให้แก้ไขสินทรัพย์ในทะเบียนก่อน) ห้ามบันทึกเป็นรายการใหม่ของตนเอง',
  pa_draft_reversal_way_held: 'ต้นทางของ {{orig}} ถูกสร้างใหม่แล้วเป็น {{held}} ผ่านรายการ {{rv}} ตามที่เป็นอยู่ แล้วบันทึกใน {{held}}: ร่างจากเอกสารเลือกบัญชีหรือแยกบรรทัดได้เฉพาะบรรทัดที่ไม่ใช่คู่ค้า ภาษีมูลค่าเพิ่ม หรือเงินสด ส่วนร่างรายการชำระให้ผ่านรายการตามที่เป็นอยู่ (บัญชีเป็นไปตามการจับคู่และบัญชีที่ใช้ผ่านรายการ) — ห้ามบันทึกเป็นรายการใหม่ของตนเอง',
  pa_draft_reversal_way_heldPosted: 'ต้นทางของ {{orig}} ลงบัญชีอีกครั้งแล้วเป็น {{held}} ผ่านรายการ {{rv}} ตามที่เป็นอยู่ หากต้องการบันทึกแบบอื่น ให้กลับรายการ {{held}} ผ่านรายการกลับนั้น สร้างรายการใหม่ แล้วบันทึกในร่างใหม่ (ร่างจากเอกสารเลือกบัญชีได้เฉพาะบรรทัดที่ไม่ใช่คู่ค้า ภาษีมูลค่าเพิ่ม หรือเงินสด ร่างรายการชำระเป็นไปตามการจับคู่และบัญชีที่ใช้ผ่านรายการ)',
  pa_draft_reversal_way_heldBlocked: 'ต้นทางของ {{orig}} ถูกสร้างใหม่เป็น {{held}} ซึ่งผ่านรายการไม่ได้แล้ว — ยกเลิก {{held}} ใน Journal ก่อน แล้วผ่านรายการ {{rv}} ตามที่เป็นอยู่และบันทึกต้นทางตามสถานะปัจจุบัน',
  pa_draft_reversal_way_gone: 'ผ่านรายการ {{rv}} ตามที่เป็นอยู่: ต้นทางของ {{orig}} ไม่มีผลแล้ว (ยกเลิก ลบ กลับรายการ หรือถูกเงินเดือนใช้) จึงไม่ถูกบันทึกอีก',
  pa_draft_reversal_way_now: 'ผ่านรายการ {{rv}} ตามที่เป็นอยู่ — จากนั้นต้นทางของ {{orig}} จะสร้างใหม่ได้ สร้างใหม่แล้วบันทึกในร่างใหม่: ร่างจากเอกสารเลือกบัญชีหรือแยกบรรทัดได้เฉพาะบรรทัดที่ไม่ใช่คู่ค้า ภาษีมูลค่าเพิ่ม หรือเงินสด ส่วนร่างรายการชำระให้ผ่านรายการตามที่เป็นอยู่ (บัญชีเป็นไปตามการจับคู่และบัญชีที่ใช้ผ่านรายการ) ห้ามบันทึกเป็นรายการใหม่ของตนเอง',
  pa_draft_reversal_way_fix: 'ผ่านรายการ {{rv}} ตามที่เป็นอยู่ แก้ไขสิ่งที่ทำให้การสร้างรายการไม่รับต้นทางของ {{orig}} แล้วสร้างใหม่และบันทึกในร่างใหม่ (ร่างจากเอกสารเลือกบัญชีหรือแยกบรรทัดได้เฉพาะบรรทัดที่ไม่ใช่คู่ค้า ภาษีมูลค่าเพิ่ม หรือเงินสด ร่างรายการชำระให้ผ่านรายการตามที่เป็นอยู่)',
  pa_draft_reversal_way_later: 'ผ่านรายการ {{rv}} ตามที่เป็นอยู่ ต้นทางของ {{orig}} ลงวันที่หลังงวดที่เปิดอยู่ — เปิดงวดทีละงวดจนถึงงวดที่ครอบคลุมวันที่นั้น สร้างรายการในงวดนั้นแล้วบันทึกในร่างใหม่ (ร่างจากเอกสารเลือกบัญชีได้เฉพาะบรรทัดที่ไม่ใช่คู่ค้า ภาษีมูลค่าเพิ่ม หรือเงินสด ร่างรายการชำระให้ผ่านรายการตามที่เป็นอยู่)',
  pa_draft_reversal_way_redate: 'ผ่านรายการ {{rv}} ตามที่เป็นอยู่ แก้วันที่ของต้นทางของ {{orig}} ในโมดูลภาษีแล้วสร้างใหม่ — หรือหากวันที่ที่ถูกต้องอยู่ในงวดที่ปิดแล้ว ให้บันทึกด้วยมือเป็นรายการที่ทำเพื่อต้นทางนั้น',
  pa_draft_reversal_way_byHand: 'ผ่านรายการ {{rv}} ตามที่เป็นอยู่ แล้วบันทึกจำนวนที่ถูกต้องด้วยมือเป็นรายการที่ทำเพื่อต้นทางของ {{orig}} (Record by hand ใน Manual entries › Reverse) รายการนั้นจะยึดต้นทางไว้ การสร้างรายการจึงไม่บันทึกซ้ำ',
  pa_draft_made_for: 'บันทึกด้วยมือสำหรับ{{what}}',
  pa_draft_edit: 'แก้ไข',
  pa_draft_edit_title: 'แก้ไขร่างรายการบัญชี',
  pa_edit_date: 'วันที่',
  pa_edit_book: 'สมุด',
  pa_edit_bank_acct: 'บัญชีธนาคาร',
  pa_edit_docno: 'เลขที่เอกสาร',
  pa_edit_docno_jv: 'เลขที่ใบสำคัญ (คงเดิม)',
  pa_edit_narration: 'คำอธิบายรายการ',
  pa_edit_calcnote: 'วิธีคำนวณจำนวนเงิน',
  pa_edit_reverse_on: 'วันที่กลับรายการ',
  pa_edit_account: 'บัญชี',
  pa_edit_dr: 'เดบิต',
  pa_edit_cr: 'เครดิต',
  pa_edit_partner: 'คู่ค้า',
  pa_edit_ref: 'เลขอ้างอิง',
  pa_edit_memo: 'คำอธิบายบรรทัด',
  pa_edit_add_line: 'เพิ่มบรรทัด',
  pa_edit_remove_line: 'ลบบรรทัดนี้',
  pa_edit_balanced: 'เดบิตเท่ากับเครดิต',
  pa_edit_unbalanced: 'ไม่สมดุล ต่างกัน {{diff}}',
  pa_edit_save: 'บันทึกการแก้ไข',
  pa_edit_saved: 'บันทึก {{docNo}} แล้ว — ยังเป็นร่างอยู่',
  pa_edit_why: 'เหตุผล (ไม่บังคับ เก็บไว้กับการแก้ไขนี้)',
  pa_edit_jv_month: '{{docNo}} เป็นเลขที่ใบสำคัญของเดือน {{month}}: วันที่ต้องอยู่ในเดือนนั้น หากต้องการบันทึกในเดือนอื่น ให้ยกเลิกแล้วบันทึกใหม่ในเดือนนั้น',
  pa_edit_jv_typed: 'เลขที่ใบสำคัญออกโดยระบบ — ให้พิมพ์เลขที่ของเอกสารภายนอก หรือเว้นว่างไว้',
  pa_edit_review_hint: 'สร้างจากเอกสาร: ที่นี่เลือกบัญชีหรือแยกบรรทัดได้เฉพาะบรรทัดที่ไม่ใช่ลูกค้าหรือผู้ขาย ภาษีมูลค่าเพิ่ม หรือเงินสด ส่วนอื่นให้แก้ที่เอกสาร แล้วรีเฟรชหรือสร้างใหม่',
  pa_edit_changed_elsewhere: 'ร่างนี้ถูกแก้ไขที่อื่นหลังจากคุณเปิด ระบบอ่านใหม่แล้ว — กรุณาแก้ไขอีกครั้ง',
  pa_edit_need_narration: 'ทุกรายการต้องมีคำอธิบายรายการ',
  pa_edit_need_calc: 'ไม่มีเลขที่เอกสารภายนอกจึงเป็นใบสำคัญภายใน: ให้เขียนวิธีคำนวณจำนวนเงิน',
  pa_edit_need_bank: 'รายการในสมุดบัญชีธนาคารต้องระบุบัญชีธนาคาร',
  pa_edit_need_lines: 'อย่างน้อยสองบรรทัด แต่ละบรรทัดมีบัญชีและจำนวนเงินด้านเดียว',
  pa_edit_date_out: 'วันที่ต้องอยู่ในงวดของร่างนี้ {{from}} – {{to}}',
  pa_edit_reverse_inside: 'วันที่กลับรายการต้องหลังสิ้นงวดนี้ ({{to}})',
  pa_edit_account_switched_off: 'บัญชี {{code}} ถูกปิดใช้งานแล้ว — ให้เลือกบัญชีอื่น',
  pa_edit_account_not_postable: 'บัญชี {{code}} ผ่านรายการไม่ได้ — ให้เลือกบัญชีอื่น',
  pa_edit_account_missing: 'บัญชีนี้ไม่อยู่ในผังบัญชีแล้ว — ให้เลือกบัญชีอื่น',
  pa_edit_refused_again: 'กรุณาแก้ไขบางอย่างก่อน: ข้อมูลเดิมจะถูกปฏิเสธอีก',
  pa_draft_mirror_broken: '{{docNo}} ไม่ได้กลับรายการ {{reverses}} ทีละบรรทัดแล้ว — ทิ้ง {{docNo}} แล้วกลับรายการ {{reverses}} ใหม่',
  pa_draft_hand_source_gone: '{{docNo}} เป็นรายการบันทึกด้วยมือสำหรับ{{what}} ซึ่งไม่มีอยู่แล้ว (ถูกถอน ลบ หรือกลับรายการไปแล้ว) — ทิ้ง {{docNo}}: หากผ่านรายการจะบันทึกสิ่งที่ไม่มีอยู่แล้วลงสมุดบัญชี',
  pa_draft_hand_source_held: '{{docNo}} เป็นรายการบันทึกด้วยมือสำหรับ{{what}} ซึ่งขณะนี้มีรายการอื่นบันทึกบัญชีไว้แล้ว — ทิ้ง {{docNo}}: หากผ่านรายการจะบันทึกซ้ำ',
  pa_draft_recorded_by_hand: 'บันทึกด้วยมือแล้ว: {{what}} ถูกถือไว้โดย {{by}} ซึ่งเป็นรายการบันทึกด้วยมือสำหรับแหล่งที่มานั้น — ทิ้งร่างนี้ (ชี้เมาส์เพื่อดูอีกวิธี) มิฉะนั้นทั้งสองรายการจะบันทึกซ้ำ',
  pa_draft_hand_prepaid_taken: '{{docNo}} เป็นรายการบันทึกด้วยมือสำหรับ{{what}} และบันทึกภาษีที่ลูกค้าหัก ณ ที่จ่ายของใบเสร็จเกินกว่าส่วนที่เหลือสำหรับรายการนี้ — คือยอดที่ถูกหักจริงหักด้วยยอดที่รายการอื่นของใบเสร็จบันทึกไว้แล้ว (ชี้เมาส์เพื่อดูว่ารายการใดและเท่าใด) ทิ้ง {{docNo}} แล้วบันทึกด้วยมือใหม่โดยใช้บัญชีภาษีถูกหัก ณ ที่จ่ายเฉพาะส่วนที่เหลือ (ไม่ใช้เลยหากรายการอื่นบันทึกครบแล้ว) หรือนำรายการอื่นออกจากบัญชีก่อน: หากผ่านรายการ ภาษีหัก ณ ที่จ่ายจะถูกบันทึกเกินกว่าที่ถูกหักจริง',
  pa_draft_hand_changed: 'แหล่งที่มาเปลี่ยนแปลง: {{what}} ไม่ตรงกับตอนที่บันทึก {{docNo}} ด้วยมือแล้ว และรายการที่บันทึกด้วยมือจะไม่เปลี่ยนตาม กรุณาตรวจตัวเลขของ {{docNo}} ก่อนผ่านรายการ — หากไม่ตรงแล้ว ให้ทิ้ง {{docNo}} แล้วบันทึกใหม่ (บันทึกด้วยมือ ตรงที่ รายการปรับปรุง › กลับรายการ มีให้ หรือ สร้างจากสมุดย่อย หากระบุว่าทำได้)',
  pa_draft_post_all_hand_left: 'ไม่รวมใน ผ่านรายการทั้งหมด: {{n}} รายการที่บันทึกด้วยมือซึ่งแหล่งที่มาเปลี่ยนแปลงภายหลัง — กรุณาตรวจตัวเลขทีละรายการ แล้วผ่านรายการจากการ์ดของรายการนั้นเอง',
  pa_draft_post_many_hand_changed: '{{docNo}} ยังไม่ได้ผ่านรายการ: แหล่งที่มาเปลี่ยนแปลงหลังบันทึกด้วยมือ กรุณาตรวจตัวเลขแล้วผ่านรายการจากการ์ดของรายการนั้นเอง หรือทิ้งแล้วบันทึกใหม่',
  pa_draft_gen_created: 'สร้างรายการรอตรวจ {{n}} รายการ',
  pa_draft_gen_skipped: 'มีรายการรอตรวจอยู่แล้ว {{n}} รายการ',
  pa_draft_gen_by_hand: 'บันทึกด้วยมือแล้ว ใช้รายการบันทึกด้วยมือของรายการนั้น {{n}} รายการ ({{list}})',
  pa_draft_gen_needs_account: 'ต้องเลือกบัญชี {{n}} รายการ',
  pa_draft_gen_blocked: 'ปฏิเสธ {{n}} บรรทัด — {{list}}',
  pa_draft_gen_blocked_more: ' … และอีก {{n}} รายการ',
  pa_draft_no_doc_no: '(ไม่มีเลขที่)',
  pa_draft_posted_single: 'ผ่านรายการแล้ว',
  pa_draft_posted_n: 'ผ่านรายการแล้ว {{n}} รายการ',

  // ── SplitLeg — แยกรายการหนึ่งไปหลายบัญชีด้วยมือ และจำบัญชีไว้ใช้ครั้งต่อไป ──────────────
  pa_split_btn: 'แยก',
  pa_split_btn_title: 'แยกรายการนี้ไปหลายบัญชี',
  pa_split_head: 'แยก {{amount}} ไปหลายบัญชี',
  pa_split_now_on: 'ขณะนี้อยู่ที่ {{account}}',
  pa_split_account: 'บัญชี',
  pa_split_amount: 'จำนวนเงิน',
  pa_split_choose: '— เลือกบัญชี —',
  pa_split_add_row: 'เพิ่มแถว',
  pa_split_remove_row: 'ลบแถวนี้',
  pa_split_even: 'แบ่งเท่า ๆ กัน',
  pa_split_carries_odd: 'แถวนี้รับเศษ {{amount}}',
  pa_split_remaining: 'ยังเหลือ {{amount}} ที่ยังไม่ได้ลง',
  pa_split_over: 'เกินจากรายการนี้ {{amount}}',
  pa_split_balanced: 'ผลรวมของแถวเท่ากับรายการนี้พอดี',
  pa_split_pin_why: 'ผลรวมของทุกแถวต้องเท่ากับ {{amount}} ซึ่งเป็นจำนวนของรายการนี้เอง ไม่เช่นนั้นรายการจะไม่ดุล — เซิร์ฟเวอร์ตรวจอีกครั้งก่อนบันทึก',
  pa_split_draft_only: 'การแยกนี้เปลี่ยนเฉพาะร่าง ยังไม่มีอะไรผ่านรายการจนกว่าคุณจะผ่านรายการเอง',
  pa_split_save: 'บันทึกการแยก',
  pa_split_cancel: 'ยกเลิก',
  pa_split_bad_amount: 'แถวที่ {{n}} ไม่ใช่จำนวนเงิน',
  pa_split_dup: 'มีสองแถวที่ระบุบัญชีเดียวกัน — รวมเป็นแถวเดียว',
  pa_split_need_account: 'ทุกแถวต้องเลือกบัญชี',
  pa_split_saved: 'บันทึกการแยกแล้ว ยังไม่มีการผ่านรายการ',
  pa_split_group_other: 'แยกเป็น {{count}} แถว',
  pa_split_group_of: '{{amount}} ที่เคยอยู่ที่ {{account}}',
  pa_split_by: 'โดย {{name}}',
  pa_split_undo: 'ยกเลิกการแยก',
  pa_split_undo_confirm: 'ยกเลิกการแยกนี้หรือไม่ ทุกแถวจะกลับไปรวมเป็นรายการเดียวที่ {{account}}',
  pa_split_undone: 'ยกเลิกการแยกแล้ว — รายการกลับไปอยู่บัญชีเดียว',
  pa_rem_link: 'จำไว้',
  pa_rem_head: 'จำ {{account}} ไว้ใช้ครั้งต่อไป',
  pa_rem_party: 'เอกสารจาก {{name}}',
  pa_rem_which: 'บัญชีนี้ใช้กับค่าบริการรายการใดในเอกสารนี้',
  pa_rem_other: 'อื่น ๆ…',
  pa_rem_type: 'พิมพ์ชื่อค่าบริการตามที่เอกสารเขียนไว้',
  pa_rem_none: 'เอกสารนี้ไม่ได้ระบุชื่อค่าบริการ กรุณาพิมพ์ตามที่ปรากฏบนเอกสาร',
  pa_rem_scope_client_only: 'จำไว้สำหรับลูกค้ารายนี้เท่านั้น',
  pa_rem_save: 'จำไว้',
  pa_rem_cancel: 'ยกเลิก',
  pa_rem_saved: 'จำไว้แล้ว: {{label}} → {{account}} ตั้งแต่เอกสารฉบับถัดไปจะลงบัญชีนี้ให้เอง',
  pa_rem_need_label: 'ระบุว่าบัญชีนี้ใช้กับค่าบริการรายการใด',
  pa_rem_why: 'การแยกมีผลกับเอกสารฉบับนี้เท่านั้น การจำไว้เป็นอีกการกระทำหนึ่งที่ต้องยืนยันที่นี่ จึงจะกลายเป็นค่าตั้งต้นของครั้งต่อไป',


  pa_chart_title: 'ผังบัญชี',
  pa_chart_download: 'ดาวน์โหลด PDF',
  pa_chart_download_failed: 'สร้าง PDF ไม่สำเร็จ',
  pa_chart_counts: '{{n}} บัญชี · ใช้งานแล้ว {{used}} บัญชี',
  pa_chart_empty: 'ยังไม่มีบัญชี — คัดลอกแม่แบบของสำนักงานเพื่อเริ่มต้น',
  pa_chart_copy_template: 'คัดลอกแม่แบบของสำนักงาน',
  pa_chart_sync_template: 'ซิงค์จากแม่แบบ',
  pa_chart_sync_title: 'ดูตัวอย่างบัญชีในแม่แบบที่ผังบัญชีนี้ยังไม่มี ผู้ดูแลระบบเป็นผู้เพิ่มบัญชีที่ทำเครื่องหมายไว้',
  pa_chart_add_account: 'เพิ่มบัญชี',
  pa_chart_open: 'เปิด',
  pa_chart_hide: 'ซ่อน',
  pa_chart_ph_code: 'รหัสบัญชี',
  pa_chart_ph_name_th: 'ชื่อบัญชี (ภาษาไทย)',
  pa_chart_ph_name_en: 'ชื่อบัญชี (ภาษาอังกฤษ)',
  pa_chart_save: 'บันทึก',
  pa_chart_cancel: 'ยกเลิก',
  pa_chart_switch_off: 'ปิดใช้',
  pa_chart_switch_on: 'เปิดใช้',
  pa_chart_name_th_manual: 'ชื่อไทยเป็นคู่มือแปลรหัสบัญชีตาม ม.21(1) — ต้องระบุ',
  pa_chart_template_complete: 'ผังบัญชีนี้มีบัญชีครบทุกบัญชีในแม่แบบของสำนักงานแล้ว',
  pa_chart_remove_confirm: 'ลบบัญชี {{code}} {{name}} หรือไม่?',
  pa_chart_class_asset: 'สินทรัพย์',
  pa_chart_class_liability: 'หนี้สิน',
  pa_chart_class_equity: 'ส่วนของเจ้าของ',
  pa_chart_class_income: 'รายได้',
  pa_chart_class_expense: 'ค่าใช้จ่าย',
  pa_chart_cf_prefixed: 'งบกระแสเงินสด: {{section}}',
  pa_chart_cf_none: '— ยังไม่ระบุ —',
  pa_chart_cf_operating: 'กิจกรรมดำเนินงาน',
  pa_chart_cf_investing: 'กิจกรรมลงทุน',
  pa_chart_cf_financing: 'กิจกรรมจัดหาเงิน',
  pa_chart_cf_cash: 'เงินสดและรายการเทียบเท่า',
  pa_chart_cf_noncash: 'ไม่ใช่เงินสด',
  pa_chart_cf_missing_warn: '⚠️ บัญชีสินทรัพย์/หนี้สิน/ส่วนของเจ้าของ ที่ไม่ได้เลือกหมวดกระแสเงินสด จะทำให้งบกระแสเงินสดไม่ดุล (ต่างกันเท่ากับจำนวนเงินที่ผ่านบัญชีนี้พอดี)',
  pa_chart_cf_missing_title: 'งบกระแสเงินสดจะไม่ดุลจนกว่าบัญชีนี้จะเลือกหมวด',
  pa_chart_cf_missing_pill: 'ไม่มีงบกระแสเงินสด',
  pa_chart_fs_prefixed: 'บัญชีทำการ: {{section}}',
  pa_chart_fs_none: '— ยังไม่ระบุ —',
  pa_chart_fs_trading: 'อยู่ใน — ขาย/ต้นทุนขาย',
  pa_chart_fs_other: 'ไม่อยู่ใน — ใต้กำไรขั้นต้น',
  pa_chart_fs_missing_title: 'บัญชีนี้มีจำนวนเงินแต่ยังไม่ได้ระบุที่แสดง บัญชีทำการ จะแสดงแยกไว้ ไม่รวมในยอดรวม',
  pa_chart_fs_missing_pill: 'ไม่มีบัญชีทำการ',
  pa_chart_wd_title: 'มีการปรับลดมูลค่าสินค้าคงเหลือ — แสดงยอดนี้ไว้ที่ใด',
  pa_chart_wd_account: '{{code}} {{name}} · {{amount}}',
  pa_chart_wd_q: 'ลูกค้ารายนี้มีการปรับลดมูลค่าสินค้าคงเหลือ เลือกว่าจะแสดงยอดนี้ไว้ที่ใดในบัญชีทำการ',
  pa_chart_wd_trading: 'รวมอยู่ในต้นทุนขาย',
  pa_chart_wd_trading_hint: 'บัญชีทำการ รวมยอดนี้ กำไรขั้นต้นในงบนั้นจึงลดลงเท่ากับยอดนี้',
  pa_chart_wd_other: 'แสดงใต้กำไรขั้นต้น',
  pa_chart_wd_other_hint: 'บัญชีทำการ ไม่รวมยอดนี้ กำไรขั้นต้นในงบนั้นจึงสูงขึ้นเท่ากับยอดนี้',
  pa_chart_wd_unanswered: 'จนกว่าจะมีคนตอบ บัญชีทำการ จะแสดงบัญชีนี้พร้อมจำนวนเงินและแจ้งเตือนไว้ และไม่รวมในยอดรวมทั้งสองฝั่ง',
  pa_chart_wd_note: 'ทั้งสองทางเลือกไม่เปลี่ยนตัวเลขใดในสมุดบัญชี เปลี่ยนเฉพาะที่แสดงรายการ การเลือกเป็นไปตามแม่บทการรายงานทางการเงินของลูกค้าเอง ระบบไม่เลือกให้ บันทึกครั้งเดียวต่อลูกค้าหนึ่งรายและจะไม่ถามซ้ำ',
  pa_chart_wd_failed: 'บันทึกไม่สำเร็จ ยังไม่มีการเปลี่ยนแปลงใด',

  // ── CompliancePanel — Accounting Act records ──────────────────────────────────
  pa_compliance_title: 'ตรวจสอบบัญชีและการยื่นต่อกรมพัฒนาธุรกิจการค้า',
  pa_compliance_file_by: 'ยื่นภายใน {{date}}',
  pa_compliance_due_unknown: 'ยังคำนวณกำหนดยื่นไม่ได้',
  pa_compliance_record_meeting_audit: 'บันทึกการประชุม/ตรวจสอบ',
  pa_compliance_record_filing: 'บันทึกการยื่น',
  pa_compliance_audit_unknown: '⚠ ยังสรุปไม่ได้ว่าต้องตรวจสอบบัญชีหรือไม่',
  pa_compliance_audit_required: 'ต้องตรวจสอบบัญชีโดยผู้สอบบัญชีรับอนุญาต',
  pa_compliance_audit_exempt: 'ได้รับยกเว้นการตรวจสอบบัญชี',
  pa_compliance_unbacked: '{{n}} รายการไม่มีเอกสารประกอบ ไม่มีเลขที่ และไม่ได้ระบุที่มา — ผ่านรายการไม่ได้ (ข้อ 5)',
  pa_compliance_filed_msg: 'บันทึกการยื่น {{kind}} วันที่ {{date}} แล้ว',
  pa_compliance_agm_due: 'ประชุมใหญ่ภายใน {{date}}',
  pa_compliance_agm_held: 'ประชุมเมื่อ {{date}}',
  pa_compliance_agm_not_held: 'ยังไม่ได้ประชุม',
  pa_compliance_statements_due: 'ส่งงบให้ผู้ถือหุ้นภายใน {{date}}',
  pa_compliance_bojo5_due: 'ยื่น บอจ.5 ภายใน {{date}}',
  pa_compliance_filed_label: 'ยื่นแล้ว:',
  pa_compliance_bookkeeper_label: 'ผู้ทำบัญชี:',
  pa_compliance_tfac: '(TFAC {{no}})',
  pa_compliance_tfac_lapsed: 'หมดสมาชิกภาพ',
  pa_compliance_no_bookkeeper: 'ยังไม่ได้แต่งตั้ง (Accounting Act s.19)',
  pa_compliance_eacct_pending: 'ยังไม่ได้แจ้ง e-Accountant สำหรับ {{name}} (ภายใน 30 วันนับแต่ {{date}})',
  pa_compliance_notified_today: 'แจ้งแล้ววันนี้',
  pa_compliance_f_agm_date: 'วันประชุมใหญ่',
  pa_compliance_f_statements_sent: 'ส่งงบให้ผู้ถือหุ้น',
  pa_compliance_f_audit_completed: 'ตรวจสอบเสร็จ',
  pa_compliance_f_opinion: 'ความเห็น',
  pa_compliance_opinion_unqualified: 'ไม่มีเงื่อนไข',
  pa_compliance_opinion_qualified: 'มีเงื่อนไข',
  pa_compliance_opinion_adverse: 'ไม่ถูกต้อง',
  pa_compliance_opinion_disclaimer: 'ไม่แสดงความเห็น',
  pa_compliance_f_auditor: 'ผู้สอบบัญชี',
  pa_compliance_f_auditor_license: 'เลขทะเบียน',
  pa_compliance_f_auditor_kind: 'ประเภท',
  pa_compliance_auditor_cpa: 'ผู้สอบบัญชีรับอนุญาต (CPA)',
  pa_compliance_auditor_ta: 'ผู้สอบบัญชีภาษีอากร (TA)',
  pa_compliance_f_audit_fee: 'ค่าสอบบัญชี',
  pa_compliance_save: 'บันทึก',
  pa_compliance_f_kind: 'ยื่นอะไร',
  pa_compliance_kind_fs: 'งบการเงิน',
  pa_compliance_kind_fs_gloss: 'Financial statements',
  pa_compliance_kind_bojo5: 'บอจ.5',
  pa_compliance_kind_bojo5_gloss: 'Shareholder list',
  pa_compliance_f_filed_on: 'วันที่ยื่น',
  pa_compliance_f_receipt_no: 'เลขที่ใบรับ',
  pa_compliance_record: 'บันทึก',

  // ── FixedAssets — the asset register ──────────────────────────────────────────
  pa_assets_register_name: 'ทะเบียนทรัพย์สิน',
  pa_assets_title: 'Fixed assets',
  pa_assets_summary: 'ทรัพย์สิน {{n}} รายการ · มูลค่าสุทธิ {{nbv}}',
  pa_assets_empty: 'ยังไม่มีรายการในทะเบียน',
  pa_assets_due: 'ค่าเสื่อมที่ต้องคิดในงวดนี้ {{amount}}',
  pa_assets_open_register: 'ทะเบียน',
  pa_assets_charge_dep: 'คิดค่าเสื่อม {{amount}}',
  pa_assets_dep_confirm: 'คิดค่าเสื่อมราคา {{amount}} สำหรับงวดนี้?\n\nระบบจะสร้างเป็นร่าง — โปรดตรวจสอบที่สมุดรายวันแล้วผ่านรายการก่อนจึงจะเข้าบัญชีแยกประเภท',
  pa_assets_add: 'ทรัพย์สิน',
  pa_assets_open: 'เปิด',
  pa_assets_hide: 'ซ่อน',
  pa_assets_registered_msg: 'บันทึก {{no}} แล้ว อัตราปีละ {{rate}}%',
  pa_assets_dep_draft: 'ทรัพย์สิน {{n}} รายการ {{amount}} — สร้างเป็นร่างเพื่อตรวจสอบ',
  pa_assets_dep_none: 'คิดค่าเสื่อมของทรัพย์สินทุกรายการในงวดนี้แล้ว',
  pa_assets_f_class: 'ประเภท',
  pa_assets_no_dep: 'ไม่หักค่าเสื่อม',
  pa_assets_f_acquired: 'ได้มาเมื่อ',
  pa_assets_f_cost: 'ราคาทุน',
  pa_assets_f_salvage: 'มูลค่าคงเหลือ',
  pa_assets_import_btn: 'นำเข้าทะเบียนสินทรัพย์',
  pa_assets_import_note: 'ไฟล์ CSV หรือ .xlsx ของทะเบียนสินทรัพย์ หนึ่งแถวต่อหนึ่งรายการ แถวแรกเป็นชื่อคอลัมน์ เป็นภาษาอังกฤษ ไทย หรือจีน เรียงลำดับอย่างไรก็ได้ ต้องมี: “ชื่อ” (และ “ชื่ออังกฤษ” ถ้ามี) “วันที่ได้มา” และ “ราคาทุน” ไม่บังคับ: “หมวดสินทรัพย์” (แถวที่ไม่ได้ระบุจะใช้หมวดที่เลือกไว้ที่นี่) “ประเภทสินทรัพย์” “อัตราค่าเสื่อม” “มูลค่าคงเหลือ” “บัญชีสินทรัพย์” “บัญชีค่าเสื่อมสะสม” และ “บัญชีค่าใช้จ่าย” (ใส่รหัสบัญชี) “ค่าเสื่อมสะสมยกมา” พร้อม “ถึงวันที่” “หมายเหตุ” “ผู้ขาย” “เลขที่ใบกำกับ” “ต้นทุนที่บันทึกเพิ่ม” “วันที่พร้อมใช้งาน” “วันที่เริ่มคิดค่าเสื่อม” “สถานที่” หรือ “แผนก” “สถานะ” (“ใช้งานอยู่” “ยังไม่พร้อมใช้งาน” หรือ “ไม่ได้ใช้งาน”) และ “อายุการใช้งาน (ปี)” คอลัมน์ที่มีหัวอื่นจะถูกข้าม และการตรวจไฟล์จะแจ้งชื่อไว้ ทุกแถวถูกตรวจเหมือนการบันทึกทีละรายการ: ต้องมีหมวด ประเภทที่คิดค่าเสื่อมต้องมีอัตราและไม่เกินเพดานตามพระราชกฤษฎีกา มูลค่าคงเหลือต้องต่ำกว่าราคาทุน และบัญชีทั้งสามต้องอยู่ในผังบัญชีของลูกค้ารายนี้',
  pa_assets_import_preview: 'ตรวจไฟล์',
  pa_assets_import_commit: 'บันทึกเข้าทะเบียน',
  pa_assets_import_summary: 'บันทึกได้ {{ok}} รายการ · ถูกปฏิเสธ {{refused}} รายการ',
  pa_assets_import_will_add: 'บันทึกได้',
  pa_assets_import_ignored: 'จะข้าม {{n}} คอลัมน์ — ข้อมูลในคอลัมน์เหล่านี้จะไม่ถูกบันทึก ถ้าคอลัมน์ใดจำเป็น ให้เปลี่ยนหัวคอลัมน์เป็นชื่อที่คำอธิบายด้านบนระบุ (หรือลบคอลัมน์นั้น) แล้วตรวจไฟล์อีกครั้ง',
  pa_assets_import_done: 'บันทึกสินทรัพย์แล้ว {{n}} รายการ',
  pa_assets_cadence_annual: 'คิดค่าเสื่อมปีละครั้ง',
  pa_assets_cadence_monthly: 'คิดค่าเสื่อมรายเดือน',
  pa_assets_month: 'คิดค่าเสื่อมเดือนใด',
  pa_assets_pick_month: 'กรุณาเลือกเดือนที่จะคิดค่าเสื่อม',
  pa_assets_f_opening: 'ค่าเสื่อมราคาสะสมยกมา',
  pa_assets_f_opening_as_of: '…ถึงวันที่',
  pa_assets_opening_hint: 'สำหรับสินทรัพย์ที่ใช้งานมาก่อนเริ่มทำบัญชีในระบบนี้ หากซื้อหลังเริ่มใช้ระบบให้เว้นว่างทั้งสองช่อง ระบบจะเริ่มคิดค่าเสื่อมจากวันถัดจากวันที่ระบุ ช่วงที่คิดไปแล้วจึงไม่ถูกคิดซ้ำ',
  pa_assets_f_rate: 'อัตรา %',
  pa_assets_f_name_th: 'รายการ (ภาษาไทย) — ต้องระบุ',
  pa_assets_f_name_en: 'รายการ (ภาษาอังกฤษ)',
  pa_assets_f_acct_asset: 'บัญชีทรัพย์สิน',
  pa_assets_f_acct_accum: 'ค่าเสื่อมสะสม',
  pa_assets_f_acct_expense: 'ค่าใช้จ่ายค่าเสื่อม',
  pa_assets_max_rate_source: 'อัตราสูงสุดตาม {{source}}',

  // ── Asset categories (batch 5 round 8, H2a) ───────────────────────────────────
  pa_cat_title: 'หมวดสินทรัพย์',
  pa_cat_count: 'หมวดทั้งหมด {{n}} รายการ',
  pa_cat_none: 'ยังไม่มีหมวดสินทรัพย์ — บันทึกทรัพย์สินได้โดยไม่ต้องเลือกหมวด',
  pa_cat_add: 'หมวดสินทรัพย์',
  pa_cat_th_name: 'หมวด',
  pa_cat_th_class: 'ประเภทตามพระราชกฤษฎีกา · อัตรา',
  pa_cat_th_accounts: 'บัญชี',
  pa_cat_th_assets: 'ทรัพย์สิน',
  pa_cat_edit: 'แก้ไข',
  pa_cat_on: 'เปิดใช้',
  pa_cat_off: 'ปิดใช้',
  pa_cat_delete: 'ลบ',
  pa_cat_delete_confirm: 'ลบหมวด "{{name}}" หรือไม่?\n\nลบได้เฉพาะเมื่อไม่มีทรัพย์สินรายการใดเคยบันทึกไว้ในหมวดนี้ รวมถึงรายการที่จำหน่ายไปแล้ว หากไม่ต้องการใช้ต่อให้ปิดใช้แทน',
  pa_cat_inactive: 'ปิดใช้แล้ว',
  pa_cat_saved: 'บันทึกหมวดแล้ว',
  pa_cat_f_name_th: 'ชื่อหมวด (ภาษาไทย) — ต้องระบุ',
  pa_cat_f_name_en: 'ชื่อหมวด (ภาษาอังกฤษ)',
  pa_cat_f_class: 'ประเภทตามพระราชกฤษฎีกา (เพดานอัตรา)',
  pa_cat_f_nature: 'ลักษณะการคิดค่า',
  pa_cat_f_rate: 'อัตรา %',
  pa_cat_nature_depreciation: 'ค่าเสื่อมราคา',
  pa_cat_nature_amortisation: 'ค่าตัดจำหน่าย',
  pa_cat_nature_none: 'ไม่คิดค่าเสื่อม (ที่ดิน)',
  pa_cat_never_note: 'ที่ดินเป็นการถือครองและไม่คิดค่าเสื่อม หมวดนี้จึงไม่มีบัญชีค่าเสื่อมสะสม ไม่มีบัญชีค่าใช้จ่าย และไม่มีอัตรา และอ้างถึงได้เฉพาะประเภทที่พระราชกฤษฎีกาไม่ให้หักค่าสึกหรอ',
  pa_cat_note: 'หมวดเป็นการจัดกลุ่มทางบัญชีของลูกค้าเอง ประเภทตามพระราชกฤษฎีกาที่หมวดอ้างถึงใช้เพียงกำหนดเพดานอัตราเท่านั้น เมื่อบันทึกทรัพย์สิน ระบบจะคัดลอกบัญชีทั้งสามและอัตราของหมวดไปเก็บไว้ที่ทรัพย์สินรายการนั้น การแก้หมวดในภายหลังจึงไม่กระทบทรัพย์สินที่บันทึกไว้แล้ว',
  pa_assets_f_category: 'หมวด',
  pa_assets_f_category_none: 'ไม่ระบุหมวด',
  pa_assets_f_category_pick: 'เลือกหมวดสินทรัพย์…',
  pa_assets_import_category: 'หมวดสำหรับแถวที่ไม่ได้ระบุหมวด',
  pa_assets_cancel: 'ยกเลิก',
  pa_assets_submit: 'บันทึก',
  pa_assets_th_no: 'เลขที่',
  pa_assets_th_item: 'รายการ',
  pa_assets_th_acquired: 'ได้มา',
  pa_assets_th_cost: 'ราคาทุน',
  pa_assets_th_accum: 'ค่าเสื่อมสะสม',
  pa_assets_th_nbv: 'มูลค่าสุทธิ',
  pa_assets_th_year: 'ปีนี้',
  pa_assets_disposed_on: 'จำหน่าย {{date}}',
  pa_assets_days: '{{n}} วัน',

  // ── ManualEntry — the general journal ─────────────────────────────────────────
  pa_manual_book_general_gloss: 'General',
  pa_manual_book_cash_gloss: 'Cash',
  pa_manual_book_bank_gloss: 'Bank',
  pa_manual_book_purchase_gloss: 'Purchase',
  pa_manual_book_sale_gloss: 'Sale',
  pa_manual_title: 'รายการปรับปรุง',
  pa_manual_subtitle: 'รายการค้างรับค้างจ่าย ค่าใช้จ่ายจ่ายล่วงหน้า ค่าเสื่อม และประมาณการหนี้สิน — และการกลับรายการ ซึ่งเป็นวิธีเดียวที่จะแก้ไขรายการที่บันทึกบัญชีแล้ว',
  pa_manual_new_btn: 'บันทึกรายการ',
  pa_manual_reverse_btn: 'กลับรายการ',
  pa_manual_sweep_title: 'ดึงรายการค้างรับค้างจ่ายจากรอบระยะเวลาบัญชีก่อนที่ต้องกลับรายการในรอบนี้เข้ามา',
  pa_manual_accruals_btn: 'รายการค้างรับค้างจ่าย',
  pa_manual_import_btn: 'นำเข้าไฟล์',
  pa_manual_import_note: 'ไฟล์ CSV ของใบสำคัญของท่าน: หนึ่งแถวต่อหนึ่งบรรทัดของรายการ แถวที่มีเลขที่เอกสารเดียวกันถือเป็นใบสำคัญเดียวกัน อ่านคอลัมน์จากชื่อหัวตาราง — วันที่ เลขที่เอกสาร คำอธิบาย รหัสบัญชี เดบิต เครดิต ทุกใบสำคัญถูกตรวจเหมือนการบันทึกด้วยมือ: ต้องดุล บัญชีต้องอยู่ในผังบัญชีของลูกค้ารายนี้ วันที่ต้องอยู่ในรอบที่ยังไม่ปิด และใบสำคัญภายในที่ไม่มีเลขที่เอกสารต้องระบุวิธีคำนวณ',
  pa_manual_import_preview: 'ตรวจไฟล์',
  pa_manual_import_commit: 'บันทึกเข้าระบบ',
  pa_manual_import_blocked: 'มีใบสำคัญบางรายการที่บันทึกไม่ได้ — แก้ในไฟล์แล้วตรวจใหม่ ระบบจะไม่นำเข้าเลยตราบใดที่ยังมีรายการถูกปฏิเสธ',
  pa_manual_import_summary: 'บันทึกได้ {{ok}} ใบ · ถูกปฏิเสธ {{refused}} ใบ',
  pa_manual_import_will_enter: 'บันทึกได้',
  pa_manual_import_done: 'บันทึกเป็นร่างแล้ว {{n}} ใบ — ตรวจและผ่านรายการ',
  pa_manual_copy_btn: 'ทำซ้ำ',
  pa_manual_copy_title: 'บันทึกใบสำคัญเดิมอีกครั้งในวันอื่น — ค่าธรรมเนียมธนาคารรายเดือน หรือรายการค้างจ่ายประจำ ระบบจะออกเลขที่ใหม่ให้',
  pa_manual_copy_ref_hand: '{{docNo}} เป็นรายการบันทึกด้วยมือสำหรับแหล่งที่มา หรือกลับรายการดังกล่าว จึงทำซ้ำไม่ได้: การทำซ้ำจะบันทึกตัวเลขเดิมอีกครั้งโดยไม่ได้ถือแหล่งที่มาใดไว้ หากต้องการบันทึกแหล่งที่มาด้วยมือ ให้ใช้ บันทึกด้วยมือ ตรงที่รายการนี้มีให้',
  pa_manual_copy_ref_settlement: '{{docNo}} เป็นรายการบันทึกชำระที่สร้างจากรายการเดินบัญชีธนาคารหรือรายการเงินสดย่อย (หรือกลับรายการดังกล่าว) จึงทำซ้ำไม่ได้: การทำซ้ำจะบันทึกเงินจำนวนนั้นอีกครั้งโดยไม่ได้ถือแหล่งที่มาใดไว้ เมื่อแหล่งที่มาว่างแล้ว การสร้างรายการจะบันทึกชำระใหม่ สิ่งที่สร้างไม่ได้ให้ใช้ บันทึกด้วยมือ',
  pa_manual_copy_prompt: 'ทำซ้ำ {{docNo}} ในวันที่ใด? (YYYY-MM-DD)',
  pa_manual_copy_done: 'บันทึกเป็น {{docNo}} แล้ว — ตรวจและผ่านรายการ',
  pa_manual_late_entries: 'รายการที่ผ่านรายการเกิน 15 วันนับจากวันที่ของรายการ: {{n}} รายการ — ช้าที่สุด {{days}} วัน ({{docNo}})',
  pa_manual_late_rule: 'DBD กำหนดให้บันทึกสมุดรายวันภายใน 15 วันนับจากวันที่เกิดรายการ',
  pa_manual_closing_overdue: 'รอบระยะเวลาบัญชีนี้สิ้นสุดเมื่อ {{date}} ผ่านมาแล้ว {{days}} วัน และยังเปิดอยู่ ต้องปิดบัญชีภายใน 60 วัน',
  pa_manual_saved: 'สร้างรายการรอตรวจ {{docNo}} แล้ว — ตรวจสอบด้านล่างแล้วผ่านรายการ',
  pa_manual_reverse_confirm: 'กลับรายการ {{docNo}} หรือไม่? รายการเดิมยังอยู่ในสมุดบัญชีพร้อมเลขหน้าเดิม การแก้ไขจะถูกสร้างเป็นรายการรอตรวจไว้ข้างกัน',
  pa_manual_reversed_msg: 'สร้าง {{docNo}} เป็นรายการรอตรวจ วันที่ {{date}}',
  pa_manual_sweep_done: 'สร้างรายการกลับรายการค้างรับค้างจ่าย {{n}} รายการเป็นรายการรอตรวจ',
  pa_manual_sweep_none: 'ไม่มีรายการค้างรับค้างจ่ายจากรอบระยะเวลาบัญชีก่อนที่ต้องกลับรายการเข้ารอบนี้',
  pa_manual_f_date: 'วันที่',
  pa_manual_f_book: 'สมุด',
  pa_manual_f_bank_acct: 'เลขที่บัญชีธนาคาร',
  pa_manual_f_bank_acct_hint: 'หนึ่งเล่มต่อหนึ่งเลขที่บัญชี',
  pa_manual_f_bank_acct_ph: 'เช่น XXX-X-XXXXX-X',
  pa_manual_bank_acct_new: 'บัญชีอื่น…',
  pa_manual_bank_acct_used: 'มีแล้ว {{n}} รายการ',
  pa_manual_bank_acct_none: 'ยังไม่มีบัญชีธนาคารสำหรับลูกค้ารายนี้ — พิมพ์เลขที่บัญชี แล้วครั้งต่อไปจะแสดงให้เลือก',
  pa_manual_f_docno: 'เลขที่เอกสาร',
  pa_manual_f_docno_hint: '(เว้นว่าง → ระบบออกเลข JV)',
  pa_manual_f_docno_ph: 'JV-… ระบบออกให้',
  pa_manual_f_reverse_on: 'กลับรายการวันที่',
  pa_manual_f_reverse_on_hint: '(รายการค้างรับค้างจ่าย)',
  pa_manual_narration_ph: 'คำอธิบายรายการ — ต้องระบุ',
  pa_manual_calcnote_ph: 'วิธีคำนวณ — ต้องระบุเมื่อไม่มีเอกสารประกอบ (ข้อ 4)',
  pa_manual_th_account: 'บัญชี',
  pa_manual_th_dr: 'เดบิต',
  pa_manual_th_cr: 'เครดิต',
  pa_manual_pick_account: '— เลือกบัญชี —',
  pa_manual_add_line: 'บรรทัด',
  pa_manual_no_amounts: 'ยังไม่มีจำนวนเงิน',
  pa_manual_balances: 'ดุล',
  pa_manual_out_by: 'ต่างกัน {{amount}}',
  pa_manual_save_draft: 'บันทึกเป็นร่าง',
  pa_manual_reverse_note: 'รายการที่บันทึกบัญชีแล้วจะไม่ถูกยกเลิกการผ่านรายการ รายการเดิมยังอยู่ที่เดิมพร้อมเลขหน้า และการแก้ไขคือรายการใหม่ที่บันทึกไว้ข้างกัน ซึ่งเป็นสิ่งที่ทำให้เกิดร่องรอยการตรวจสอบ',
  pa_manual_search_ph: 'ค้นหาเลขที่เอกสารหรือคำอธิบาย…',
  pa_manual_filter_by_hand: 'บันทึกด้วยมือได้',
  pa_manual_none_posted: 'ไม่พบรายการที่บันทึกบัญชีแล้วที่ตรงกัน',
  pa_manual_reversed_by: 'กลับแล้ว {{docNo}}',
  pa_manual_rev_ref_asset: 'กลับรายการที่นี่ไม่ได้: {{docNo}} ติดกฎของสินทรัพย์ถาวร (ชี้เมาส์เพื่อดูเหตุผล)',
  pa_manual_rev_ref_asset_reversal: '{{docNo}} ได้คืนทะเบียนสินทรัพย์แล้ว — ให้คำนวณค่าเสื่อมราคาหรือจำหน่ายใหม่แทนการกลับรายการนี้',
  pa_manual_rev_ref_reversal_of_released: 'กลับรายการที่นี่ไม่ได้: {{docNo}} กลับรายการที่แหล่งที่มาถูกปล่อยแล้ว (ชี้เมาส์เพื่อดูเหตุผล)',
  pa_manual_rev_ref_no_open_period: 'ไม่มีงวดบัญชีที่เปิดอยู่สำหรับรายการกลับบัญชีของ {{docNo}}',
  pa_manual_rev_ref_no_lines: '{{docNo}} ไม่มีบรรทัดให้กลับรายการ',
  pa_manual_by_hand_btn: 'บันทึกด้วยมือ',
  pa_manual_by_hand_title: 'บันทึกการแก้ไขเป็นรายการด้วยมือสำหรับแหล่งที่มานี้: รายการนี้ถือแหล่งที่มาไว้ การสร้างจากสมุดย่อยจึงไม่บันทึกซ้ำตราบที่รายการนี้ยังอยู่',
  pa_manual_for_tax_line: 'เอกสารต้นทาง {{label}}',
  pa_manual_for_bank_line: 'รายการเดินบัญชี {{label}}',
  pa_manual_prepaid_taken: 'ภาษีที่ลูกค้าหัก ณ ที่จ่ายของ {{receipt}} ถูกบันทึกไว้แล้วโดย {{by}} — {{way}}',
  pa_manual_prepaid_by_settlement: 'รายการบันทึกชำระของรายการเดินบัญชี {{line}}',
  pa_manual_prepaid_by_hand: '{{docNo}} (บันทึกด้วยมือสำหรับรายการเดินบัญชี {{line}})',
  pa_manual_prepaid_way_discard: 'กรุณานำบัญชี {{codes}} ออกจากรายการนี้ หรือทิ้งร่างนั้นก่อน',
  pa_manual_prepaid_way_reverse: 'กรุณานำบัญชี {{codes}} ออกจากรายการนี้ หรือกลับรายการนั้นและผ่านรายการกลับก่อน',
  pa_manual_prepaid_way_post_reversal: 'กรุณานำบัญชี {{codes}} ออกจากรายการนี้ หรือผ่านรายการกลับ {{rv}} ของรายการนั้นก่อน',
  pa_manual_prepaid_way_closed: 'รายการนั้นผ่านรายการในงวดบัญชีที่ปิดแล้วและคงอยู่ในบัญชี — กรุณานำบัญชี {{codes}} ออกจากรายการนี้',
  pa_manual_prepaid_unclear_self: 'รายการเดินบัญชีนี้จับคู่กับใบแจ้งหนี้ {{invoice}} ซึ่งมีใบเสร็จที่มีภาษีหัก ณ ที่จ่ายมากกว่าหนึ่งใบ ({{receipts}}) — จึงบอกไม่ได้ว่ารายการนี้บันทึกภาษีหัก ณ ที่จ่ายของใบเสร็จใด กรุณาจับคู่รายการเดินบัญชีกับใบเสร็จแทนใบแจ้งหนี้ หรือนำบัญชี {{codes}} ออกจากรายการนี้',
  pa_manual_prepaid_unclear_other: '{{docNo}} (บันทึกด้วยมือสำหรับรายการเดินบัญชี {{line}} ที่จับคู่กับใบแจ้งหนี้ {{invoice}}) อาจบันทึกภาษีที่ลูกค้าหัก ณ ที่จ่ายของ {{receipt}} ไว้แล้ว — ใบแจ้งหนี้นั้นมีใบเสร็จที่มีภาษีหัก ณ ที่จ่ายมากกว่าหนึ่งใบ จึงบอกไม่ได้ว่าบันทึกของใบใด กรุณาจับคู่รายการเดินบัญชีนั้นกับใบเสร็จก่อน หรือนำบัญชี {{codes}} ออกจากรายการนี้',
  pa_manual_prepaid_over: 'จากภาษีที่ลูกค้าหัก ณ ที่จ่ายของ {{receipt}} ({{withheld}}) มี {{booked}} ถูกบันทึกไว้แล้วโดย {{by}} — ยอด {{mine}} ในบัญชี {{codes}} ของรายการนี้จะทำให้บันทึกเกินกว่าที่ถูกหักจริง: เดบิตได้ไม่เกิน {{room}}',
  pa_manual_prepaid_over_whole: 'ลูกค้าหักภาษี ณ ที่จ่ายไว้ {{withheld}} ใน {{receipt}} — ยอด {{mine}} ในบัญชี {{codes}} ของรายการนี้มากกว่าที่ถูกหักจริง: เดบิตได้ไม่เกิน {{room}}',
  pa_manual_prepaid_room_hint: 'หากลูกค้าหักภาษีไว้ในการชำระครั้งนี้ รายการนี้บันทึกภาษีหัก ณ ที่จ่ายส่วนที่เหลือ {{room}} ของใบเสร็จ {{receipt}} ในบัญชีภาษีถูกหักจ่ายล่วงหน้าได้',
  pa_manual_prepaid_by_more: '{{by}} และอีก {{n}} รายการของใบเสร็จนั้น',
  pa_manual_prepaid_way_several: 'กรุณานำบัญชี {{codes}} ออกจากรายการนี้ หรือนำรายการใดรายการหนึ่งเหล่านั้นออกจากบัญชีก่อน',
  pa_manual_for_cash_line: 'รายการเงินสดย่อย {{label}}',
  pa_manual_for_banner: 'บันทึกด้วยมือสำหรับ{{what}}: รายการนี้ถือแหล่งที่มานั้นไว้ การสร้างจากสมุดย่อยจึงไม่บันทึกซ้ำตราบที่รายการนี้ยังอยู่',
  pa_manual_row_closed_period: 'งวดที่ปิดแล้ว {{from}} – {{to}}',
  pa_manual_payroll_owned: 'รอบเงินเดือน {{docNo}} — กลับรายการจากรอบที่ เงินเดือน › รายการเงินเดือน ไม่ใช่ที่นี่',
  pa_manual_payroll_pay_owned: 'การจ่าย {{payNo}} ของรอบเงินเดือน {{docNo}} — กลับรายการในหน้าต่างของรอบนั้นที่ เงินเดือน › รายการเงินเดือน ไม่ใช่ที่นี่',
  pa_manual_payroll_remit_owned: 'การนำส่งประจำเดือน {{month}} — กลับรายการที่ เงินเดือน › รายการเงินเดือน › การนำส่ง ไม่ใช่ที่นี่',

  // ── Headless — the records with no other home ─────────────────────────────────
  pa_headless_wa_rule_a: 'Revenue Code s.68 bis — เพื่อการภาษี ทุกบริษัทต้องจัดทำงบดุล ',
  pa_headless_wa_rule_b: 'บัญชีทำการ',
  pa_headless_wa_rule_c: ' และบัญชีกำไรขาดทุน ⚠️ ระบบนี้ไม่มีรูปแบบทางการของ DBD จึงพิมพ์ตามรูปแบบของระบบนี้เอง',
  pa_headless_wa_income: 'รายได้',
  pa_headless_wa_cost: 'ต้นทุน',
  pa_headless_wa_gross: 'กำไรขั้นต้น',
  pa_headless_wa_expenses: 'ค่าใช้จ่าย',
  pa_headless_wa_net: 'กำไร(ขาดทุน)สุทธิ',
  pa_headless_wa_unplaced: 'มี {{n}} บัญชีที่ยังไม่ได้ระบุว่าอยู่ในบัญชีทำการ — กำไรขั้นต้นด้านบนไม่ได้รวม {{amount}}',
  pa_headless_wa_doc_name: 'บัญชีทำการ',
  pa_headless_wa_doc: 'Working account',
  pa_headless_fw_rule: 'แบบ 1, 2, 4 และ 5 จัดทำบนฐาน TFRS สำหรับ NPAEs; แบบ 3 บนฐาน TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะ กิจการ NPAE อาจเลือกใช้ TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะได้หากใช้ทั้งฉบับ และต้องเปิดเผยการเปลี่ยนรูปแบบไว้ในหมายเหตุประกอบงบการเงิน',
  pa_headless_fw_period: 'บันทึกไว้กับรอบระยะเวลาบัญชีนี้',
  pa_headless_fw_period_tail: ' — รอบที่ปิดบัญชีแล้วไม่มีการปรับปรุงย้อนหลัง',
  pa_headless_fw_none: '— ยังไม่ได้บันทึก —',
  pa_headless_fw_npae: 'TFRS สำหรับ NPAEs',
  pa_headless_fw_pae: 'TFRS สำหรับกิจการที่มีส่วนได้เสียสาธารณะ',
  pa_headless_fw_dbd_form: 'DBD แบบ {{form}}',
  pa_headless_fw_locked: 'ปิดบัญชีแล้ว — ล็อกแล้ว',
  pa_headless_sh_rule: 'CCC — บริษัทต้องจัดทำทะเบียนผู้ถือหุ้นตั้งแต่จดทะเบียนจัดตั้ง และยื่น บอจ.5 ตามสถานะ ณ วันประชุมภายใน 14 วัน ⚠️ ระบบนี้ไม่มีแบบ บอจ.5 เปล่าของนายทะเบียน จึงอ่านและตรวจรายชื่อได้ แต่พิมพ์ลงแบบทางการไม่ได้',
  pa_headless_sh_as_at: 'ณ วันที่',
  pa_headless_sh_holders: '{{n}} ผู้ถือหุ้น',
  pa_headless_sh_shares: '{{n}} หุ้น',
  pa_headless_sh_form_name: 'บอจ.5',
  pa_headless_sh_form_not_held: 'ไม่มีแบบฟอร์ม',
  pa_headless_sh_name_ph: 'ชื่อผู้ถือหุ้น',
  pa_headless_sh_individual: 'บุคคลธรรมดา',
  pa_headless_sh_juristic: 'นิติบุคคล',
  pa_headless_sh_idno: 'เลขประจำตัว',
  pa_headless_sh_nationality: 'สัญชาติ',
  pa_headless_sh_shares_ph: 'จำนวนหุ้น',
  pa_headless_sh_paid_per_share: 'ชำระแล้ว/หุ้น',
  pa_headless_sh_held_from: 'ถือตั้งแต่',
  pa_headless_sh_add: 'เพิ่ม',
  pa_headless_sh_th_name: 'ชื่อ',
  pa_headless_sh_th_kind: 'ประเภท',
  pa_headless_sh_th_shares: 'หุ้น',
  pa_headless_sh_end_prompt: 'สิ้นสุดการถือหุ้นเมื่อ (YYYY-MM-DD)?',
  pa_headless_sh_end: 'สิ้นสุด…',
  pa_headless_sh_empty: 'ยังไม่มีการถือหุ้นที่บันทึกไว้ ณ วันที่นี้',
  pa_headless_pkg_rule: 'งบการเงินและรายงานของผู้สอบบัญชียื่นผ่าน DBD e-Filing: งบการเงินเป็น XBRL รายงานของผู้สอบบัญชีเป็นภาพสแกนขาวดำ และรายชื่อผู้ถือหุ้น',
  pa_headless_pkg_unit: 'หน่วย',
  pa_headless_pkg_file: 'ไฟล์ {{id}}',
  pa_headless_pkg_declared: 'แจ้งแล้ว',
  pa_headless_pkg_not_attached: 'ยังไม่แนบ',
  pa_headless_pkg_not_producible: 'ระบบนี้ผลิตไม่ได้',
  pa_headless_pkg_file_prompt: 'File ID ของ {{slot}}?',
  pa_headless_pkg_attach: 'แนบ…',
  pa_headless_pkg_declare_bw: 'แจ้งว่ารายงานของผู้สอบบัญชีเป็นขาวดำ',
  pa_headless_cit_rule: 'Revenue Code s.65 ter — บัญชีใดหักเป็นรายจ่ายทางภาษีเงินได้นิติบุคคลได้ และบัญชีใดหักไม่ได้ ⚠️ ระบบนี้มีเฉพาะอนุมาตรา (1)–(6); ส่วนที่เหลือของ s.65 ter ยังไม่ได้มา',
  pa_headless_cit_th_account: 'บัญชี',
  pa_headless_cit_th_movement: 'ความเคลื่อนไหว',
  pa_headless_cit_th_treatment: 'การปฏิบัติทางภาษีเงินได้นิติบุคคล',
  pa_headless_cit_th_note: 'หมายเหตุ',
  pa_headless_cit_undecided: '— ยังไม่ได้ระบุ —',
  pa_headless_cit_yes: 'หักได้',
  pa_headless_cit_no: 'หักไม่ได้',
  pa_headless_cit_limited: 'หักได้บางส่วน',
  pa_headless_cit_empty: 'ไม่มีรายการที่ต้องตรวจสอบในรอบนี้',
  pa_headless_fx_rule_a: 'Revenue Code s.86/4 — ใบกำกับภาษีต้องออกเป็นเงินบาท เว้นแต่กรมสรรพากรอนุมัติให้ใช้สกุลเงินอื่น ',
  pa_headless_fx_rule_b: 'ขณะนี้มีการบังคับใช้ด่านนี้เมื่อออกหรือแก้ไขใบกำกับภาษี',
  pa_headless_fx_rule_c: ': จนกว่าจะบันทึกการอนุมัติไว้ที่นี่ ใบกำกับภาษีที่ไม่ใช่ THB จะถูกปฏิเสธ',
  pa_headless_fx_approval_no: 'เลขที่หนังสืออนุมัติกรมสรรพากร',
  pa_headless_fx_approved_on: 'อนุมัติเมื่อ',
  pa_headless_fx_effective_from: 'มีผลตั้งแต่',
  pa_headless_fx_effective_to: 'ถึง',
  pa_headless_fx_recorded: 'บันทึกการอนุมัติสำหรับ {{currency}} แล้ว',
  pa_headless_fx_record: 'บันทึกการอนุมัติ',
  pa_headless_fx_check: 'ตรวจ {{currency}} วันนี้',
  pa_headless_fx_allowed: 'อนุญาต',
  pa_headless_fx_not_allowed: 'ไม่อนุญาต',
  pa_headless_ret_label: 'ระยะเวลาเก็บรักษา',
  pa_headless_ret_5y: '5 ปี (ขั้นต่ำตามมาตรา 14)',
  pa_headless_ret_6y: '6 ปี',
  pa_headless_ret_7y: '7 ปี (สูงสุดที่ DBD ขยายได้)',
  pa_headless_ret_th_when: 'เมื่อ',
  pa_headless_ret_th_what: 'รายการ',
  pa_headless_ret_th_mode: 'วิธี',
  pa_headless_ret_th_reason: 'เหตุผล',
  pa_headless_ret_th_by: 'โดย',
  pa_headless_ret_none: 'ยังไม่มีการทำลายเอกสาร',
  pa_headless_agm_registered: 'จดทะเบียน',
  pa_headless_agm_first_due: 'ประชุมครั้งแรกภายใน',
  pa_headless_agm_last_held: 'ประชุมครั้งล่าสุด',
  pa_headless_agm_next_due: 'ครั้งถัดไปภายใน',
  pa_headless_agm_overdue: 'เกินกำหนด',
  pa_headless_wht_settled: '{{n}} ชำระแล้ว',
  pa_headless_wht_payer_alone: 'ผู้จ่ายรับผิดแต่ผู้เดียว',
  pa_headless_wht_joint: 'รับผิดร่วม',
  pa_headless_wht_at_risk: 'ภาษีที่ยังมีความเสี่ยงรวม {{amount}} $t(cur_word)',
  pa_headless_wht_th_paid: 'วันจ่าย',
  pa_headless_wht_th_payee: 'ผู้รับเงิน',
  pa_headless_wht_th_form: 'แบบ',
  pa_headless_wht_th_gross: 'ยอดจ่าย',
  pa_headless_wht_th_withheld: 'หักภาษี',
  pa_headless_wht_th_who: 'ใครรับผิด',
  pa_headless_wht_th_why: 'เหตุผล',
  pa_headless_wht_all_clear: 'ทุกบรรทัดหักและนำส่งแล้ว',
  pa_headless_sso_rule_a: 'บริษัทที่จดทะเบียนใหม่เป็นนายจ้างโดยผลของกฎหมาย และต้องขึ้นทะเบียนลูกจ้างเข้าประกันสังคม เลขประกันสังคมของลูกจ้างแต่ละคนมีอยู่แล้ว แต่บัญชี',
  pa_headless_sso_rule_b: 'ของนายจ้างเอง',
  pa_headless_sso_rule_c: 'ยังไม่มี',
  pa_headless_sso_account_no: 'เลขที่บัญชีนายจ้าง',
  pa_headless_sso_branch: 'สาขา',
  pa_headless_sso_registered_on: 'ขึ้นทะเบียน',
  pa_headless_sso_form_101: 'สปส.1-01',
  pa_headless_sso_office: 'สำนักงานเขตพื้นที่',
  pa_headless_sso_saved: 'บันทึกแล้ว',
  pa_headless_sso_save: 'บันทึก',
  pa_headless_sso_employees: '{{n}} พนักงาน',
  pa_headless_sso_no_number: '{{n}} คนยังไม่มีเลขประกันสังคม',
  pa_headless_rev_rule_a: 'Revenue Code s.65 bis (3)-(4) — ผลกำไรจากการตีราคาสินทรัพย์เพิ่มไม่ถือเป็นกำไรที่ต้องเสียภาษี',
  pa_headless_rev_rule_b: ' และ',
  pa_headless_rev_rule_c: 'ไม่นับเป็นฐานค่าเสื่อม จึงทำให้ฐานทางบัญชีกับฐานทางภาษีแยกจากกันอย่างถาวร',
  pa_headless_rev_th_asset: 'ทรัพย์สิน',
  pa_headless_rev_th_cost: 'ราคาทุน',
  pa_headless_rev_th_book: 'ฐานทางบัญชี',
  pa_headless_rev_th_tax: 'ฐานทางภาษี',
  pa_headless_rev_th_reval: 'การตีราคาสินทรัพย์เพิ่ม',
  pa_headless_rev_amount_prompt: 'มูลค่าตามบัญชีใหม่ของ {{name}} ($t(cur_word, {"lng":"en"}))?',
  pa_headless_rev_date_prompt: 'ตีราคาใหม่เมื่อ (YYYY-MM-DD)?',
  pa_headless_rev_basis_prompt: 'ใครประเมินราคา และตามรายงานฉบับใด (ต้องระบุ)',
  pa_headless_rev_revalue: 'ตีราคาใหม่…',
  pa_headless_rev_empty: 'ไม่มีทรัพย์สินในทะเบียน',
  pa_headless_module_title: 'รายการที่เหลือ',
  pa_headless_sec_wa_name: 'บัญชีทำการ',
  pa_headless_sec_wa: 'Working account (s.68 bis)',
  pa_headless_sec_wa_sub: 'สร้างไว้แล้ว แต่เพิ่งมีปุ่มเข้าใช้',
  pa_headless_sec_fw_name: 'แม่บทการรายงานทางการเงิน',
  pa_headless_sec_fw: 'Reporting framework',
  pa_headless_sec_fw_sub: 'บันทึกไว้กับรอบระยะเวลาบัญชี — รอบที่ปิดบัญชีแล้วไม่มีการปรับปรุงย้อนหลัง',
  pa_headless_sec_sh_name: 'ทะเบียนผู้ถือหุ้น',
  pa_headless_sec_sh: 'Share register / บอจ.5',
  pa_headless_sec_sh_sub: 'การถือหุ้น ณ วันที่ที่ระบุ พร้อมสายการโอน',
  pa_headless_sec_pkg: 'ชุดเอกสารยื่น DBD e-Filing',
  pa_headless_sec_pkg_sub: 'สามช่องเอกสาร การระบุหน่วย และสิ่งที่ระบบนี้ผลิตไม่ได้',
  pa_headless_sec_cit: 'ภาษีเงินได้: หักได้ / หักไม่ได้ (s.65 ter)',
  pa_headless_sec_cit_sub: 'เฉพาะอนุมาตรา (1)–(6) — ส่วนที่เหลือยังไม่ได้มา',
  pa_headless_sec_fx_name: 'ใบกำกับภาษีสกุลเงินต่างประเทศ',
  pa_headless_sec_fx: 'Foreign-currency approval (s.86/4)',
  pa_headless_sec_fx_sub: '⚠️ บังคับใช้แล้วเมื่อออกหรือแก้ไขใบกำกับภาษี',
  pa_headless_sec_ret: 'ระยะเวลาเก็บรักษาและทะเบียนการทำลาย (s.14)',
  pa_headless_sec_ret_sub: 'กลไกมีอยู่แล้วแต่ไม่มีเส้นทางเรียกใช้เลย',
  pa_headless_sec_agm: 'การประชุมใหญ่ (CCC s.1197)',
  pa_headless_sec_agm_sub: 'ครั้งแรกภายใน 6 เดือนนับจากจดทะเบียน จากนั้นทุก 12 เดือน',
  pa_headless_sec_wht: 'ความรับผิดของผู้จ่ายเงิน (s.54)',
  pa_headless_sec_wht_sub: 'หักแล้วไม่นำส่ง ผู้จ่ายรับผิดแต่ผู้เดียว; ไม่ได้หักเลย รับผิดร่วม',
  pa_headless_sec_sso: 'ขึ้นทะเบียนนายจ้าง',
  pa_headless_sec_sso_sub: 'บัญชีของนายจ้างเอง ไม่ใช่ของลูกจ้าง',
  pa_headless_sec_rev: 'การตีราคาสินทรัพย์เพิ่ม: ฐานทางบัญชีกับฐานทางภาษี (s.65 bis)',
  pa_headless_sec_rev_sub: 'ผลกำไรนั้นอยู่นอกกำไรที่ต้องเสียภาษี และอยู่นอกฐานค่าเสื่อม',

  // ── Registers — the statutory registers ───────────────────────────────────────
  pa_reg_title: 'ทะเบียนตามกฎหมาย',
  pa_reg_record: 'บันทึก',
  pa_reg_save: 'บันทึก',
  pa_reg_saved: 'บันทึกแล้ว',
  pa_reg_empty: 'ยังไม่มีรายการในทะเบียน',
  pa_reg_overdue: 'เกินกำหนด',
  pa_reg_days_late: 'เกินกำหนด {{n}} วัน',
  pa_reg_doc_directors_report: 'รายงานของกรรมการ',
  pa_reg_doc_firm_register: 'ทะเบียนสำนักงานบัญชี',
  pa_reg_doc_liquidation_report: 'รายงานการชำระบัญชี',
  pa_reg_doc_sales_tax_invoice: 'ใบกำกับภาษีขาย',
  pa_reg_incident_kind_lost: 'สูญหาย',
  pa_reg_incident_kind_damaged: 'เสียหาย',
  pa_reg_incident_discovered: 'พบเมื่อ',
  pa_reg_incident_happened: 'เกิดเมื่อ',
  pa_reg_incident_scope_ph: 'สมุดบัญชี / เอกสารใด',
  pa_reg_incident_cause_ph: 'สาเหตุ (s.16: เว้นว่าง = ถือว่าเจตนา)',
  pa_reg_incident_presumed_alert: 's.16: การสูญหายที่ไม่ได้ระบุสาเหตุ ถือว่าเป็นการกระทำโดยเจตนา บันทึกสิ่งที่เกิดขึ้นทันทีที่ทราบ',
  pa_reg_incident_th_discovered: 'พบเมื่อ',
  pa_reg_incident_th_what: 'ลักษณะ',
  pa_reg_incident_th_scope: 'ขอบเขต',
  pa_reg_incident_th_cause: 'สาเหตุ',
  pa_reg_incident_th_notify_by: 'แจ้งภายใน',
  pa_reg_incident_th_notified: 'แจ้งแล้ว',
  pa_reg_incident_recovered: 'ได้คืนแล้ว',
  pa_reg_incident_cause_blank: 'ไม่ได้ระบุ — s.16 ถือว่าเจตนา',
  pa_reg_incident_notified_prompt: 'แจ้ง DBD เมื่อวันที่ (YYYY-MM-DD)?',
  pa_reg_incident_epermit_prompt: 'เลขอ้างอิง e-Permit?',
  pa_reg_incident_notified_btn: 'แจ้งแล้ว…',
  pa_reg_incident_unexplained: 'รายการที่ไม่ได้ระบุสาเหตุ: {{n}} รายการ s.16 ถือว่าการสูญหายที่อธิบายไม่ได้เป็นการกระทำโดยเจตนา — บันทึกสิ่งที่เกิดขึ้น',
  pa_reg_cess_ceased: 'เลิกประกอบกิจการเมื่อ',
  pa_reg_cess_reason_ph: 'เหตุผล',
  pa_reg_cess_record: 'บันทึกการเลิกประกอบธุรกิจ',
  pa_reg_cess_ceased_on: 'เลิกกิจการ {{date}}',
  pa_reg_cess_deliver_by: 'ส่งมอบภายใน',
  pa_reg_cess_extended_to: 'ขยายถึง {{date}}',
  pa_reg_cess_max_extension: '(ขยายได้ถึง {{date}} เป็นอย่างช้า)',
  pa_reg_cess_delivered: 'ส่งมอบแล้ว {{date}}',
  pa_reg_cess_not_delivered: 'ยังไม่ส่งมอบ',
  pa_reg_cess_overdue_days: 'เกินกำหนด {{n}} วัน',
  pa_reg_cess_extend_prompt: 'ขยายถึง (YYYY-MM-DD, อย่างช้า {{date}})?',
  pa_reg_cess_extend_ref_prompt: 'เลขอ้างอิงการขยายเวลา?',
  pa_reg_cess_extend_btn: 'ได้รับการขยายเวลา…',
  pa_reg_cess_delivered_prompt: 'ส่งมอบให้ DBD เมื่อวันที่ (YYYY-MM-DD)?',
  pa_reg_cess_receipt_prompt: 'เลขอ้างอิงการรับมอบของ DBD?',
  pa_reg_cess_delivered_btn: 'ส่งมอบแล้ว…',
  pa_reg_insp_demanded: 'วันที่เรียก',
  pa_reg_insp_authority_ph: 'หน่วยงาน',
  pa_reg_insp_inspector_ph: 'พนักงานเจ้าหน้าที่',
  pa_reg_insp_due: 'กำหนดส่ง',
  pa_reg_insp_record: 'บันทึกการเรียกตรวจ',
  pa_reg_insp_asked_label: 'ขอให้ส่ง',
  pa_reg_insp_preview: 'ดูตัวอย่างรายการเอกสาร',
  pa_reg_insp_build: 'สร้างชุดเอกสาร',
  pa_reg_insp_th_demanded: 'วันที่เรียก',
  pa_reg_insp_th_by: 'โดย',
  pa_reg_insp_th_asked: 'ขอให้ส่ง',
  pa_reg_insp_th_due: 'กำหนดส่ง',
  pa_reg_insp_th_answered: 'ส่งแล้ว',
  pa_reg_insp_answered_prompt: 'ส่งเมื่อวันที่ (YYYY-MM-DD)?',
  pa_reg_insp_answered_btn: 'ส่งแล้ว…',
  pa_reg_chg_resolved: 'มติเมื่อ',
  pa_reg_chg_from_ph: 'จาก',
  pa_reg_chg_to_ph: 'เป็น',
  pa_reg_chg_liquidation_due: 'รายงานฉบับถัดไปต้องส่งภายใน {{due}} (สามเดือนนับจาก {{since}})',
  pa_reg_chg_th_resolved: 'มติเมื่อ',
  pa_reg_chg_th_what: 'เรื่อง',
  pa_reg_chg_th_fromto: 'จาก → เป็น',
  pa_reg_chg_th_register_by: 'จดทะเบียนภายใน',
  pa_reg_chg_th_registered: 'จดทะเบียนแล้ว',
  pa_reg_chg_registered_prompt: 'จดทะเบียนกับ DBD เมื่อวันที่ (YYYY-MM-DD)?',
  pa_reg_chg_receipt_prompt: 'เลขที่หลักฐานการรับจดทะเบียน?',
  pa_reg_chg_registered_btn: 'จดทะเบียนแล้ว…',
  pa_reg_dr_meeting: 'วันประชุม',
  pa_reg_dr_presented: 'เสนอเมื่อ',
  pa_reg_dr_body_th_label: 'รายงานของกรรมการ (ไทย)',
  pa_reg_dr_body_en_label: 'รายงานของกรรมการ (อังกฤษ)',
  pa_reg_vat_rev12: 'รายได้ 12 เดือน {{from}} – {{to}}',
  pa_reg_vat_source: 'จาก {{source}}',
  pa_reg_vat_source_ledger: 'สมุดบัญชี',
  pa_reg_vat_exceeded_head: 'เกิน 1.8 ล้านบาทแล้ว.',
  pa_reg_vat_exceeded_body: 'รายได้ 12 เดือนย้อนหลังเกินเกณฑ์จดทะเบียนแล้ว',
  pa_reg_vat_notice_ack_by: 'ยืนยันแล้ว — ยืนยันโดย',
  pa_reg_vat_notice_ack_tail: ' จึงหยุดแจ้งเตือนรายวันแล้ว',
  pa_reg_vat_notice_sending: 'แจ้งเตือนทุกวัน — ส่งไปที่ Telegram และ {{mailbox}} แล้ว {{n}} ครั้ง',
  pa_reg_vat_notice_sending_last: 'แจ้งเตือนทุกวัน — ส่งไปที่ Telegram และ {{mailbox}} แล้ว {{n}} ครั้ง ครั้งล่าสุด {{date}}',
  pa_reg_vat_notice_stops: 'จะหยุดส่งเมื่อมีผู้คลิกลิงก์ในอีเมล',
  pa_reg_vat_notice_last_send: 'การส่งครั้งล่าสุด:',
  pa_reg_vat_notice_resume: 'เริ่มส่งอีกครั้ง',
  pa_reg_stock_cost_formula: 'วิธีคำนวณต้นทุน',
  pa_reg_stock_method_none: '— ยังไม่ระบุ —',
  pa_reg_stock_th_item: 'สินค้า',
  pa_reg_stock_th_qty: 'จำนวน',
  pa_reg_stock_th_cost: 'ราคาทุน',
  pa_reg_stock_th_market: 'ราคาตลาด',
  pa_reg_stock_th_carried: 'มูลค่าที่บันทึก',
  pa_reg_stock_th_writedown: 'ผลต่างจากการตีราคาลด',
  pa_reg_stock_total: 'รวม',
  pa_reg_stock_empty: 'ไม่มีสินค้าคงเหลือที่ต้องตีราคา',
  pa_reg_stock_struck_msg: 'ตีราคาแล้ว ผลต่างจากการตีราคาลด {{amount}} อยู่ในรายการรอตรวจ {{doc}} — ตรวจสอบพร้อมรายการรอตรวจอื่น',
  pa_reg_stock_struck_none: 'ตีราคาแล้ว ไม่มีผลต่างจากการตีราคาลด',
  pa_reg_stock_strike_btn: 'ตีราคาสินค้าคงเหลือ',
  pa_reg_stock_struck_line: 'ตีราคาแล้ว {{n}} รายการ · ตีราคาเมื่อ {{date}} โดย {{name}}',
  pa_reg_firm_rule: 'Accounting Professions Act s.11 — นิติบุคคลที่ให้บริการด้านบัญชีต้องจดทะเบียนกับ TFAC และวางหลักประกันความรับผิดต่อบุคคลที่สาม ผู้ที่ให้บริการสอบบัญชีต้องมีผู้สอบบัญชีรับอนุญาต (CPA) เป็นผู้ผูกพัน และรับผิดร่วมกับผู้สอบบัญชีของตน',
  pa_reg_firm_regno_ph: 'เลขทะเบียน TFAC',
  pa_reg_firm_registered: 'จดทะเบียน',
  pa_reg_firm_expires: 'หมดอายุ',
  pa_reg_firm_guarantee_ph: 'หลักประกันที่วาง (บาท)',
  pa_reg_firm_guarantee_form_ph: 'รูปแบบ (หนังสือค้ำประกันธนาคาร…)',
  pa_reg_firm_offers_audit: 'ให้บริการสอบบัญชี',
  pa_reg_firm_cpa_name_ph: 'ผู้สอบบัญชีรับอนุญาต (CPA) ผู้ลงลายมือชื่อ',
  pa_reg_firm_cpa_no_ph: 'เลขทะเบียน CPA',
  pa_reg_firm_expired: 'หมดอายุแล้ว — ทะเบียนนี้หมดอายุเมื่อ {{date}}',
  pa_reg_firm_expiring: 'หมดอายุ {{date}} — ภายใน 60 วัน',
  pa_reg_sec_incidents_title: 'บัญชีหรือเอกสารสูญหาย/เสียหาย (ss.15-16)',
  pa_reg_sec_incidents_sub: 'แจ้งภายใน 15 วันนับจากวันที่พบ การสูญหายที่อธิบายไม่ได้ถือว่าเป็นการกระทำโดยเจตนา',
  pa_reg_sec_cess_title: 'เลิกประกอบธุรกิจ (s.17)',
  pa_reg_sec_cess_sub: 'ส่งมอบสมุดบัญชีให้ DBD ภายใน 90 วัน ขยายได้ถึง 180 วัน',
  pa_reg_sec_insp_title: 'พนักงานเจ้าหน้าที่เรียกตรวจ (ss.22, 24, 25)',
  pa_reg_sec_insp_sub: 'บันทึกการเรียกตรวจ และรวมสมุดบัญชี ใบสำคัญ และคู่มือรหัสบัญชีเป็นชุดเดียว',
  pa_reg_sec_chg_title: 'จดทะเบียนเปลี่ยนแปลง (14 วัน)',
  pa_reg_sec_chg_sub: 'ทุน ข้อบังคับ กรรมการ และรอบรายงานการชำระบัญชีทุกสามเดือน',
  pa_reg_sec_dr_title_gloss: 'Directors\' report (CCC s.1198)',
  pa_reg_sec_dr_sub: 'เสนอต่อการประชุมใหญ่พร้อมงบดุล ไม่มีแบบที่กำหนดไว้',
  pa_reg_sec_stock_title: 'ตีราคาสินค้าคงเหลือ ราคาทุนหรือราคาตลาดที่ต่ำกว่า (s.65 bis)',
  pa_reg_sec_stock_sub: 'เข้าก่อนออกก่อน (FIFO) หรือถัวเฉลี่ยถ่วงน้ำหนัก ล็อกเมื่อใช้แล้ว (มูลค่าเข้าลบมูลค่าออกไม่ใช่วิธีคำนวณต้นทุน)',
  pa_reg_sec_vat_title: 'เกณฑ์จดทะเบียน VAT 1.8 ล้าน',
  pa_reg_sec_vat_sub: 'รายได้ 12 เดือนย้อนหลังเทียบกับ 1,800,000 บาท',
  pa_reg_sec_firm_title_gloss: 'The firm\'s own TFAC registration (APA s.11)',
  pa_reg_sec_firm_sub: 'เกี่ยวกับ {{brand}} เอง ไม่ใช่ลูกค้า: การจดทะเบียน หลักประกัน และผู้สอบบัญชีรับอนุญาต (CPA) ที่ผูกพันสำนักงาน',

  // ── TaxLedgers — the six tax ledgers ──────────────────────────────────────────
  pa_tax_channel_label: 'ยื่นแบบทาง',
  pa_tax_channel_online: 'ยื่นออนไลน์',
  pa_tax_channel_paper: 'ยื่นกระดาษ',
  pa_tax_channel_deadlines_paper: 'แบบรายเดือนกำหนดภายในวันที่ 7 (ภาษีมูลค่าเพิ่มวันที่ 15); PND.50 150 วัน, PND.51 สองเดือน — ไม่ได้รับการขยายเวลาของการยื่นออนไลน์',
  pa_tax_channel_deadlines_online: 'แบบรายเดือนกำหนดภายในวันที่ 15 (ภาษีมูลค่าเพิ่มวันที่ 23); PND.50 158 วัน, PND.51 สองเดือนแปดวัน',
  pa_tax_fx_rule_1: 's.65 bis (5) — ยอดคงเหลือสิ้นปีแปลงค่าด้วยอัตราของธนาคารแห่งประเทศไทย โดยใช้วิธีเดียวอย่างสม่ำเสมอ ระบบนี้ไม่มีแหล่งอัตรา อัตราต้องนำเข้า และยอดคงเหลือที่ไม่มีอัตราในวันสิ้นปีหรือก่อนนั้น ',
  pa_tax_fx_rule_not: 'ไม่',
  pa_tax_fx_rule_2: 'ถูกแปลงค่าด้วยอัตราใดเลย',
  pa_tax_fx_method_label: 'วิธีแปลงค่า',
  pa_tax_fx_other_method_prompt: 'วิธีที่ได้รับอนุมัติคือวิธีใด และใครอนุมัติ?',
  pa_tax_fx_method_none: '— ยังไม่ระบุ —',
  pa_tax_fx_no_method_warn: 'ยังไม่ได้ล็อกวิธีแปลงค่า s.65 bis (5) กำหนดให้ใช้วิธีเดียวอย่างสม่ำเสมอ จึงจะไม่มีการตีราคาใหม่จนกว่าจะเลือกวิธี — และเมื่อมีรอบระยะเวลาบัญชีที่ตีราคาใหม่ด้วยวิธีนั้นแล้ว จะเปลี่ยนวิธีไม่ได้',
  pa_tax_fx_rate_label: 'อัตรา',
  pa_tax_fx_kind_avg: 'อัตราเฉลี่ย (BOT)',
  pa_tax_fx_kind_buy: 'อัตราซื้อ (BOT)',
  pa_tax_fx_kind_sell: 'อัตราขาย (BOT)',
  pa_tax_fx_source_ph: 'ที่มาของอัตรา',
  pa_tax_fx_rate_stored: 'บันทึกอัตราแล้ว',
  pa_tax_fx_nothing_imported: 'ไม่มีการนำเข้า',
  pa_tax_fx_add_rate: 'เพิ่มอัตรา',
  pa_tax_fx_account_none: '— บัญชี —',
  pa_tax_fx_amount_fx_ph: 'จำนวนเงินตราต่างประเทศ',
  pa_tax_fx_booked_ph: '$t(cur_word)ตามบัญชี',
  pa_tax_fx_add_balance: 'เพิ่มยอดเงินตราต่างประเทศ',
  pa_tax_fx_th_account: 'บัญชี',
  pa_tax_fx_th_cur: 'สกุล',
  pa_tax_fx_th_amount: 'จำนวน',
  pa_tax_fx_th_booked: 'ยอดตามบัญชี',
  pa_tax_fx_th_rate: 'อัตราแลกเปลี่ยน',
  pa_tax_fx_th_at_rate: 'ตามอัตรา',
  pa_tax_fx_th_diff: 'กำไร / (ขาดทุน)',
  pa_tax_fx_empty: 'ไม่มียอดเงินตราต่างประเทศบันทึกไว้ในรอบระยะเวลาบัญชีนี้',
  pa_tax_fx_revalued_msg: 'รายการรอตรวจ {{doc}} — สุทธิ {{amount}} · ยอดที่ตีราคาใหม่: {{n}} ตรวจสอบพร้อมรายการรอตรวจอื่นก่อนผ่านรายการ',
  pa_tax_fx_revalue_btn: 'ปรับปรุงสิ้นปี {{amount}}',
  pa_tax_rem_warn_1: 'ระบบนี้',
  pa_tax_rem_warn_norate: 'ไม่มีอัตรา',
  pa_tax_rem_warn_2: ' สำหรับทั้งสองแบบ และไม่มีฐานทางกฎหมายของอัตรา อัตราตาม ท.ป.4/2528 ที่ใช้กับการจ่ายในประเทศเป็นอีกบทบัญญัติหนึ่ง ห้ามใช้ที่นี่ — ทุกบรรทัดใช้อัตราที่ผู้ให้คำปรึกษากำหนด และบันทึกผู้ที่กรอกไว้',
  pa_tax_rem_lines: '{{n}} บรรทัด',
  pa_tax_rem_base: 'ฐาน {{amount}}',
  pa_tax_rem_tax: 'ภาษี',
  pa_tax_rem_due: 'กำหนดภายใน {{date}}',
  pa_tax_rem_filed: 'ยื่นแล้ว',
  pa_tax_rem_payee_ph: 'ผู้รับเงิน',
  pa_tax_rem_country_ph: 'ประเทศ',
  pa_tax_rem_nature_ph: 'ลักษณะเงินได้',
  pa_tax_rem_base_ph: 'ฐาน (บาท)',
  pa_tax_rem_rate_ph: 'อัตรา %',
  pa_tax_rem_basis_ph: 'ฐานทางกฎหมาย',
  pa_tax_rem_add: 'เพิ่ม',
  pa_tax_rem_th_paid: 'วันจ่าย',
  pa_tax_rem_th_payee: 'ผู้รับเงิน',
  pa_tax_rem_th_for: 'ลักษณะเงินได้',
  pa_tax_rem_th_base: 'ฐาน',
  pa_tax_rem_th_rate: 'อัตรา',
  pa_tax_rem_th_tax: 'ภาษี',
  pa_tax_rem_th_basis: 'ฐานกฎหมาย',
  pa_tax_rem_not_recorded: 'ยังไม่ได้บันทึก',
  pa_tax_rem_empty: 'ไม่มีรายการในแบบของเดือนนี้',
  pa_tax_rem_filed_prompt: 'นำส่งเมื่อ (YYYY-MM-DD)?',
  pa_tax_rem_mark_filed: 'ทำเครื่องหมายยื่นแล้ว',
  pa_tax_stamp_rule: 'ss.104 และ 118 — ตราสารที่อยู่ในบัญชีอัตราต้องปิดแสตมป์ให้ครบถ้วน และตราสารที่ไม่ปิดแสตมป์ใช้เป็นพยานหลักฐานไม่ได้จนกว่าจะชำระค่าอากรและเงินเพิ่ม สำนักงานในฐานะผู้รับจ้างตามสัญญาจ้างของตนเองมีหน้าที่เสียอากร 1 บาท ต่อ 1,000 — 0.1%',
  pa_tax_stamp_party_ph: 'คู่สัญญา',
  pa_tax_stamp_base_ph: 'ค่าเช่า / ค่าธรรมเนียม / เงินต้น',
  pa_tax_stamp_pick_none: '— จำนวนใด —',
  pa_tax_baht_amount: '{{amount}} บาท',
  pa_tax_stamp_one_act: ' (ทำครั้งเดียว)',
  pa_tax_stamp_several_acts: ' (ทำหลายครั้ง)',
  pa_tax_stamp_vat_exempt: 'มี VAT บนจำนวนนี้ (ยกเว้น)',
  pa_tax_stamp_record: 'บันทึก',
  pa_tax_stamp_schedule_head: 'ลักษณะแห่งตราสาร',
  pa_tax_stamp_number_inferred: ' (เลขที่ลักษณะเป็นการอนุมาน — แหล่งที่มาให้ทั้งเจ็ดลักษณะมาเป็นชุด)',
  pa_tax_stamp_band: ' · บัญชีอัตรากำหนดไว้ {{min}}–{{max}} บาท เป็นช่วง ไม่ใช่สูตรคำนวณ',
  pa_tax_stamp_th_date: 'วันที่',
  pa_tax_stamp_th_instrument: 'ตราสาร',
  pa_tax_stamp_th_party: 'คู่สัญญา',
  pa_tax_stamp_th_base: 'ฐาน',
  pa_tax_stamp_th_duty: 'ค่าอากร',
  pa_tax_stamp_th_stamped: 'ปิดอากร',
  pa_tax_stamp_not_established: 'ยังไม่ได้กำหนด',
  pa_tax_stamp_exempt: 'ยกเว้น',
  pa_tax_stamp_unpaid: 'ยังไม่ปิด',
  pa_tax_stamp_method_prompt: 'ชำระด้วยวิธีใด? พิมพ์ adhesive, impressed หรือ cash',
  pa_tax_stamp_paid_on_prompt: 'ชำระเมื่อ (YYYY-MM-DD)?',
  pa_tax_stamp_duty_prompt: 'ค่าอากรที่ชำระ (บาท)?',
  pa_tax_stamp_receipt_prompt: 'เลขที่ใบรับ อ.ส.?',
  pa_tax_stamp_mark_btn: 'บันทึกปิดอากร',
  pa_tax_stamp_empty: 'ไม่มีรายการในทะเบียน',
  pa_tax_stamp_unstamped_count: 'ยังไม่ปิดอากร {{n}} ฉบับ',
  pa_tax_stamp_duty_established: 'ค่าอากรที่กำหนดแล้ว: {{amount}}',
  pa_tax_stamp_unquantified: ' · อีก {{n}} ฉบับยังไม่มีจำนวนค่าอากร',
  pa_tax_loss_year_ph: 'ปี',
  pa_tax_loss_amount_ph: 'ขาดทุน ($t(cur_word))',
  pa_tax_loss_note_ph: 'หมายเหตุ',
  pa_tax_loss_record: 'บันทึกผลขาดทุน',
  pa_tax_loss_years_prompt: 'ระยะเวลายกไป กี่ปี?',
  pa_tax_loss_basis_prompt: 'มาจากบทบัญญัติใด? (ต้องระบุ)',
  pa_tax_loss_set_period: 'กำหนดระยะเวลา…',
  pa_tax_loss_th_year: 'ปี',
  pa_tax_loss_th_loss: 'ขาดทุน',
  pa_tax_loss_th_used: 'ใช้แล้ว',
  pa_tax_loss_th_left: 'คงเหลือ',
  pa_tax_loss_th_expires: 'หมดอายุ',
  pa_tax_loss_th_used_in: 'ใช้ในปี',
  pa_tax_loss_use_year_prompt: 'ใช้ในปีใด?',
  pa_tax_loss_use_amount_prompt: 'ใช้เท่าใด ($t(cur_word))?',
  pa_tax_loss_use_btn: 'ใช้…',
  pa_tax_loss_total: 'รวม',
  pa_tax_loss_empty: 'ไม่มีผลขาดทุนในทะเบียน',
  pa_tax_loss_unreconciled: 'ยอดรวมที่บันทึกไว้กับผลรวมของการใช้แต่ละครั้งไม่ตรงกัน ยังไม่ควรนำตัวเลขในหน้านี้ไปยื่นจนกว่าจะตรวจสอบให้ตรงกัน',
  pa_tax_rc_no_threshold: 'ยังไม่ได้บันทึกเกณฑ์ การตรวจนี้จึงไม่ทำงาน s.105 ให้ไว้เพียงเพดาน — 1,000 บาทสำหรับผู้ประกอบการจดทะเบียน VAT/SBT และ 10,000 สำหรับรายอื่น — และจำนวนที่อธิบดีประกาศกำหนดไม่ได้เก็บไว้ในระบบนี้ โปรดบันทึกจำนวนนั้น หรือบันทึก 0 เพื่อให้หมายถึงทุกการรับเงิน ซึ่งเป็นการตีความที่ปลอดภัยในขณะที่ยังไม่ทราบจำนวน',
  pa_tax_rc_registrant_prompt: 'เป็นผู้ประกอบการจดทะเบียน VAT/SBT หรือไม่? พิมพ์ vat_sbt หรือ other',
  pa_tax_rc_threshold_prompt: 'เกณฑ์เป็นบาท (0 = ทุกการรับเงิน; เพดาน {{ceiling}})',
  pa_tax_rc_notification_prompt: 'เลขที่ประกาศอธิบดี ถ้าสำนักงานมี',
  pa_tax_rc_change_threshold: 'แก้ไขเกณฑ์…',
  pa_tax_rc_record_threshold: 'บันทึกเกณฑ์…',
  pa_tax_rc_registrant_yes: 'ผู้ประกอบการจดทะเบียน VAT/SBT',
  pa_tax_rc_registrant_no: 'ไม่ได้จดทะเบียน',
  pa_tax_rc_scope_level: 'ระดับ {{scope}}',
  pa_tax_rc_th_received: 'วันรับเงิน',
  pa_tax_rc_th_against: 'เอกสารอ้างอิง',
  pa_tax_rc_th_amount: 'จำนวน',
  pa_tax_rc_th_desc: 'คำอธิบายรายการ',
  pa_tax_rc_all_covered: 'ทุกการรับเงินที่จับคู่แล้วในช่วงนี้มีใบรับรองรับ (ตรวจแล้ว {{n}} รายการ)',
  pa_tax_rc_daily_head: 'ยอดรวมรายวัน s.105 ter',
  pa_tax_rc_daily_body: '{{n}} วัน รวม {{amount}}',
  pa_tax_title: 'ภาษีที่เกี่ยวข้อง',
  pa_tax_sec_fx_title: 'อัตราแลกเปลี่ยนและการปรับปรุงสิ้นปี',
  pa_tax_sec_fx_sub: 's.65 bis (5) — ใช้วิธีเดียวอย่างสม่ำเสมอ ถ้าไม่มีอัตราที่นี่ ก็ไม่มีการตีราคาใหม่',
  pa_tax_sec_rem_forms: 'ภ.ง.ด.54 · ภ.พ.36',
  pa_tax_sec_rem_title: 'การนำส่งภาษี',
  pa_tax_sec_rem_sub: 'เป็นทะเบียน ไม่ใช่ตัวคำนวณอัตรา ระบบนี้ไม่ทราบอัตราของทั้งสองแบบ',
  pa_tax_sec_stamp_title: 'อากรแสตมป์',
  pa_tax_sec_stamp_sub: 'อัตราตามบัญชีอัตราเจ็ดลักษณะที่สำนักงานกฎหมายหรือสำนักงานบัญชีพบ และส่วนที่ยังไม่ได้กำหนด',
  pa_tax_sec_loss_title: 'ทะเบียนผลขาดทุนยกไป',
  pa_tax_sec_loss_sub: 'หนึ่งแถวต่อหนึ่งปีที่ขาดทุน และไม่ระบุวันหมดอายุจนกว่าจะบันทึกระยะเวลายกไป',
  pa_tax_sec_rc_doc: 'ใบรับ',
  pa_tax_sec_rc_title: 'ออกใบรับทุกครั้งที่รับเงิน (s.105)',
  pa_tax_sec_rc_sub: 'เงินรับที่ไม่มีใบรับรองรับ จะไม่ทำงานจนกว่าจะบันทึกเกณฑ์',

  // ── ค่าบริการ → บัญชีรายได้ ──────────────────────────────────────────────────────────────────
};
